/**
 * The control-api contract.
 *
 * Every route is described once: method, path template, request schema,
 * response schema, and - for mutations - whether it supports the
 * preview→confirm convention (ADR-30 §2.3). The Fastify routes import these
 * to validate; the typed client imports them to parse; the MCP server maps
 * them to tools. Change a shape here and all three move together.
 *
 * Money is integer cents. Dates are ISO strings on the wire.
 */

import { z } from 'zod';
import { FLOW_OBSERVATION_ROUTES } from './flow-observation.js';
export * from './flow-observation.js';
// Knowledge Base V1: its own route table (KNOWLEDGE_ROUTES) and a dependency-free
// Markdown parser, both standalone modules like flow-observation.
export * from './knowledge.js';
export * from './knowledge-markdown.js';
// ADR-71: the discovery directory (lotnumber.com / HipNumber discovery) has its
// own route table, DISCOVERY_ROUTES, with a public/operator access vocabulary.
export * from './discovery.js';
// ADR-70: which engine serves which ring, shared with the clerk console.
export * from './ring-addresses.js';
// C1b: who may change a lot's photos by phase, shared by the API and the office screen.
export * from './lot-photos.js';
// C3: the token that says which order the lots were in when a page loaded them.
export * from './lot-order.js';
import { KIND_KEYS, saleSelectableEquineTypeSchema } from '@hipnumber/lot-kinds';
// ADR-69: the product and vertical vocabularies are imported from the one
// registry, never restated. Request bodies take the enum (a client cannot ask
// for a product that does not exist); responses read them as strings so a
// vertical added by a later migration never breaks a deployed client.
import { PRODUCT_KEYS, SHELL_KEYS, VERTICAL_KEYS } from '@hipnumber/products';
export const ProductKey = z.enum(PRODUCT_KEYS);
export const VerticalKey = z.enum(VERTICAL_KEYS);
/** The brand shell a storefront host wears (ADR-69 Phase 3). */
export const ShellKey = z.enum(SHELL_KEYS);
// ADR-49: the diagnostic vocabulary is imported, never restated. Two spellings
// of "health" is the problem that ADR exists to fix.
import {
  FindingSchema as DiagFinding,
  FindingSummarySchema as DiagFindingSummary,
  HealthSchema as DiagHealth,
  InvariantResultSchema as DiagInvariantResult,
  ProbeResultSchema as DiagProbeResult,
  RunbookPreviewSchema,
  SeveritySchema as DiagSeverity,
} from '@hipnumber/diagnostics-contract';
// ADR-56: the Marketing Studio domain vocabulary is imported, never restated -
// the same posture as the diagnostics import above.
import {
  CampaignObjectiveSchema,
  MarketingChannelSchema,
  VisualStyleSchema,
  FeaturedLotSelectionSchema,
  CampaignStatusSchema,
  CampaignAssetStatusSchema,
  AssetGenerationStatusSchema,
  CreativeFormatSchema,
  RegenerationScopeSchema,
  CopySurfaceSchema,
  GeneratedCopySchema,
  VisualStrategySchema,
  VisualReuseScopeSchema,
  ResultsDisclosureSchema,
  ConnectionProviderSchema,
  ConnectionStatusSchema,
  DestinationTypeSchema,
} from '@hipnumber/marketing-contract';

// ADR-58: the Incident Operations vocabulary (INC / CHG / RITM), imported and
// never restated, for the same reason as the two imports above.
import {
  ActorTypeSchema,
  ChangeRiskSchema,
  ChangeStatusSchema,
  ChangeTypeSchema,
  FreezeVerdictSchema,
  GuardVerdictSchema,
  LiveExposureSchema,
  ImpactResourceTypeSchema,
  ImpactTypeSchema,
  PublicComponentIdSchema,
  PublicImpactSchema,
  PublicStateSchema,
  IncidentSeveritySchema,
  IncidentSourceSchema,
  IncidentStatusSchema,
  LinkRelationshipSchema,
  LinkTargetKindSchema,
  RecordKindSchema,
  RequestCategorySchema,
  RequestStatusSchema,
  SignalStatusSchema,
  TimelineEventTypeSchema,
  SURFACE_AREAS, APPROVAL_METHODS, GATES, GATE_VERDICTS, HUMAN_LINE_KEYS, PLATFORM_EVALUABLE_GATES, EXECUTION_PHASES,
} from '@hipnumber/ops-contract';
import {
  AssetConditionSchema,
  AssetEventTypeSchema,
  AssetStatusSchema,
  AssignmentStatusSchema,
  AssignmentTypeSchema,
  BillingModeSchema,
  HardwareCategorySchema,
  InspectionItemOutcomeSchema,
  InspectionResultSchema,
  KitInstanceStatusSchema,
  KitReadinessSchema,
  LocationTypeSchema,
  MaintenanceEventTypeSchema,
  MaintenanceStatusSchema,
  OwnerTypeSchema,
  TrackingRequirementSchema,
  TrackingTypeSchema,
} from '@hipnumber/hardware-contract';

export { RunbookPreviewSchema };

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const Uuid = z.string().uuid();
export const IsoDate = z.string().datetime({ offset: true });
export const Cents = z.number().int().nonnegative();
export const PricingMode = z.enum(['PER_LOT', 'PER_HEAD', 'CHOICE']);
/** The statuses staff may set on a lot by hand (PatchLotBody). The ring's
 *  dispositions are written here too when the office records them; the
 *  settlement projector writes them from the log. */
export const EditableLotStatus = z.enum(['draft', 'approved', 'scratched', 'sold', 'no_sale', 'passed']);
/** Every status a lot can READ as. ADR-65: `turned_back` = the lot sold and
 *  the sale later ceased to stand (distinct from `no_sale`, which never sold).
 *  Projector-owned, written only from SALE_VOIDED: it is deliberately absent
 *  from EditableLotStatus so the typed client never advertises it as
 *  something staff may PATCH, which the catalog route refuses. */
export const LotStatus = z.enum([...EditableLotStatus.options, 'turned_back']);
export const SaleStatus = z.enum(['draft', 'published', 'live', 'settled', 'archived']);
/** Auction format (ADR-42 engine modes, ADR-48 tenant-declared). LIVE = clerk
 *  outcry; TIMED = per-lot scheduled close with soft-close; LIVE_WITH_PREBIDDING
 *  = advance max bids then a live event. The "Requested Mode" of the three-source
 *  model (requested == provisioned == observed at go-live). */
export const AuctionMode = z.enum(['LIVE', 'TIMED', 'LIVE_WITH_PREBIDDING']);

/** ADR-69 Phase 2: a TIMED sale's close schedule. The policy half (pattern,
 *  stagger, group size, preset) inherits sale -> company default -> product
 *  default; the first close is sale-only. Open time is the sale's start.
 *  The vocabulary is auction-core's (ClosingPattern, TIMED_CLOSE_PRESETS);
 *  control-api pins these copies to it under test. */
export const TimedClosePattern = z.enum(['INDIVIDUAL', 'STAGGERED', 'GROUPED']);
export const TimedClosePreset = z.enum(['standard-2m', 'quick-30s', 'hard-close']);
/** ADR-70 cut 6h (hitlist 18): a preset, or 'custom' with customClose. */
export const TimedCloseChoice = z.enum([...TimedClosePreset.options, 'custom']);
/** A soft close with the office's own numbers, in seconds. Extensions are
 *  on (no extensions = 'hard-close'). extendFrom BID_TIME: the new close is
 *  the bid's time plus extendSeconds; SCHEDULED_CLOSE: the current close
 *  plus extendSeconds. maxExtensions null = no limit. */
export const CustomCloseRule = z.object({
  windowSeconds: z.number().int().min(5).max(3600),
  extendSeconds: z.number().int().min(5).max(3600),
  extendFrom: z.enum(['BID_TIME', 'SCHEDULED_CLOSE']),
  maxExtensions: z.number().int().min(1).max(1000).nullable(),
}).strict();
export const TimedScheduleSource = z.enum(['SALE', 'TENANT', 'PRODUCT']);
const TimedSchedulePolicyFields = z.object({
  closePattern: TimedClosePattern.optional(),
  /** Seconds between consecutive closes (STAGGERED) or groups (GROUPED). */
  staggerSeconds: z.number().int().min(0).max(86_400).optional(),
  /** Lots per close group (GROUPED). */
  lotsPerGroup: z.number().int().min(1).max(500).optional(),
  closePreset: TimedCloseChoice.optional(),
  /** With closePreset 'custom' only; inherited with it as one field. */
  customClose: CustomCloseRule.optional(),
}).strict();
const customCloseGoesWithCustom = (s: { closePreset?: string | undefined; customClose?: unknown }, ctx: z.RefinementCtx): void => {
  if ((s.closePreset === 'custom') !== (s.customClose !== undefined)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['customClose'], message: 'customClose is set with closePreset "custom", and only with it' });
  }
};
export const TimedScheduleDefaults = TimedSchedulePolicyFields.superRefine(customCloseGoesWithCustom);
export const SaleTimedSchedule = TimedSchedulePolicyFields.extend({
  /** ISO instant of the first close. Editable until the lots have opened. */
  firstCloseAt: IsoDate.optional(),
}).strict().superRefine(customCloseGoesWithCustom);
export const EffectiveTimedSchedule = z.object({
  /** null = no layer set one: the sale cannot go live as TIMED without it. */
  firstCloseAt: z.string().nullable(),
  closePattern: TimedClosePattern,
  staggerSeconds: z.number().int(),
  lotsPerGroup: z.number().int(),
  closePreset: TimedCloseChoice,
  /** The custom rule's numbers when closePreset is 'custom', else null.
   *  Optional so an older API's reply still parses. */
  customClose: CustomCloseRule.nullable().optional(),
  /** Where each policy field came from, for the "company default" note. */
  sources: z.object({ closePattern: TimedScheduleSource, staggerSeconds: TimedScheduleSource, lotsPerGroup: TimedScheduleSource, closePreset: TimedScheduleSource }),
});
export type TimedScheduleDefaults = z.infer<typeof TimedScheduleDefaults>;
export type SaleTimedSchedule = z.infer<typeof SaleTimedSchedule>;
export type EffectiveTimedSchedule = z.infer<typeof EffectiveTimedSchedule>;
export type CustomCloseRule = z.infer<typeof CustomCloseRule>;

/**
 * What a sale requires before a bidder is financially qualified to bid
 * (migration 0033). Independent and combinable - a select sale can require a
 * card AND a bank letter of credit - which is why this is an object of flags
 * rather than one enum value.
 *
 * A bank letter is not a payment method: it is a bank vouching for a bidder,
 * and it is reviewed by the office like any other document.
 */
export const BidderFinancialRequirements = z
  .object({
    cardOnFile: z.object({ required: z.boolean() }).partial({ required: true }).optional(),
    deposit: z.object({ required: z.boolean(), amountCents: z.number().int().nullable().optional() }).partial({ required: true }).optional(),
    bankLetter: z
      .object({
        required: z.boolean(),
        minimumAmountCents: z.number().int().nullable().optional(),
        mustBeCurrentWithinDays: z.number().int().nullable().optional(),
      })
      .partial({ required: true })
      .optional(),
  })
  .strict();
export type BidderFinancialRequirements = z.infer<typeof BidderFinancialRequirements>;
export const ApprovalStatus = z.enum(['PENDING', 'APPROVED', 'SUSPENDED', 'REVOKED']);
export const StaffRole = z.enum(['admin', 'clerk', 'office', 'cataloger', 'consignor']);
export const LotKind = z.enum(KIND_KEYS as [string, ...string[]]);

export const ApiError = z.object({ error: z.unknown() });

/**
 * Preview→confirm envelope (ADR-30). A mutating endpoint called WITHOUT
 * `confirm: true` returns `{ preview: true, wouldChange }` and changes
 * nothing; the caller shows it, and re-calls with `confirm: true` after human
 * approval. Endpoints that support this declare `confirmable: true` below.
 */
export const Preview = <T extends z.ZodTypeAny>(wouldChange: T) =>
  z.object({ preview: z.literal(true), previewHash: z.string(), wouldChange });

/**
 * A preview's `wouldChange` must declare EVERY field the route puts in it.
 *
 * Zod strips undeclared keys, so an under-declared preview silently hides
 * information from every typed client - and `previewHash` is computed over the
 * full object the SERVER built, so a client cannot even reconstruct what it is
 * being asked to confirm. Several of these were under-declared until the
 * strict-response check in client.ts caught them (engineering rule §1); the
 * MCP server was the surface that broke.
 *
 * `z.record(z.unknown())` is the honest choice where the payload genuinely
 * varies (a lot's attributes, a sale's proposed columns). Where it does not,
 * spell the fields out.
 */
const TransitionPreview = z.object({ saleId: Uuid, from: SaleStatus, status: SaleStatus });
/** deleteSale: what goes with the sale. Only the platform's own setup for it
 *  is ever removed; the route refuses (409, in words) while anything a person
 *  did against the sale exists, so the counts here are never lots or bidders. */
export const SaleDeletePreview = z.object({
  saleId: Uuid, title: z.string(), from: SaleStatus, delete: z.literal(true),
  removes: z.object({ terms: z.number().int(), documents: z.number().int(), rings: z.number().int() }),
});
export const SaleDeleted = z.object({ saleId: Uuid, title: z.string(), deleted: z.literal(true) });
const RegistrationTransitionPreview = z.object({
  registrationId: Uuid,
  from: ApprovalStatus,
  approvalStatus: ApprovalStatus,
  approvalVersion: z.number().int(),
  onlinePaddle: z.number().nullable().optional(),
  bidLimitCents: z.number().int().optional(),
});

// ---------------------------------------------------------------------------
// Entities (response shapes = drizzle rows serialized)
// ---------------------------------------------------------------------------

/** One processed branding image: public URL of the rendition to use plus its
 *  pixel size. Never a storage key. */
export const BrandingImage = z.object({
  url: z.string().url().max(2000),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
}).strict();
export type BrandingImage = z.infer<typeof BrandingImage>;

/** How a sale page is dressed on the public storefront (ADR-55). The company
 *  default lives at TenantSettings.branding; a sale may override it wholesale
 *  (Sale.branding, null = inherit). `enabled: false` is a real value: it turns
 *  the branded layout off for that sale even when the company default is on.
 *  Mirrors BrandingSchema in @hipnumber/db. */
/** When the storefront shows the "Consign to this sale" button (ADR-55
 *  follow-up). Mirrors ConsignmentDisplaySchema in @hipnumber/db.
 *    auto              show while the sale is upcoming (default)
 *    hidden            never show for this sale
 *    until_days_before hide once within N days of the sale start */
export const ConsignmentDisplay = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('auto') }).strict(),
  z.object({ mode: z.literal('hidden') }).strict(),
  z.object({ mode: z.literal('until_days_before'), days: z.number().int().min(1).max(365) }).strict(),
]);
export type ConsignmentDisplay = z.infer<typeof ConsignmentDisplay>;

export const Branding = z.object({
  enabled: z.boolean(),
  /** The company mark, shown in the storefront header instead of the name. */
  logo: BrandingImage.nullable().optional(),
  /** The hero photograph behind the sale title. */
  hero: BrandingImage.nullable().optional(),
  /** Six-digit hex; the page's accent and button colour. */
  accent: z.string().regex(/^#[0-9A-Fa-f]{6}$/).optional(),
  /** Small caps above the title ("FALL CLASSIC"). */
  eyebrow: z.string().max(60).optional(),
  /** One line under the title. */
  tagline: z.string().max(200).optional(),
  /** Markdown for the storefront About page; the nav shows About only when set. */
  about: z.string().max(4000).optional(),
  /** Social links for the footer. Absolute https URLs only. */
  links: z.object({
    facebook: z.string().url().max(500).optional(),
    instagram: z.string().url().max(500).optional(),
    youtube: z.string().url().max(500).optional(),
  }).strict().optional(),
}).strict();
export type Branding = z.infer<typeof Branding>;

export const BrandingAssetKind = z.enum(['hero', 'logo']);
export const BrandingAssetBody = z.object({ uploadKey: z.string().min(1), kind: BrandingAssetKind }).strict();
export const BrandingAssetJobStatus = z.enum(['queued', 'running', 'done', 'failed']);
/** One branding render. In production the request only records the job (the
 *  API has no route to the image store) and the media worker renders it, so
 *  the office polls until `done` (image set) or `failed` (error set); on a
 *  laptop the API renders inline and answers `done` at once. `image` is the
 *  rendition to store in Branding.hero / Branding.logo; `small` is the
 *  phone-size hero (null for a logo). */
export const BrandingAssetJob = z.object({
  id: z.string().uuid(),
  kind: BrandingAssetKind,
  status: BrandingAssetJobStatus,
  image: BrandingImage.nullable(),
  small: BrandingImage.nullable(),
  error: z.string().nullable(),
}).strict();
export type BrandingAssetJob = z.infer<typeof BrandingAssetJob>;

/** ADR-65 (decision 2): what a reversed sale gives back and what the public is
 *  told. `refundPremium` = the buyer's premium is refunded with the hammer
 *  (default true); `publicTurnedBackLabel` = the storefront's word for a
 *  turned-back lot (default "Not Sold"). A set object replaces the company
 *  default wholesale. Read only at the moment a reversal is recorded. */
export const ReversalPolicyConfig = z.object({
  refundPremium: z.boolean().optional(),
  publicTurnedBackLabel: z.string().trim().min(1).max(40).optional(),
}).strict();
/** The policy a reversal recorded now would freeze, and where it came from. */
export const EffectiveReversalPolicy = z.object({
  refundPremium: z.boolean(),
  publicTurnedBackLabel: z.string(),
  source: z.enum(['SALE', 'TENANT', 'DEFAULT']),
});

export const Sale = z.object({
  id: Uuid,
  tenantId: Uuid,
  slug: z.string(),
  title: z.string(),
  status: SaleStatus,
  startsAt: z.string().nullable(),
  timezone: z.string().nullable(),
  location: z.string().nullable(),
  descriptionMd: z.string().nullable(),
  buyerPremium: z.unknown(),
  /** Seller-side commission config (migration 0018). The route has returned it
   *  since then; the contract had not declared it, so every typed client - the
   *  console and every MCP tool - was silently blind to it. Found by the
   *  strict-response check in client.ts. */
  sellerCommission: z.unknown(),
  incrementTable: z.unknown().nullable(),
  paddleRanges: z.object({ liveStart: z.number(), onlineStart: z.number(), externalStart: z.number() }).passthrough(),
  questionnaire: z.unknown().nullable(),
  private: z.boolean(),
  onlineBidAccept: z.enum(['auto', 'manual']),
  /** Auction format (ADR-48). The tenant-declared Requested Mode. */
  auctionMode: AuctionMode,
  /** ADR-69: the product this sale runs under (HIPNUMBER | LOTNUMBER) and the
   *  vertical it serves (LIVESTOCK | GENERAL). Durable on the sale, declared
   *  at creation, locked with the format once bidding opens. The tenant has
   *  no product: ONE company runs sales under both. */
  product: z.string(),
  vertical: z.string(),
  /** When bidding/prebidding opened (ADR-48). NULL = not yet opened, so
   *  auctionMode is still editable; once set the format is locked. */
  biddingOpenedAt: z.string().nullable(),
  /** ADR-69 Phase 2: the RAW timed schedule override (null = inherit the
   *  company default, then the product default). `effectiveTimedSchedule`
   *  in @hipnumber/products is the only resolver; saleSettings returns it. */
  timedSchedule: SaleTimedSchedule.nullable(),
  /** What this sale requires before a bidder is financially qualified
   *  (migration 0033): combinable cardOnFile / deposit / bankLetter. NULL =
   *  inherit the company default; when set it replaces that default wholesale.
   *  `effectiveBidderFinancialRequirements` in @hipnumber/db is the only
   *  resolver - clients display the raw setting, they do not re-derive it. */
  bidderFinancialRequirements: BidderFinancialRequirements.nullable(),
  /** Per-sale CheckShield policy override (ADR-43, migration 0038). NULL =
   *  inherit the company default; when set it replaces that default wholesale.
   *  `effectiveCheckPolicy` in @hipnumber/db is the only resolver. Configured
   *  through the off-contract checkout routes - settling money and its policy
   *  are never agent powers; clients only display it. */
  checkPolicy: z.unknown().nullable(),
  /** Per-sale storefront branding override (ADR-55). null = inherit the company default. */
  branding: Branding.nullable().optional(),
  /** Per-sale consignment-button policy (ADR-55 follow-up). null = inherit. */
  consignmentDisplay: ConsignmentDisplay.nullable().optional(),
  /** ADR-65: per-sale reversal policy override (migration 0079). null =
   *  inherit the company default. Read only when a reversal is recorded. */
  reversalPolicy: ReversalPolicyConfig.nullable().optional(),
  /** Which item kinds a consignor may bring to this sale (ADR-46). NULL =
   *  inherit the company/platform default. The route has returned this column
   *  since ADR-46; the contract had not declared it, so every typed client was
   *  silently blind to it (found by the strict-response check, same class as
   *  sellerCommission). This is the CONFIGURED intent (`configuredItemTypes`
   *  in @hipnumber/db); what the sale actually carries is that list filtered
   *  through its product and vertical (`effectiveKinds` in
   *  @hipnumber/products), which the readiness read reports. */
  acceptedItemTypes: z.array(z.string()).nullable(),
  /** Which equine SUBTYPES this sale accepts when it takes the `equine` kind
   *  (ADR-66 PR 2). NULL = inherit the company/platform default (['horse']).
   *  `effectiveAcceptedEquineTypes` in @hipnumber/annie resolves (fail-closed
   *  against the finite lot-kinds vocabulary). Response echoes the stored list. */
  acceptedEquineTypes: z.array(z.string()).nullable(),
  ringProvisioning: z.record(z.unknown()),
  /** ADR-70 cut 5f (migration 0096): how many times the office has changed
   *  which floor staff run which ring of this sale; 0 = never (every clerk
   *  runs every ring). The roster itself is GET /sales/:saleId/ring-staff. */
  ringStaffVersion: z.number().int(),
  createdAt: z.string(),
});
export type Sale = z.infer<typeof Sale>;

export const Lot = z.object({
  id: Uuid,
  tenantId: Uuid,
  saleId: Uuid,
  ringId: Uuid.nullable(),
  consignorId: Uuid.nullable(),
  hipNumber: z.string(),
  runOrder: z.number().int().nullable(),
  kind: z.string(),
  title: z.string(),
  descriptionMd: z.string().nullable(),
  pricingMode: PricingMode,
  quantity: z.number().int(),
  attributes: z.record(z.unknown()),
  reserveCents: z.number().nullable(),
  openingAskCents: z.number().nullable(),
  status: LotStatus,
  location: z.string().nullable(),
  notesPrivate: z.string().nullable(),
  /** Office-set: in the catalog to be read, not bid on. The public bid
   *  control is greyed out and the lot leaves the sellable count. */
  informational: z.boolean().default(false),
  /** Office-set: a real lot that exists to exercise bidding (a "test bid"
   *  item). It bids normally; the flag lets reports tell a rehearsal bid from
   *  a sale-day one. */
  testItem: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Who consigned it, resolved for display.
   *
   * `consignorId` is the fact; this is the name a clerk needs to read out at
   * the ring without a second round-trip. Nullable because a lot may be
   * unconsigned (added at the ring), and null must be renderable.
   */
  consignorName: z.string().nullable().default(null),
  /**
   * C4: the lot's seller can no longer be changed in the catalog: the lot has a purchase
   * (even a voided one) or its status already reads sold. The same rule the server
   * enforces on PATCH /lots/:lotId and the batch route, so the screen can offer it.
   * Only the catalog list read fills it in; an answer that carries a lot without
   * computing it leaves it out, and a missing value means "not asked", never "unlocked".
   * A lot a ring has up right now is not counted here: the server refuses that one when the change is made.
   */
  sellerLocked: z.boolean().optional(),
  /**
   * The same media projection the public catalog gets: public URLs and
   * dimensions, never the storage key. The clerk sees exactly what the bidder
   * sees, which is the point - the two must not disagree about the horse.
   */
  media: z.array(z.union([
    z.object({ type: z.literal('image'), variants: z.record(z.object({ url: z.string(), width: z.number(), height: z.number() }).passthrough()) }),
    z.object({ type: z.literal('video'), url: z.string().nullable() }),
  ])).default([]),
  /**
   * ADR-70 cut 5c: the ring this lot runs in, by the engine's ring id and the
   * sale's name for it. A one-ring sale runs every lot in its ring. With
   * several rings a lot is in the ring the office put it in, and null means
   * it is in none yet (go-live refuses that). `listLots?ring=<id>` returns one
   * ring's lots.
   */
  ring: z.object({ ringId: z.string(), name: z.string() }).nullable().default(null),
});
export type Lot = z.infer<typeof Lot>;

/** The public projection - an allowlist. Adding a field here is a decision. */
export const PublicLot = z.object({
  id: Uuid,
  hipNumber: z.string(),
  runOrder: z.number().int().nullable(),
  kind: z.string(),
  title: z.string(),
  descriptionMd: z.string().nullable(),
  pricingMode: PricingMode,
  quantity: z.number().int(),
  attributes: z.record(z.unknown()),
  status: LotStatus,
  /** ADR-65: the tenant's own word for a lot whose sale was reversed (the
   *  status reads no_sale); null for every other lot. */
  statusLabel: z.string().nullable().optional(),
  /** Read, not bid on: the page greys the bid control out. A deliberate
   *  addition to the allowlist - a bidder must know before trying to bid. */
  informational: z.boolean().default(false),
  /** A rehearsal lot. Public so the page can say so; a bidder is never left
   *  thinking a test item is for sale. */
  testItem: z.boolean().default(false),
  media: z.array(z.union([
    z.object({ type: z.literal('image'), variants: z.record(z.object({ url: z.string(), width: z.number(), height: z.number() }).passthrough()) }),
    z.object({ type: z.literal('video'), url: z.string().nullable() }),
  ])).default([]),
  /** ADR-70 cut 5c: the engine ring id of the ring this lot runs in (one of
   *  the sale's `rings`), or null while the office has not placed it. Which
   *  ring sells a horse is something sale companies print in the catalog. */
  ring: z.string().nullable().optional(),
});
export type PublicLot = z.infer<typeof PublicLot>;

export const PublicSale = z.object({
  slug: z.string(),
  title: z.string(),
  status: SaleStatus,
  startsAt: z.string().nullable(),
  timezone: z.string().nullable(),
  location: z.string().nullable(),
  descriptionMd: z.string().nullable(),
  /** ADR-69 / D5 follow-up: the product the sale runs under, so a page that holds only
   *  this catalog (the ring-fed TV and Progress boards) can name the catalog number in
   *  the sale's own word. Optional for an older API that does not send it. */
  product: z.string().optional(),
  /** The live ring's WebSocket endpoint - present only when the sale is live.
   *  Clients (bidder app, Auction TV/Progress) discover the ring here. */
  ringEndpoint: z.string().nullable().optional(),
  /** ADR-70 cut 5c: every ring of the sale, in ring order. `endpoint` is set
   *  only while the sale is live, like ringEndpoint (which stays the first
   *  ring's, for clients that know one ring). */
  rings: z.array(z.object({ ringId: z.string(), name: z.string(), endpoint: z.string().nullable() })).optional(),
});

/**
 * A seller, as the barn needs to know them.
 *
 * Deliberately thin. A consignor record carries tax id, drivers licence and
 * an account manager, and none of that belongs to somebody standing in an
 * aisle at 7am holding a lead rope. Check-in records WHO the horse came with
 * so the lot is attributable; the office completes the paperwork before
 * anyone gets paid.
 */
export const Consignor = z.object({
  id: Uuid,
  name: z.string(),
  sellerNumber: z.number().int().nullable().default(null),
  phone: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
  /** Whether the office still has to finish this record before settlement. */
  needsOfficeDetail: z.boolean().default(false),
  /** D1: whether a tax id is on file. The id itself is WRITE-ONLY - no route
   *  returns it, only this flag. */
  hasTaxId: z.boolean().default(false),
  /** Set when this seller also asked to bid. The buyer desk issues the
   *  card; a timestamp so the desk can see who has been waiting longest. */
  bidderCardRequestedAt: z.string().nullable().default(null),
});

/** A seller's tax id (EIN, SSN, ITIN or a foreign tax number) as the office
 *  types it. Deliberately loose on shape - sellers are not all American - and
 *  strict on junk: letters, digits, spaces and hyphens, 5 to 20 characters. */
export const ConsignorTaxId = z.string().trim()
  .min(5, 'A tax ID is at least 5 characters.')
  .max(20, 'A tax ID is at most 20 characters.')
  .regex(/^[A-Za-z0-9][A-Za-z0-9 -]*$/, 'Use letters, digits, spaces and hyphens only.');

/** D1: set (or remove, with null) a seller's tax id. WRITE-ONLY: the value is
 *  never echoed by any response. Not confirmable - a preview would have to
 *  carry the number. */
export const UpdateConsignorBody = z.object({ taxId: ConsignorTaxId.nullable() }).strict();

export const CreateConsignorBody = z.object({
  name: z.string().min(1).max(200),
  phone: z.string().max(40).optional(),
  email: z.string().email().optional(),
  /** "They also want to bid." Sends them to the buyer desk rather than
   *  issuing a number here: a consignor is who we PAY and a registration is
   *  who may BID, and only one desk should be handing out paddles. */
  wantsBidderCard: z.boolean().optional(),
  confirm: z.boolean().optional(),
}).strict();

/** Clear (or raise) the bidder-card request as the desk works the queue. */
export const BidderCardRequestBody = z.object({ requested: z.boolean(), confirm: z.boolean().optional() }).strict();

/** What the office has noted about a person (K2): how CheckShield treats their checks. */
export const PaymentTrustTier = z.enum(['NORMAL', 'TRUSTED', 'CASH_ONLY']);
export type PaymentTrustTier = z.infer<typeof PaymentTrustTier>;

/** One registration waiting for the office, across every open sale. */
export const PendingRegistration = z.object({
  id: Uuid,
  saleId: Uuid,
  saleSlug: z.string(),
  saleTitle: z.string(),
  saleStartsAt: z.string().nullable(),
  saleTimezone: z.string().nullable(),
  accountId: Uuid,
  bidderName: z.string().nullable(),
  bidderEmail: z.string().nullable(),
  bidderPhone: z.string().nullable(),
  termsVersion: z.number().int().nullable(),
  createdAt: z.string(),
  /** The office's tier and one-line alert for this person (K2): so the approve dialog can show them. */
  paymentTrust: PaymentTrustTier.default('NORMAL'),
  alertText: z.string().nullable().default(null),
});
export type PendingRegistration = z.infer<typeof PendingRegistration>;
export const PendingRegistrations = z.object({ count: z.number().int().nonnegative(), items: z.array(PendingRegistration) });
export type PendingRegistrations = z.infer<typeof PendingRegistrations>;

export const Registration = z.object({
  id: Uuid,
  tenantId: Uuid,
  saleId: Uuid,
  accountId: Uuid,
  onlinePaddle: z.number().int().nullable(),
  livePaddle: z.number().int().nullable(),
  approvalStatus: ApprovalStatus,
  approvalVersion: z.number().int(),
  bidLimitCents: z.number().nullable(),
  termsVersion: z.number().int().nullable(),
  termsAcceptedAt: z.string().nullable(),
  questionnaireAnswers: z.record(z.unknown()),
  createdAt: z.string(),
  /**
   * Who the paddle belongs to, denormalized for the clerk.
   *
   * A clerk about to commit a sale to "paddle 247" needs to see a HUMAN there,
   * not an account UUID: reading back "247, John Smith" is the cross-check that
   * catches a mistyped digit before the horse changes hands. Staff-only routes,
   * same as the rest of this record.
   *
   * Phone is here so the office can search by it and reach a buyer; the console
   * shows only the last four so a full number is not left sitting on a screen
   * in a public sale office.
   */
  bidderName: z.string().nullable().default(null),
  bidderPhone: z.string().nullable().default(null),
  /**
   * ADR-70 cut 3: the id the RING knows this bidder by (the account's sign-in
   * subject, or the account id where there is none - SqsApprovals.ringBidderId),
   * so the clerk console can light the bidder holding the high bid. Staff-only,
   * like the rest of this record; the floor snapshot already names it.
   */
  ringBidderId: z.string().nullable().default(null),
  /**
   * K2: what the office has noted about this person, for the desk. The tier is
   * how CheckShield treats their checks; the alert is the one line every desk
   * should see at check-in and checkout. The private note is NOT here.
   */
  paymentTrust: PaymentTrustTier.default('NORMAL'),
  alertText: z.string().nullable().default(null),
});
export type Registration = z.infer<typeof Registration>;

export const SaleTerms = z.object({
  id: Uuid, tenantId: Uuid, saleId: Uuid, version: z.number().int(), bodyMd: z.string(), publishedAt: z.string().nullable(),
});

export const LotDocument = z.object({
  id: Uuid, tenantId: Uuid, lotId: Uuid, docType: z.string(), label: z.string().nullable(), r2Key: z.string().nullable(),
  status: z.enum(['pending', 'received', 'verified']), uploadedAt: z.string().nullable(),
});

export const LotMedia = z.object({
  id: Uuid, tenantId: Uuid, lotId: Uuid, mediaType: z.enum(['image', 'video_url']), r2Key: z.string().nullable(),
  url: z.string().nullable(), variants: z.record(z.unknown()), position: z.number().int(), createdAt: z.string(),
  /** Focal point, normalized 0..1 (ADR-56 hardening B): 'auto' from ingest
   *  analysis, 'manual' when the tenant set it. Null on video rows and on
   *  photos ingested before focal detection existed (backfill available). */
  focalX: z.number().min(0).max(1).nullable().optional(),
  focalY: z.number().min(0).max(1).nullable().optional(),
  focalSource: z.enum(['auto', 'manual']).nullable().optional(),
  /** False while the render job has not finished (`variants` is empty and the
   *  storefront shows nothing for the photo yet); true once the renditions
   *  exist. A video link has nothing to render and is always true. */
  rendered: z.boolean(),
  /** Why the render failed, in words the office can read; null otherwise. */
  renderError: z.string().nullable(),
});
/** Manual focal-point override: normalized coordinates of the subject. */
export const LotMediaFocalBody = z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).strict();

export const DocRequirement = z.object({ docType: z.string(), label: z.string(), required: z.boolean() });

export const ImportIssue = z.object({
  row: z.number().int(), column: z.string().optional(), severity: z.enum(['error', 'warning']), message: z.string(),
});
export const ImportPlan = z.object({
  headers: z.array(z.object({ raw: z.string(), target: z.unknown().nullable() })),
  lots: z.array(z.record(z.unknown())),
  issues: z.array(ImportIssue),
  summary: z.object({
    rows: z.number(), creates: z.number(), updates: z.number(), errors: z.number(), warnings: z.number(),
    unmappedColumns: z.array(z.string()), unmatchedImages: z.array(z.string()),
  }).passthrough(),
  ok: z.boolean(),
  /** Which lots the plan was read against: send it back with the commit and a catalog that moved since is a 409 CATALOG_CHANGED. */
  basis: z.string().optional(),
});
export const ImportLog = z.object({
  id: Uuid, tenantId: Uuid, saleId: Uuid, actorId: z.string(), filename: z.string().nullable(),
  status: z.enum(['planned', 'committed', 'failed']), summary: z.record(z.unknown()), issues: z.array(z.unknown()), createdAt: z.string(),
});

// C3: the run order of a sale's lots, and renumbering the lots to match it.
/** Every lot of the sale, once, in the order they should run: the first runs first. */
export const SetLotOrderBody = z.object({
  lotIds: z.array(Uuid).min(1).max(5000),
  /** `lotOrderBasis` of the lots as the caller loaded them. A preview that has none is answered with the current one (the preview's `basis`); a confirm must carry one, and a basis that is no longer the order the sale is in is a 409 ORDER_STALE. */
  basis: z.string().min(1).max(64).optional(),
  confirm: z.boolean().optional(),
}).strict();
/** One lot's place in the order, counted from 1: where it ran in the order before, and where it runs now. */
export const LotOrderMove = z.object({ lotId: Uuid, hipNumber: z.string(), from: z.number().int().positive(), to: z.number().int().positive() });
/** `changed` is how many lots had their run order written (a sale never ordered before numbers every lot, moved or not). */
export const SetLotOrderResult = z.object({ lots: z.number().int(), changed: z.number().int(), moves: z.array(LotOrderMove) });
/** `basis` as for SetLotOrderBody: the numbers a person saw in the preview are the ones handed out. */
export const RenumberLotsBody = z.object({ basis: z.string().min(1).max(64).optional(), confirm: z.boolean().optional() }).strict();
export const LotRenumber = z.object({ lotId: Uuid, from: z.string(), to: z.string() });
export const LotRenumberSkip = z.object({ lotId: Uuid, hipNumber: z.string(), reason: z.literal('NOT_A_PLAIN_NUMBER') });
export const RenumberLotsResult = z.object({ changed: z.number().int(), renumbers: z.array(LotRenumber), skipped: z.array(LotRenumberSkip) });

// ---------------------------------------------------------------------------
// Context-shaped reads (ADR-30 §2.2) - what the model reads, computed
// server-side so tokens stay low and the logic stays testable.
// ---------------------------------------------------------------------------

export const LotBrief = z.object({
  lot: Lot,
  kindLabel: z.string(),
  /** Facet lines in the kind's order, already formatted. */
  specs: z.array(z.object({ label: z.string(), value: z.string() })),
  media: z.array(LotMedia),
  documents: z.array(LotDocument),
  release: z.object({ required: z.array(DocRequirement), missing: z.array(DocRequirement), releasable: z.boolean() }),
  consignor: z.object({ id: Uuid, name: z.string(), sellerNumber: z.number().int().nullable() }).nullable(),
  neighbors: z.object({
    prev: z.object({ id: Uuid, hipNumber: z.string(), title: z.string() }).nullable(),
    next: z.object({ id: Uuid, hipNumber: z.string(), title: z.string() }).nullable(),
  }),
});
export type LotBrief = z.infer<typeof LotBrief>;

export const SaleBrief = z.object({
  sale: Sale,
  counts: z.object({
    lots: z.number().int(),
    byKind: z.record(z.number().int()),
    byStatus: z.record(z.number().int()),
    registrations: z.record(z.number().int()), // by approvalStatus
    releasable: z.number().int(),
    missingDocs: z.number().int(),
  }),
  terms: z.object({ currentVersion: z.number().int().nullable(), publishedAt: z.string().nullable() }),
  ring: z.object({ provisioned: z.boolean(), endpoint: z.string().nullable() }),
  /** ADR-70 cut 5b: each ring the sale runs, in ring order. `ring` is the first. */
  rings: z.array(z.object({ ringId: z.string(), name: z.string(), provisioned: z.boolean(), endpoint: z.string().nullable() })).optional(),
});
export type SaleBrief = z.infer<typeof SaleBrief>;

/** One finding from the integrity check. `severity` drives ordering in the
 *  console; `fix` names the confirmable action that resolves it, when one
 *  exists - the AI-3 monitor turns these into one-tap proposals. */
export const IntegrityFinding = z.object({
  code: z.string(),
  severity: z.enum(['error', 'warning', 'info']),
  message: z.string(),
  lotId: Uuid.optional(),
  hipNumber: z.string().optional(),
  registrationId: Uuid.optional(),
  fix: z.object({ action: z.string(), args: z.record(z.unknown()) }).optional(),
});
export const IntegrityReport = z.object({
  saleId: Uuid,
  generatedAt: z.string(),
  findings: z.array(IntegrityFinding),
  summary: z.object({ errors: z.number().int(), warnings: z.number().int(), info: z.number().int() }),
});
export type IntegrityReport = z.infer<typeof IntegrityReport>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Tenant settings + sale settings (migration 0041): Tenant Default ->
// Auction Override -> Operational Record. Sale money columns are nullable;
// NULL = inherit tenants.settings.saleDefaults; the resolvers live in
// @hipnumber/db settings.ts and the routes return both the raw override and
// the resolved `effective` so the UI renders "Use company default" vs
// "Override" without re-deriving anything.
// ---------------------------------------------------------------------------

/** Per-channel buyer premium in basis points - the shape sales.buyer_premium
 *  has used since migration 0000 ({ live, online }, auction-core's
 *  BuyerPremium). Writes are strict; reads elsewhere stay z.unknown()
 *  because the column predates validation. */
export const BuyerPremiumConfig = z.object({
  live: z.number().int().nonnegative().optional(),
  online: z.number().int().nonnegative().optional(),
}).strict();

/** Flat seller commission in bps (migration 0018 shape). */
export const SellerCommissionConfig = z.object({ bps: z.number().int().nonnegative().optional() }).strict();


export const PaddleRangesConfig = z.object({
  liveStart: z.number().int().positive(),
  onlineStart: z.number().int().positive(),
  externalStart: z.number().int().positive(),
}).strict();

export const CreateSaleBody = z.object({
  slug: z.string().min(1).regex(/^[a-z0-9-]+$/),
  title: z.string().min(1),
  startsAt: IsoDate.optional(),
  timezone: z.string().optional(),
  location: z.string().optional(),
  descriptionMd: z.string().optional(),
  /** Omitted → prefilled from tenant settings.saleDefaults.paddleRanges
   *  (creation prefill - the column is NOT NULL, so there is no NULL-means-
   *  inherit at read for it). */
  paddleRanges: PaddleRangesConfig.optional(),
  /** Omitted → prefilled from tenant settings.saleDefaults.onlineBidAccept. */
  onlineBidAccept: z.enum(['auto', 'manual']).optional(),
  /** Auction format (ADR-48, ADR-69). Omitted → prefilled from
   *  settings.saleDefaults.auctionMode, else the product's default (LotNumber
   *  is born TIMED, HipNumber LIVE). A default, never a restriction. */
  auctionMode: AuctionMode.optional(),
  /** ADR-69: omitted → the company's default product (settings.products
   *  .default, else HIPNUMBER). A product the company may not sell under is
   *  422 PRODUCT_NOT_ALLOWED. */
  product: ProductKey.optional(),
  /** ADR-69: omitted → the product's default vertical. */
  vertical: VerticalKey.optional(),
  /** ADR-29/30: omit → preview only; true → execute. */
  confirm: z.boolean().optional(),
}).strict();

export const TenantSettings = z.object({
  /** Who the company is - identity strings for the console and documents.
   *  currency defaults to 'USD' at READ; unset stays unset in storage. */
  organization: z.object({
    displayName: z.string().max(200).optional(),
    legalName: z.string().max(200).optional(),
    address: z.string().max(500).optional(),
    phone: z.string().max(40).optional(),
    email: z.string().max(320).optional(),
    website: z.string().max(500).optional(),
    timezone: z.string().max(64).optional(),
    currency: z.string().max(8).optional(),
    defaultSaleLocation: z.string().max(200).optional(),
  }).strict().optional(),
  saleDefaults: z.object({
    /** Inherited at read by sales whose buyer_premium is NULL. */
    buyerPremium: BuyerPremiumConfig.optional(),
    /** Inherited at read by sales whose seller_commission is NULL. */
    sellerCommission: SellerCommissionConfig.optional(),
    /** ADR-65 (decision 2): what a reversed sale gives back and what the
     *  public is told; read only when a reversal is recorded. */
    reversalPolicy: ReversalPolicyConfig.optional(),
    /** STORED CONFIG ONLY - nothing consumes these yet (ADR-38 gate:
     *  config now, money movement in a later slice). */
    entryFee: z.object({ cents: Cents }).strict().optional(),
    noSaleFee: z.object({ cents: Cents }).strict().optional(),
    /** Inherited at read by sales whose increment_table is NULL. Opaque
     *  jsonb, same posture as Sale.incrementTable. */
    incrementTable: z.unknown().optional(),
    /** Creation prefill for new sales (NOT NULL column - no read inherit). */
    paddleRanges: PaddleRangesConfig.optional(),
    /** Creation prefill for new sales (same reason). */
    onlineBidAccept: z.enum(['auto', 'manual']).optional(),
    /** Company default auction format, prefilled into a new sale (ADR-48). */
    auctionMode: AuctionMode.optional(),
    /** The company's standard terms (markdown). A new sale is born with these
     *  published as its terms v1; the office may publish a sale-specific
     *  version on top. */
    termsMd: z.string().min(1).max(20000).optional(),
    /** Company default for when the storefront shows the consign button. */
    consignmentDisplay: ConsignmentDisplay.optional(),
    /** ADR-69 Phase 2: company default timed close policy (no first close:
     *  that is per sale). Inherited at read by sales whose timed_schedule
     *  leaves a field unset; the product default fills the rest. */
    timedSchedule: TimedScheduleDefaults.optional(),
  }).strict().optional(),
  /** Company-default storefront branding (ADR-55). */
  branding: Branding.optional(),
  /** ADR-69: which products this company may create sales under, and the one
   *  a new sale takes when the request names none. Absent = HipNumber only.
   *  OPERATOR-ONLY: the tenant PUT /settings refuses a change (403
   *  PRODUCTS_OPERATOR_ONLY) and carries the stored value through; the
   *  platform console writes it. Plain strings here; the registry resolves. */
  products: z.object({
    allowed: z.array(z.string().min(1)).optional(),
    default: z.string().min(1).optional(),
  }).strict().optional(),
  /** D1: how the company pays its sellers. `taxIdRequiredAboveCents` is the
   *  payout net above which the seller must have a tax id on file; absent =
   *  $600.00. 0 = every payout needs one. */
  settlement: z.object({
    taxIdRequiredAboveCents: Cents.optional(),
  }).strict().optional(),
  /** Subtrees owned by other surfaces (Annie, invoicing). Their shapes are
   *  validated by @hipnumber/db on write; the admin carries them through a
   *  save untouched (settings-merge.ts), so here they are opaque records. */
  annie: z.record(z.unknown()).optional(),
  invoicing: z.record(z.unknown()).optional(),
}).strict();
export type TenantSettings = z.infer<typeof TenantSettings>;

/** saleDefaults as a READ is checked (ADR-73 cut 2a): the seller commission is
 *  left open at runtime, because a stored commission schedule is not the flat
 *  `{ bps }` the request schema above accepts, and a response check that failed
 *  on it would break every settings screen. The TYPE stays the flat shape on
 *  purpose: a screen reads `.bps` and gets undefined for a schedule. A save
 *  that hands the schedule back is refused by the strict write schemas (this
 *  one and @hipnumber/db's); a save that leaves it out is accepted and the
 *  old screens do leave it out, which cut 4's writers must close (ADR-73).
 *  Nothing can store a schedule until then, and that cut widens the type. */
const TenantSaleDefaultsRead = TenantSettings.shape.saleDefaults.unwrap().extend({ sellerCommission: z.unknown().optional() }).optional();
const readTolerantSettings: z.ZodType<TenantSettings> = TenantSettings.extend({ saleDefaults: TenantSaleDefaultsRead }) as unknown as z.ZodType<TenantSettings>;

/** Deliberately WITHOUT theme: tenants.theme carries Annie's per-tenant
 *  config (theme.annie), which is not tenant-settings surface area. */
export const TenantSettingsView = z.object({
  name: z.string(),
  slug: z.string(),
  settings: readTolerantSettings.nullable(),
});

export const UpdateTenantSettingsBody = z.object({
  /** Optionally rename the tenant (tenants.name) in the same act. */
  name: z.string().min(1).max(200).optional(),
  /** Replaces tenants.settings WHOLESALE with this validated object. */
  settings: TenantSettings,
  confirm: z.boolean().optional(),
}).strict();

/** The tenant's saleDefaults as they apply to one sale (null = unset), and
 *  the resolved effective values, so "Use company default" needs no second
 *  round trip. Money reads are z.unknown() like Sale's - the columns predate
 *  validation. */
export const SaleTenantDefaults = z.object({
  buyerPremium: z.unknown().nullable(),
  sellerCommission: z.unknown().nullable(),
  /** ADR-65: the company-default reversal policy, null when never configured. */
  reversalPolicy: ReversalPolicyConfig.nullable(),
  incrementTable: z.unknown().nullable(),
  /** The company-default branding (ADR-55), null when never configured. */
  branding: Branding.nullable(),
  /** The company-default consignment-button policy, null when never configured. */
  consignmentDisplay: ConsignmentDisplay.nullable(),
  /** ADR-69 Phase 2: the company-default timed close policy, null when never configured. */
  timedSchedule: TimedScheduleDefaults.nullable(),
});
export const SaleEffectiveConfig = z.object({
  buyerPremium: z.unknown(),
  sellerCommission: z.unknown(),
  /** ADR-65: the reversal policy a reversal recorded NOW would freeze (sale
   *  override, else company default, else the built-in default) and where
   *  it came from. */
  reversalPolicy: EffectiveReversalPolicy,
  incrementTable: z.unknown().nullable(),
  /** What the storefront renders with: the sale override, else the company default, else null. */
  branding: Branding.nullable(),
  /** The consignment-button policy that applies (sale override, else company default, else null = auto). */
  consignmentDisplay: ConsignmentDisplay.nullable(),
  /** ADR-69 Phase 2: the timed schedule that applies (sale, else company,
   *  else product), with the source of each policy field. Resolved for
   *  every sale; only a TIMED sale acts on it. */
  timedSchedule: EffectiveTimedSchedule,
});

export const UpdateSaleBody = z.object({
  title: z.string().min(1).optional(),
  startsAt: IsoDate.nullable().optional(),
  timezone: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  descriptionMd: z.string().nullable().optional(),
  private: z.boolean().optional(),
  onlineBidAccept: z.enum(['auto', 'manual']).optional(),
  /** Auction format (ADR-48). Locked once bidding has opened (409
   *  AUCTION_MODE_LOCKED); a change before then requires re-provisioning. */
  auctionMode: AuctionMode.optional(),
  /** ADR-69: the product and vertical share the format's lifecycle lock
   *  (409 PRODUCT_LOCKED once bidding has opened); a product the company may
   *  not use is 422 PRODUCT_NOT_ALLOWED. */
  product: ProductKey.optional(),
  vertical: VerticalKey.optional(),
  /** null clears the override back to "inherit the company default". */
  buyerPremium: BuyerPremiumConfig.nullable().optional(),
  sellerCommission: SellerCommissionConfig.nullable().optional(),
  /** ADR-65: per-sale reversal policy override; null clears back to the
   *  company default. Editable while live: it is read only at the moment a
   *  reversal is recorded, never re-read for one already recorded. */
  reversalPolicy: ReversalPolicyConfig.nullable().optional(),
  incrementTable: z.unknown().optional(),
  paddleRanges: PaddleRangesConfig.optional(),
  /** Which item kinds Annie may accept at THIS sale (ADR-46). null clears the
   *  override back to the company default; an array replaces it wholesale. */
  acceptedItemTypes: z.array(z.string().min(1)).nullable().optional(),
  /** Which equine subtypes this sale accepts (ADR-66 PR 2). Only sale-selectable
   *  values (horse|mule|donkey|hinny; reserved zebra rejected) - the finite
   *  vocabulary, so a bad write cannot persist an unrenderable subtype. null
   *  clears the override back to the company default; an array replaces it
   *  wholesale. */
  acceptedEquineTypes: z.array(saleSelectableEquineTypeSchema).nullable().optional(),
  /** Storefront branding override (ADR-55). null clears back to the company
   *  default; an object replaces it wholesale. Editable while live. */
  branding: Branding.nullable().optional(),
  /** Consignment-button policy override (ADR-55 follow-up). null clears back to
   *  the company default. Editable while live (presentation only). */
  consignmentDisplay: ConsignmentDisplay.nullable().optional(),
  /** ADR-69 Phase 2: timed schedule override; null clears back to the company
   *  default. The policy fields lock with the format once bidding opens (409
   *  TIMED_SCHEDULE_LOCKED); the first close stays editable, even while live,
   *  until the control plane has opened the lots. A first close at or before
   *  the sale's start is 422 TIMED_SCHEDULE_INVALID. */
  timedSchedule: SaleTimedSchedule.nullable().optional(),
  confirm: z.boolean().optional(),
}).strict();

export const SaleWithConfig = Sale.extend({
  tenantDefaults: SaleTenantDefaults,
  effective: SaleEffectiveConfig,
});

export const SaleSettingsView = z.object({
  /** The editable subset plus identity/lifecycle context. Money fields are
   *  the RAW overrides (null = inherit); `effective` is what applies. */
  sale: z.object({
    id: Uuid,
    slug: z.string(),
    status: SaleStatus,
    createdAt: z.string(),
    title: z.string(),
    startsAt: z.string().nullable(),
    timezone: z.string().nullable(),
    location: z.string().nullable(),
    descriptionMd: z.string().nullable(),
    private: z.boolean(),
    onlineBidAccept: z.enum(['auto', 'manual']),
    auctionMode: AuctionMode,
    /** ADR-69: durable on the sale; locked with the format once bidding opens. */
    product: z.string(),
    vertical: z.string(),
    biddingOpenedAt: z.string().nullable(),
    buyerPremium: z.unknown().nullable(),
    sellerCommission: z.unknown().nullable(),
    /** The RAW reversal policy override (null = inherit). */
    reversalPolicy: ReversalPolicyConfig.nullable(),
    incrementTable: z.unknown().nullable(),
    paddleRanges: z.object({ liveStart: z.number(), onlineStart: z.number(), externalStart: z.number() }).passthrough(),
    /** The RAW branding override (null = inherit). */
    branding: Branding.nullable(),
    /** The RAW consignment-button override (null = inherit). */
    consignmentDisplay: ConsignmentDisplay.nullable(),
    /** The RAW timed schedule override (null = inherit). ADR-69 Phase 2. */
    timedSchedule: SaleTimedSchedule.nullable(),
  }),
  tenantDefaults: SaleTenantDefaults,
  effective: SaleEffectiveConfig,
  /** Tenant-safe readiness (ADR-48): is the auction prepared for its CURRENT
   *  format? True when the ring is provisioned for `sale.auctionMode`. No ring
   *  internals are exposed - the tenant sees "ready" vs "preparing", nothing
   *  more. The operator console carries the full requested/provisioned/observed
   *  truth. */
  infrastructureReady: z.boolean(),
});

/** ADR-69 Phase 2: the office's view of a TIMED sale's close plan. The
 *  effective schedule, every lot the control plane will open with its planned
 *  close, what the ring costs to keep up for the window, and the ring's own
 *  phase per lot once it has opened them. Derived per read; the only stored
 *  part is the `timedOpens` marker the worker wrote when it opened the lots. */
export const TimedLotPhase = z.enum(['OPEN', 'EXTENDED', 'CLOSED_PENDING', 'SOLD', 'NO_SALE', 'VOIDED']);
export const TimedPlanLot = z.object({
  lotId: Uuid,
  hipNumber: z.string(),
  title: z.string(),
  /** The planned close (ISO), before any extension. */
  closesAt: z.string(),
  closeGroupId: z.string().nullable(),
  /** The ring's truth once it opened the lot, else null. closeSeq is the
   *  close a timed recovery answers (ADR-70 cut 6i), while the lot stays
   *  closed; reopens counts the recoveries that reopened it. */
  live: z.object({ phase: TimedLotPhase, scheduledCloseAt: z.string(), extensions: z.number().int(), price: z.number().nullable(), closeSeq: z.number().int().nullable(), reopens: z.number().int() }).nullable(),
});

/** ADR-70 cut 6i (hitlist 17.4): timed recovery. Why a lot would not reopen,
 *  judged from the ring's log (auction-core timedRecoveryBlocker). */
export const TimedRecoverySkipReason = z.enum(['NOT_OPENED', 'SOLD', 'STILL_OPEN', 'AWAITING_STAFF', 'VOIDED', 'CHANGED_SINCE']);
/** The office's request, as stored on the sale, and once the worker sent it
 *  the closes it fixed and the ring's per-lot answer. */
export const TimedRecoveryMarker = z.object({
  id: Uuid,
  status: z.enum(['PENDING', 'SENT', 'REFUSED', 'EXPIRED']),
  requestedAt: z.string(),
  requestedBy: z.string(),
  reason: z.string(),
  graceMinutes: z.number().int(),
  lots: z.array(z.object({ lotId: Uuid, closeSeq: z.number().int() })),
  planned: z.array(z.object({ lotId: Uuid, closeSeq: z.number().int(), closesAt: z.number(), closeGroupId: z.string().nullable() })).nullable(),
  sentAt: z.string().nullable(),
  answeredAt: z.string().nullable(),
  reopened: z.array(z.string()),
  skipped: z.array(z.object({ lotId: z.string(), reason: z.string() })),
  refusal: z.string().nullable(),
});
export const TimedRecoveryBody = z.object({
  lotIds: z.array(Uuid).min(1).max(500),
  /** Whole minutes added to the two-minute notice before the first fresh close. */
  graceMinutes: z.number().int().min(0).max(120),
  reason: z.string().trim().min(1).max(500),
  confirm: z.boolean().optional(),
}).strict();
/** The preview: per lot, what would reopen (closesAfterMs counts from when
 *  the ring takes the request) and what would not, and why. */
export const TimedRecoveryPreview = z.object({
  saleId: Uuid,
  graceMinutes: z.number().int(),
  noticeMinutes: z.number().int(),
  reason: z.string(),
  pattern: TimedClosePattern,
  reopen: z.array(z.object({ lotId: Uuid, hipNumber: z.string(), title: z.string(), closeSeq: z.number().int(), closesAfterMs: z.number(), closeGroupId: z.string().nullable() })),
  skip: z.array(z.object({ lotId: Uuid, hipNumber: z.string(), reason: TimedRecoverySkipReason })),
});
export const TimedRecoveryAccepted = z.object({
  timedRecovery: TimedRecoveryMarker,
  skip: z.array(z.object({ lotId: Uuid, hipNumber: z.string(), reason: TimedRecoverySkipReason })),
});
export const TimedPlanView = z.object({
  saleId: Uuid,
  auctionMode: AuctionMode,
  schedule: EffectiveTimedSchedule,
  /** When the lots open: the sale's start (null = no date yet). */
  opensAt: z.string().nullable(),
  /** The planner's verdict. Only an ok plan lists lots. */
  plan: z.union([
    z.object({ ok: z.literal(true), firstCloseAt: z.string(), lastCloseAt: z.string(), lots: z.number().int() }),
    z.object({ ok: z.literal(false), reason: z.enum(['NOT_TIMED', 'NO_OPEN_TIME', 'NO_FIRST_CLOSE', 'NO_LOTS', 'CLOSE_IN_PAST', 'INCREMENT_TABLE_INVALID']) }),
  ]),
  lots: z.array(TimedPlanLot),
  /** The ring's lifetime for this plan (open time minus a day, through the
   *  last close plus a margin), with the running cost at the ring stack's
   *  size, so the operator can see a week-long sale for what it costs. */
  lifetime: z.object({ provisionBy: z.string(), destroyAfter: z.string(), hours: z.number(), estimatedUsd: z.number(), exceedsQueueRetention: z.boolean() }).nullable(),
  /** What the control plane recorded when it opened (or failed to open) the lots. */
  timedOpens: z.object({ at: z.string(), status: z.enum(['OPENED', 'REFUSED']), opened: z.number().int(), skipped: z.array(z.object({ lotId: z.string(), reason: z.string() })), reason: z.string().optional() }).nullable(),
  /** Whether the live phases were read from the ring's log on this call. */
  liveRead: z.boolean(),
  /** ADR-70 cut 6i: the ring has opened the lots (its log names them, or the
   *  control plane recorded the opens). The plan is history from then on. */
  opened: z.boolean(),
  /** Whether the ring's log says the sale is finalized (null = not read). */
  ringFinalized: z.boolean().nullable(),
  /** The latest timed recovery request and the ring's answer, if any. */
  timedRecovery: TimedRecoveryMarker.nullable(),
});
export type TimedPlanView = z.infer<typeof TimedPlanView>;

/** listSales rows: the sale plus the cheap aggregates the auctions list
 *  renders (grouped counts, one query each - never N+1). */
export const SaleListItem = Sale.extend({
  counts: z.object({
    lots: z.number().int(),
    registrations: z.number().int(),
    consignors: z.number().int(),
  }),
});

export const CreateLotBody = z.object({
  hipNumber: z.string().min(1),
  kind: LotKind,
  title: z.string().min(1),
  descriptionMd: z.string().optional(),
  runOrder: z.number().int().optional(),
  pricingMode: PricingMode.optional(),
  quantity: z.number().int().min(1).optional(),
  attributes: z.record(z.unknown()).optional(),
  reserveCents: Cents.optional(),
  openingAskCents: Cents.optional(),
  consignorId: Uuid.optional(),
  location: z.string().optional(),
  notesPrivate: z.string().optional(),
  informational: z.boolean().optional(),
  testItem: z.boolean().optional(),
  /** ADR-70 cut 5c: the ring the lot runs in, by engine ring id (a lot added
   *  at a ring's console lands in that ring). */
  ring: z.string().optional(),
  /** ADR-29/30: omit → preview only; true → execute. */
  confirm: z.boolean().optional(),
}).strict();

export const PatchLotBody = CreateLotBody.partial().extend({
  status: EditableLotStatus.optional(),
  /** ADR-70 cut 5c: move the lot to another ring; null takes it out of every ring. */
  ring: z.string().nullable().optional(),
}).strict();

/** ADR-70 cut 5c: put lots in a ring, or (null) take them out of every ring. */
export const MoveLotsToRingBody = z.object({
  ring: z.string().nullable(),
  lotIds: z.array(Uuid).min(1).max(2000),
  confirm: z.boolean().optional(),
}).strict();
export const RingMove = z.object({ lotId: Uuid, hipNumber: z.string(), from: z.string().nullable(), to: z.string().nullable() });
export const MoveLotsToRingResult = z.object({ ring: z.string().nullable(), moved: z.number().int(), moves: z.array(RingMove) });

/** C4: set one consignor on many lots. The preview and the answer list the lots
 *  that change and the lots that were left alone, each with the plain reason. */
export const SetLotsConsignorBody = z.object({
  consignorId: Uuid,
  lotIds: z.array(Uuid).min(1).max(2000),
  confirm: z.boolean().optional(),
}).strict();
export const LotConsignorMove = z.object({ lotId: Uuid, hipNumber: z.string(), from: Uuid.nullable(), to: Uuid });
/** Why a lot's consignor is not changed: it has sold (SOLD_LOT_CONSIGNOR_LOCKED), and on top of that its proceeds are on a paid-out statement (CONSIGNOR_LOCKED_BY_PAYOUT, with that statement's id); or its auction is live and it already has a consignor (SALE_IS_LIVE). */
export const LotConsignorLockCode = z.enum(['CONSIGNOR_LOCKED_BY_PAYOUT', 'SOLD_LOT_CONSIGNOR_LOCKED', 'SALE_IS_LIVE']);
export const LotConsignorSkip = z.object({
  lotId: Uuid, hipNumber: z.string(), reason: LotConsignorLockCode, statementId: Uuid.nullable(), message: z.string(),
});
export const SetLotsConsignorResult = z.object({
  consignorId: Uuid, changed: z.number().int(), moves: z.array(LotConsignorMove), skipped: z.array(LotConsignorSkip),
});

export const TermsBody = z.object({ bodyMd: z.string().min(1), confirm: z.boolean().optional() }).strict();
export const RegisterBody = z.object({ termsVersion: z.number().int().positive(), confirm: z.boolean().optional() }).strict();
// The ring bids in whole dollars, so a bid limit under $1 would stop every bid: it is refused, never raised to $1.
const BidLimitCents = z.number().int().min(100, 'a bid limit is at least $1.00 (100 cents)');
export const WalkUpBody = z.object({ displayName: z.string().min(1).max(160), email: z.string().email().optional(), livePaddle: z.number().int().positive().optional(), bidLimitCents: BidLimitCents.optional(), confirm: z.boolean().optional() }).strict();
export const ApproveBody = z.object({
  onlinePaddle: z.number().int().positive().optional(),
  bidLimitCents: BidLimitCents.optional(),
  confirm: z.boolean().optional(),
}).strict();
export const TransitionBody = z.object({ confirm: z.boolean().optional() }).strict();
export const GoLiveBody = z.object({ ringEndpoint: z.string().optional(), confirm: z.boolean().optional() }).strict(); // ringEndpoint is DEV-ONLY; prod uses the operator-provisioned ring
export const RehearseBody = z.object({ note: z.string().max(500).optional() }).strict();
export const DocumentBody = z.object({
  docType: z.string().min(1), label: z.string().optional(), r2Key: z.string().optional(),
  status: z.enum(['pending', 'received', 'verified']).optional(),
}).strict();
export const PresignBody = z.object({ contentType: z.string().min(1), contentLength: z.number().int().positive() }).strict();
export const AttachBody = z.object({ uploadKey: z.string().min(1), position: z.number().int().nonnegative().optional() }).strict();
/** The photos of one lot, in the order they should run. The first is the cover. A photo left out keeps its place after the ones listed. */
export const MediaOrderBody = z.object({ mediaIds: z.array(Uuid).min(1).max(100) }).strict();
export const VideoBody = z.object({ url: z.string().url() }).strict();
export const ImportBody = z.object({
  csv: z.string().min(1),
  filename: z.string().optional(),
  options: z.object({
    defaultKind: z.string().optional(),
    mapping: z.record(z.unknown()).optional(),
    imageFilenames: z.array(z.string()).optional(),
  }).strict().optional(),
  /** The commit only: the `basis` of the plan the person read. */
  basis: z.string().min(1).max(64).optional(),
}).strict();

export const ApprovalBody = z.object({
  previewHash: z.string().min(16),
  route: z.string().min(1),
  wouldChange: z.unknown(),
  agentSession: z.string().optional(),
  skill: z.string().optional(),
  ttlSeconds: z.number().int().positive().max(3600).optional(),
}).strict();
export const AiApproval = z.object({
  id: Uuid, tenantId: Uuid, previewHash: z.string(), route: z.string(), wouldChange: z.unknown(),
  approvedBy: z.string(), agentSession: z.string().nullable(), skill: z.string().nullable(),
  approvedAt: z.string(), expiresAt: z.string(), usedAt: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Annie (ADR-39) - the office's view of the AI sale secretary. Threads,
// timelines, intake cards, the needs-a-human queue, the consignor invitation
// (a HUMAN act: sends a text), and the one gated bridge from an intake to a
// catalog lot (preview→confirm).
// ---------------------------------------------------------------------------

export const AnnieThreadSummary = z.object({
  id: Uuid, phoneE164: z.string(), actorType: z.string(), actorName: z.string().nullable(), state: z.string(),
  activeIntent: z.string().nullable(), lastChannel: z.string().nullable(), currentIntakeId: Uuid.nullable(), verificationLevel: z.number().int(),
  lastActivityAt: z.string(), lastMessage: z.string().nullable(),
});
export type AnnieThreadSummary = z.infer<typeof AnnieThreadSummary>;
const Row = z.record(z.unknown());
export const AnnieThreadDetail = z.object({ thread: Row, messages: z.array(Row), media: z.array(Row), jobs: z.array(Row), intakes: z.array(Row) });
/** The fee block on an Annie intake card: derived from durable invoice rows
 *  every time (engineering rule 11), never from the conversation. */
export const ConsignmentFeeFact = z.union([
  z.object({ status: z.literal('NOT_REQUIRED') }),
  z.object({ status: z.enum(['DRAFT', 'DUE', 'PAID', 'VOID']), invoiceId: Uuid, invoiceNumber: z.string(), amountCents: Cents, amountDueCents: Cents, currency: z.string(), payUrl: z.string().nullable(), paidAt: z.string().nullable() }),
]);

export const AnnieIntakeCard = z.object({
  intake: Row, consignor: Row.nullable(), facts: z.array(Row), media: z.array(Row), contradictions: z.array(Row),
  missing: Row, completeness: Row, proposal: z.object({ title: z.string(), attributes: z.record(z.unknown()), issues: z.array(z.string()), sources: z.record(z.string()) }),
  // ADR-40. These MUST be declared: the typed client parses every response
  // against this schema, and Zod strips what the contract does not describe -
  // so a field the route returns but the contract omits silently never reaches
  // the console. (It did, for the whole two-stage review panel.)
  reviewStatus: z.string().nullable().optional(),
  reviewEvents: z.array(Row).optional(),
  /** ADR-51: the consignment fee for this intake, derived from invoice rows. */
  consignmentFee: ConsignmentFeeFact.optional(),
  /** D5 follow-up: the sale this intake belongs to, as far as the screen needs it - the
   *  product, so the number box asks for a Hip or a Lot number in the sale's own word. */
  sale: z.object({ product: z.string() }).optional(),
});
export const AnnieInviteBody = z.object({
  saleId: Uuid, name: z.string().min(1).max(160), phone: z.string().min(7), horses: z.number().int().min(1).max(50),
  /** Staff attest the consignor agreed to be texted. Without it, no text is sent. */
  smsConsent: z.boolean(), horseNames: z.array(z.string().min(1)).optional(),
}).strict();
export const AnnieInviteResult = z.object({ consignorId: Uuid, reusedConsignor: z.boolean(), intakeIds: z.array(Uuid), threadId: Uuid, invited: z.boolean(), body: z.string().optional() });
/** hipNumber is optional: omit it to auto-assign the sale's next number (what the
 *  office ACCEPT path does), or pass one to pin it. Editable later via patchLot. */
export const AnnieMaterializeBody = z.object({ hipNumber: z.string().min(1).optional(), confirm: z.boolean().optional() }).strict();
export const AnnieReviewBody = z.object({
  decision: z.enum(['accept', 'request_changes', 'decline']),
  /** Customer-facing reason Annie relays to the consignor by SMS. Required for request_changes and decline. */
  reason: z.string().min(1).max(1000).optional(),
}).strict();
export const AnnieDescription = z.object({
  summary: z.string(), description: z.string(), highlights: z.array(z.string()),
  sourceMap: z.array(z.object({ sentence: z.string(), factKeys: z.array(z.string()) })), unsupported: z.array(z.string()),
  model: z.string(), promptVersion: z.string(), generatedAt: z.string(), inputFactCount: z.number().int(),
});

// ---------------------------------------------------------------------------
// Invoicing (ADR-51). "Your auction. Your customers. Your money."
//
// The tenant's payment CONNECTION is read here and mutated off-contract
// (onboard/refresh are human acts with a browser redirect, the check-policy
// posture). Invoices and the fee catalog are on-contract with preview->confirm
// on every mutation, so an AI agent may propose an invoice and a human
// commits it (ADR-29). Recording an offline payment against an invoice is
// OFF-contract, like sale-day checkout: settling money is never an agent
// power.
// ---------------------------------------------------------------------------

export const PaymentProviderId = z.enum(['STRIPE_CONNECT']);
export const PaymentConnectionStatus = z.enum(['NOT_CONNECTED', 'ONBOARDING', 'ACTION_REQUIRED', 'CONNECTED', 'RESTRICTED', 'DISCONNECTED']);
/** Tenant-safe view: status words, capability flags, and where to manage the
 *  account. Never a provider payload, never a secret. */
export const PaymentConnectionView = z.object({
  provider: PaymentProviderId,
  status: PaymentConnectionStatus,
  chargesEnabled: z.boolean(),
  payoutsEnabled: z.boolean(),
  detailsSubmitted: z.boolean(),
  /** Names of things the provider still needs (e.g. "external_account"). */
  requirementsDue: z.array(z.string()),
  connectedAt: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  /** Can this tenant take an online payment right now (fail closed)? */
  usable: z.boolean(),
  manageUrl: z.string().nullable(),
  /** Is a provider wired on this server at all? False = the Connect button cannot work. */
  providerConfigured: z.boolean(),
  webhooksConfigured: z.boolean(),
});

export const InvoicePurposeCode = z.enum(['CONSIGNMENT_FEE', 'ENTRY_FEE', 'NO_SALE_FEE', 'STALL', 'BEDDING', 'HAY', 'VETERINARY', 'ADVERTISING', 'CATALOG_UPGRADE', 'VIP', 'DEPOSIT', 'HAULING', 'MISCELLANEOUS', 'CUSTOM']);
export const InvoiceStatus = z.enum(['DRAFT', 'OPEN', 'SENT', 'VIEWED', 'PARTIALLY_PAID', 'PAID', 'PAST_DUE', 'VOID']);
export const InvoiceAssociationKind = z.enum(['NONE', 'SALE', 'LOT', 'ANNIE_INTAKE', 'REGISTRATION']);
export const InvoicePaymentState = z.enum(['INITIATED', 'PROCESSING', 'CAPTURED', 'CLEARED', 'FAILED', 'EXPIRED', 'REFUNDED', 'VOID']);
export const InvoicePaymentMethod = z.enum(['CARD', 'BANK', 'CASH', 'CHECK', 'WIRE', 'OTHER']);

export const InvoiceFeeDefinition = z.object({
  id: Uuid,
  saleId: Uuid.nullable(),
  purposeCode: InvoicePurposeCode,
  label: z.string(),
  description: z.string().nullable(),
  amountCents: Cents,
  active: z.boolean(),
  autoDraftOnIntakeAccept: z.boolean(),
  position: z.number().int(),
  createdAt: z.string(),
});
export const CreateInvoiceFeeBody = z.object({
  saleId: Uuid.nullable().optional(),
  purposeCode: InvoicePurposeCode,
  label: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  amountCents: Cents,
  active: z.boolean().optional(),
  autoDraftOnIntakeAccept: z.boolean().optional(),
  position: z.number().int().optional(),
  confirm: z.boolean().optional(),
}).strict();
export const UpdateInvoiceFeeBody = z.object({
  label: z.string().min(1).max(120).optional(),
  description: z.string().max(500).nullable().optional(),
  amountCents: Cents.optional(),
  active: z.boolean().optional(),
  autoDraftOnIntakeAccept: z.boolean().optional(),
  position: z.number().int().optional(),
  confirm: z.boolean().optional(),
}).strict();

export const InvoiceRecipientInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('CONSIGNOR'), consignorId: Uuid }).strict(),
  z.object({ kind: z.literal('ACCOUNT'), accountId: Uuid }).strict(),
  z.object({ kind: z.literal('CONTACT'), name: z.string().min(1).max(200), phone: z.string().max(40).optional(), email: z.string().email().optional() }).strict(),
]);
export const InvoiceAssociationInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('NONE') }).strict(),
  z.object({ kind: z.literal('SALE'), saleId: Uuid }).strict(),
  z.object({ kind: z.literal('LOT'), saleId: Uuid, lotId: Uuid }).strict(),
  z.object({ kind: z.literal('ANNIE_INTAKE'), saleId: Uuid, intakeId: Uuid }).strict(),
  z.object({ kind: z.literal('REGISTRATION'), saleId: Uuid, registrationId: Uuid }).strict(),
]);
export const InvoiceLineInput = z.object({
  description: z.string().min(1).max(200),
  quantity: z.number().int().positive().default(1),
  unitAmountCents: Cents,
  purposeCode: InvoicePurposeCode,
  feeDefinitionId: Uuid.optional(),
}).strict();
/** Either explicit lines, or a fee definition the server expands into one
 *  line at the CONFIGURED amount (the only way automation may price a line). */
export const CreateInvoiceBody = z.object({
  recipient: InvoiceRecipientInput,
  association: InvoiceAssociationInput.optional(),
  purposeCode: InvoicePurposeCode.optional(),
  feeDefinitionId: Uuid.optional(),
  lines: z.array(InvoiceLineInput).min(1).max(50).optional(),
  taxCents: Cents.optional(),
  /** null / omitted = due on receipt. */
  dueAt: IsoDate.nullable().optional(),
  memo: z.string().max(1000).nullable().optional(),
  confirm: z.boolean().optional(),
}).strict();
export const UpdateInvoiceBody = z.object({
  recipient: InvoiceRecipientInput.optional(),
  association: InvoiceAssociationInput.optional(),
  purposeCode: InvoicePurposeCode.optional(),
  lines: z.array(InvoiceLineInput).min(1).max(50).optional(),
  taxCents: Cents.optional(),
  dueAt: IsoDate.nullable().optional(),
  memo: z.string().max(1000).nullable().optional(),
  confirm: z.boolean().optional(),
}).strict();
export const SendInvoiceBody = z.object({
  channels: z.object({ sms: z.boolean(), email: z.boolean() }).strict(),
  /** Staff attest the recipient agreed to be texted (needed unless the
   *  recipient is an account with consent on file). Without it, no text. */
  smsConsent: z.boolean().optional(),
  confirm: z.boolean().optional(),
}).strict();
export const VoidInvoiceBody = z.object({ reason: z.string().min(1).max(500), confirm: z.boolean().optional() }).strict();

export const InvoiceLine = z.object({
  id: Uuid, position: z.number().int(), description: z.string(), quantity: z.number().int(), unitAmountCents: Cents, lineTotalCents: Cents, purposeCode: InvoicePurposeCode, feeDefinitionId: Uuid.nullable(),
});
export const InvoiceSummary = z.object({
  id: Uuid,
  invoiceNumber: z.string(),
  status: InvoiceStatus,
  recipientName: z.string(),
  /** "Consignment fee - Hip 114 - Charming" */
  forLabel: z.string(),
  purposeCode: InvoicePurposeCode,
  associationKind: InvoiceAssociationKind,
  associationLabel: z.string().nullable(),
  saleId: Uuid.nullable(),
  saleTitle: z.string().nullable(),
  currency: z.string(),
  totalCents: Cents,
  amountPaidCents: Cents,
  amountDueCents: Cents,
  dueAt: z.string().nullable(),
  /** Derived at read: unpaid and past due_at (the row may not be PAST_DUE yet). */
  overdue: z.boolean(),
  createdAt: z.string(),
  sentAt: z.string().nullable(),
  viewedAt: z.string().nullable(),
  paidAt: z.string().nullable(),
});
export const InvoiceEvent = z.object({ id: Uuid, type: z.string(), actorType: z.string(), actorId: z.string().nullable(), detail: z.record(z.unknown()), createdAt: z.string() });
export const InvoiceDelivery = z.object({ id: Uuid, channel: z.enum(['SMS', 'EMAIL']), destination: z.string(), status: z.string(), skipReason: z.string().nullable(), lastError: z.string().nullable(), requestedAt: z.string(), updatedAt: z.string() });
export const InvoicePayment = z.object({ id: Uuid, source: z.enum(['PROVIDER', 'MANUAL']), method: InvoicePaymentMethod, amountCents: Cents, state: InvoicePaymentState, provider: z.string().nullable(), reference: z.string().nullable(), failureReason: z.string().nullable(), createdAt: z.string(), stateChangedAt: z.string().nullable() });
export const InvoiceDetail = InvoiceSummary.extend({
  subtotalCents: Cents,
  taxCents: Cents,
  memo: z.string().nullable(),
  recipient: z.object({ kind: z.enum(['CONSIGNOR', 'ACCOUNT', 'CONTACT']), consignorId: Uuid.nullable(), accountId: Uuid.nullable(), name: z.string(), phone: z.string().nullable(), email: z.string().nullable() }),
  association: z.object({ kind: InvoiceAssociationKind, saleId: Uuid.nullable(), lotId: Uuid.nullable(), intakeId: Uuid.nullable(), registrationId: Uuid.nullable(), label: z.string().nullable() }),
  feeDefinitionId: Uuid.nullable(),
  lines: z.array(InvoiceLine),
  events: z.array(InvoiceEvent),
  deliveries: z.array(InvoiceDelivery),
  payments: z.array(InvoicePayment),
  /** The hosted invoice URL (the capability the customer receives). Present
   *  once issued; staff may copy it. */
  hostedUrl: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  createdBy: z.string(),
  createdVia: z.string(),
});
/** What "Send" will do, for the confirm dialog: exactly who gets what. */
export const SendInvoicePreview = z.object({
  invoiceId: Uuid,
  invoiceNumber: z.string(),
  amountDueCents: Cents,
  sms: z.object({ to: z.string().nullable(), body: z.string().nullable(), willSend: z.boolean(), reason: z.string().nullable() }),
  email: z.object({ to: z.string().nullable(), willSend: z.boolean(), reason: z.string().nullable() }),
  hostedUrl: z.string(),
  paymentConnectionUsable: z.boolean(),
  warnings: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// Sale readiness (ADR-52). Derived on every read from the owning domains,
// never stored; blocked mirrors exactly what publish / go-live would refuse.
// ---------------------------------------------------------------------------

export const ReadinessStepId = z.enum(['basics', 'format', 'money', 'fees', 'payments', 'bidders', 'checks', 'consignments', 'terms', 'registration', 'catalog', 'staff', 'rings', 'broadcast', 'rehearsal']);
export const ReadinessState = z.enum(['ready', 'attention', 'blocked', 'unknown', 'not_applicable']);
export const ReadinessStep = z.object({
  id: ReadinessStepId,
  title: z.string(),
  state: ReadinessState,
  headline: z.string(),
  detail: z.string(),
  gate: z.enum(['publish', 'go_live']).nullable(),
  fix: z.object({ step: ReadinessStepId, action: z.object({ route: z.string(), args: z.record(z.unknown()) }).optional() }).nullable(),
  codes: z.array(z.string()),
});
export const SaleReadiness = z.object({
  saleId: Uuid,
  status: SaleStatus,
  generatedAt: z.string(),
  steps: z.array(ReadinessStep),
  summary: z.object({ ready: z.number().int(), attention: z.number().int(), blocked: z.number().int(), unknown: z.number().int(), notApplicable: z.number().int() }),
  blockers: z.object({ publish: z.array(z.string()), goLive: z.array(z.string()) }),
  canPublish: z.boolean(),
  canGoLive: z.boolean(),
  next: z.object({ step: ReadinessStepId.nullable(), headline: z.string() }),
  /** Where the broadcast graphics render for this sale, once public. */
  gfxUrl: z.string().nullable(),
  /** The item kinds Annie accepts at this sale after inheritance (ADR-46). */
  effectiveItemTypes: z.array(z.string()),
});
export type SaleReadiness = z.infer<typeof SaleReadiness>;

// ---------------------------------------------------------------------------
// Sale health (ADR-70 cut 6g, hitlist 19). The readiness checklist above and
// the integrity rules, grown into one report a sale office and the ring
// console both read: every check the platform can make, sorted into
// errors / warnings / advisories / passed, plus the checks it cannot make
// and why. Derived every call, never stored. A finding's fingerprint is
// stable while its cause is (the check, the rule, the lots it names), so a
// screen can tell a new finding from one it has already shown.
// ---------------------------------------------------------------------------

export const HealthSeverity = z.enum(['error', 'warning', 'advisory']);
export type HealthSeverity = z.infer<typeof HealthSeverity>;
/** Where the office fixes a finding: a setup step, the catalog, the
 *  registrations or sellers list, or the sale's live video page. */
export const HealthFix = z.object({
  where: z.enum(['setup', 'catalog', 'registrations', 'consignors', 'live_video']),
  step: ReadinessStepId.optional(),
  action: z.object({ route: z.string(), args: z.record(z.unknown()) }).optional(),
});
export const HealthFinding = z.object({
  /** The check that raised it (sale_times, lot_media, setup.terms, ...). */
  check: z.string(),
  /** The rule, greppable (LOT_NO_MEDIA, SALE_NO_TERMS, ...). */
  code: z.string(),
  severity: HealthSeverity,
  message: z.string(),
  remediation: z.string(),
  affectedListingIds: z.array(Uuid),
  /** The same lots by number, in the sale's order. */
  affectedHips: z.array(z.string()),
  affectedRegistrationIds: z.array(Uuid),
  /** One line per affected lot or registration, where the rule has one. */
  details: z.array(z.string()),
  fingerprint: z.string(),
  fix: HealthFix.nullable(),
});
export type HealthFinding = z.infer<typeof HealthFinding>;
export const HealthCheckNote = z.object({ check: z.string(), title: z.string(), message: z.string() });
export const SaleHealth = z.object({
  saleId: Uuid,
  status: SaleStatus,
  generatedAt: z.string(),
  /** The worst thing in the report: errors, warnings, advisories, or clear. */
  verdict: z.enum(['errors', 'warnings', 'advisories', 'clear']),
  summary: z.object({ errors: z.number().int(), warnings: z.number().int(), advisories: z.number().int(), passed: z.number().int(), notChecked: z.number().int() }),
  errors: z.array(HealthFinding),
  warnings: z.array(HealthFinding),
  advisories: z.array(HealthFinding),
  passed: z.array(HealthCheckNote),
  /** Checks that do not apply to this sale, or that HipNumber has no data
   *  for - said, never counted as passed. */
  notChecked: z.array(HealthCheckNote),
});
export type SaleHealth = z.infer<typeof SaleHealth>;

// ---------------------------------------------------------------------------
// The route table
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// ADR-65 cut 2: the office door and the reversal ledger, read
// ---------------------------------------------------------------------------

/** The reversal kinds the office may record at its door. BUYER_CORRECTION is
 *  written by the reattribute route; the ring's own kinds arrive through the
 *  projector. */
export const OfficeReversalKind = z.enum(['VOID', 'TURN_BACK', 'BUYER_DEFAULT']);
export const ReversalKind = z.enum(['VOID', 'TURN_BACK', 'BUYER_DEFAULT', 'BUYER_CORRECTION']);
export const RefundStatus = z.enum(['NONE', 'DUE', 'RECORDED', 'WAIVED']);
export const ClawbackStatus = z.enum(['NONE', 'DUE', 'OFFSET', 'RECORDED', 'WAIVED']);
export const RefundMethod = z.enum(['CASH', 'CARD', 'CHECK', 'WIRE', 'CREDIT', 'OTHER']);

/** One recorded refund tender: the INSTRUMENT that settled a reversal's
 *  refund obligation (a separate record from the obligation itself). */
export const RefundView = z.object({
  id: Uuid,
  method: RefundMethod,
  amountCents: Cents,
  reference: z.string().nullable(),
  recordedBy: z.string(),
  recordedAt: IsoDate,
  /** CREDIT only: the buyer statement checkout consumed it against. */
  appliedStatementId: Uuid.nullable(),
});

/**
 * One reversal as the office reads it: the immutable fact (what, why, who,
 * when, against which purchase), the OBLIGATIONS it computed (refund due,
 * clawback due, frozen forever) and where each stands, and the instrument
 * that settled the refund, if any.
 */
export const ReversalView = z.object({
  id: Uuid,
  purchaseId: Uuid,
  lotId: Uuid,
  hip: z.string(),
  title: z.string(),
  consignorId: Uuid.nullable(),
  registrationId: Uuid.nullable(),
  paddle: z.string().nullable(),
  purchaseStatus: z.string(),
  lineTotalCents: Cents,
  soldSeq: z.number().int(),
  kind: ReversalKind,
  origin: z.enum(['RING', 'OFFICE']),
  ringSeq: z.number().int().nullable(),
  /** An OFFICE reversal the ring later described too (its SALE_VOIDED seq):
   *  one reversal, two descriptions, never a second row or obligation. */
  reconciledRingSeq: z.number().int().nullable(),
  reason: z.string(),
  actor: z.string(),
  recordedAt: IsoDate,
  previousStatementId: Uuid.nullable(),
  refundDueCents: z.number().int().nullable(),
  refundStatus: RefundStatus.nullable(),
  refund: RefundView.nullable(),
  previousPayoutStatementId: Uuid.nullable(),
  clawbackDueCents: z.number().int().nullable(),
  clawbackStatus: ClawbackStatus.nullable(),
  clawbackOffsetStatementId: Uuid.nullable(),
  deficiencyCents: z.number().int().nullable(),
  resalePurchaseId: Uuid.nullable(),
  /** The policy frozen at the reversal (decision 2), the public label with it. */
  policy: z.object({ refundPremium: z.boolean().nullable(), publicTurnedBackLabel: z.string().nullable(), source: z.string().nullable() }).nullable(),
});
export type ReversalView = z.infer<typeof ReversalView>;

export const ReversePurchaseBody = z.object({
  kind: OfficeReversalKind,
  reason: z.string().trim().min(1).max(500),
  /** admin only: replace the computed refund with this amount (audited; both numbers are kept). */
  refundCents: Cents.optional(),
  confirm: z.boolean().optional(),
}).strict();

export const ReversePurchasePreview = z.object({
  purchaseId: Uuid,
  lotId: Uuid,
  hip: z.string(),
  title: z.string(),
  kind: OfficeReversalKind,
  /** The reason and the override are part of the approved action (ADR-29). */
  reason: z.string(),
  refundOverrideCents: Cents.nullable(),
  from: z.string(),
  to: z.literal('VOID'),
  lineTotalCents: Cents,
  registrationId: Uuid.nullable(),
  refundDueCents: z.number().int().nullable(),
  refundStatus: RefundStatus,
  clawbackDueCents: z.number().int().nullable(),
  clawbackStatus: ClawbackStatus,
  policy: z.object({ refundPremium: z.boolean(), publicTurnedBackLabel: z.string(), source: z.enum(['SALE', 'TENANT', 'DEFAULT']) }),
  lotStatusTo: z.literal('turned_back'),
});

export const ReattributePurchaseBody = z.object({
  toRegistrationId: Uuid.optional(),
  toPaddle: z.number().int().positive().optional(),
  reason: z.string().trim().min(1).max(500),
  confirm: z.boolean().optional(),
}).strict();

export const ReattributePurchasePreview = z.object({
  purchaseId: Uuid,
  lotId: Uuid,
  hip: z.string(),
  from: z.object({ registrationId: Uuid.nullable(), paddle: z.string().nullable() }),
  to: z.object({ registrationId: Uuid, paddle: z.string().nullable() }),
  status: z.string(),
  /** Part of the approved action (ADR-29). */
  reason: z.string(),
});

export interface RouteDef<Req extends z.ZodTypeAny, Res extends z.ZodTypeAny> {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Path relative to /t/:tenantSlug. `:name` segments are params. */
  readonly path: string;
  readonly request: Req;
  readonly response: Res;
  /** Who may call. 'public' needs no identity; 'bidder' needs an account. */
  readonly access: 'public' | 'bidder' | readonly z.infer<typeof StaffRole>[];
  /** Mutation supports { confirm } preview→confirm. */
  readonly confirmable?: boolean;
  /** For the MCP tool description. */
  readonly summary: string;
}

const r = <Req extends z.ZodTypeAny, Res extends z.ZodTypeAny>(d: RouteDef<Req, Res>) => d;
const NoBody = z.undefined();
/** The body of a non-GET route that takes nothing: the typed client sends {} (NoBody is for GETs only). */
const NoPayload = z.object({}).strict();
const STAFF_ALL = ['admin', 'clerk', 'office', 'cataloger'] as const;
const OFFICE = ['admin', 'office'] as const;
const FLOOR_STAFF = ['admin', 'office', 'clerk'] as const; // front desk + ring: walk-up registration (11.4)
const CATALOG = ['admin', 'office', 'cataloger'] as const;
/** C1b: appended to every route that changes a lot's photos (see lot-photos.ts for the rule). */
const PHOTO_LOCK_NOTE = ' Once bidding has opened on the sale, or the lot has sold, drawn no sale, passed or been turned back, a cataloger is refused with 409 (code LOT_PHOTOS_LOCKED, the plain-words reason in error); admin and office may still do it, and each such change is recorded in the audit trail.';
// A clerk edits lots AT THE RING during a sale (on-the-fly lot, fix a typo on
// the current lot) - plan section 11.3. Not media/import (those stay CATALOG).
const LOT_EDIT = ['admin', 'office', 'cataloger', 'clerk'] as const;

// ---------------------------------------------------------------------------
// Marketing Studio DTOs (ADR-56). Wire shapes; the domain lives in
// @hipnumber/marketing-contract and is imported, never restated.
// ---------------------------------------------------------------------------

/** The tenant's Create Campaign selections (the four-step form). `visualStrategy`
 *  replaces the old creativeMode (ADR-56 cut 7): BRAND_TREATMENT / REAL_MEDIA are
 *  included; AI_CREATIVE spends credits and is refused when the objective requires
 *  a real subject. `reuseScope` applies to an AI creative once approved. */
export const MarketingCreativeDirection = z
  .object({
    visualStrategy: VisualStrategySchema,
    channels: z.array(MarketingChannelSchema).min(1),
    style: VisualStyleSchema,
    notes: z.string().max(2000).optional(),
    featuredLots: FeaturedLotSelectionSchema.optional(),
    reuseScope: VisualReuseScopeSchema.optional(),
    /** Sale Results only (ADR-56 hardening D): what the campaign discloses.
     *  Default SOLD_ONLY, the public catalog's own level; buyers never. */
    resultsDisclosure: ResultsDisclosureSchema.optional(),
  })
  .strict();
export type MarketingCreativeDirection = z.infer<typeof MarketingCreativeDirection>;

export const CreateCampaignBody = z
  .object({
    name: z.string().min(1).max(120),
    objective: CampaignObjectiveSchema,
    direction: MarketingCreativeDirection,
    confirm: z.literal(true).optional(),
  })
  .strict();
export const GenerateCampaignBody = z.object({ confirm: z.literal(true).optional() }).strict();
export const ApproveAssetBody = z.object({ confirm: z.literal(true).optional() }).strict();
export const ExportAssetBody = z.object({ confirm: z.literal(true).optional() }).strict();
export const RegenerateAssetBody = z
  .object({ scope: RegenerationScopeSchema, confirm: z.literal(true).optional() })
  .strict();

export const MarketingCampaignView = z
  .object({
    id: Uuid,
    saleId: Uuid,
    name: z.string(),
    objective: CampaignObjectiveSchema,
    status: CampaignStatusSchema,
    direction: MarketingCreativeDirection.nullable(),
    generated: z.boolean(),
    createdBy: z.string(),
    createdAt: IsoDate,
    updatedAt: IsoDate,
  })
  .strict();
export type MarketingCampaignView = z.infer<typeof MarketingCampaignView>;

export const MarketingCampaignSummary = z
  .object({
    id: Uuid,
    name: z.string(),
    objective: CampaignObjectiveSchema,
    status: CampaignStatusSchema,
    assetCount: z.number().int().nonnegative(),
    createdAt: IsoDate,
  })
  .strict();

export const MarketingAssetVersionView = z
  .object({
    id: Uuid,
    version: z.number().int().positive(),
    generationStatus: AssetGenerationStatusSchema,
    storageKey: z.string().nullable(),
    /** Public URL of the exported PNG (ADR-56 cut 10), once exported. */
    downloadUrl: z.string().nullable(),
  })
  .strict();

export const MarketingCampaignAssetView = z
  .object({
    id: Uuid,
    campaignId: Uuid,
    name: z.string(),
    creativeSpecId: z.string(),
    format: CreativeFormatSchema,
    mediaType: z.enum(['IMAGE', 'VIDEO']),
    status: CampaignAssetStatusSchema,
    stale: z.boolean(),
    /** The real lot this asset features (ADR-56 cut 9), from the frozen truth;
     *  null for a sale-level creative. */
    subjectLot: z.object({ lotId: z.string(), hipNumber: z.string().nullable(), name: z.string().nullable() }).strict().nullable(),
    activeVersion: MarketingAssetVersionView.nullable(),
    /** The deterministically composed SVG for the active version, recomputed on
     *  read from the frozen snapshots (ADR-56 cut 3/4). Null before generation
     *  or when the layout could not be composed. The admin renders it inline. */
    previewSvg: z.string().nullable(),
    /** Why the composition is not final (a locked fact missing, text that
     *  cannot fit), recomputed on read. Empty when the creative composed. */
    previewErrors: z.array(z.string()),
  })
  .strict();
export type MarketingCampaignAssetView = z.infer<typeof MarketingCampaignAssetView>;

// --- Tenant Connections (ADR-57) ------------------------------------------

/** A place a connection can publish to. Never carries a credential. */
export const PublishingDestinationView = z
  .object({
    id: Uuid,
    connectionId: Uuid,
    type: DestinationTypeSchema,
    externalId: z.string(),
    displayName: z.string(),
    enabled: z.boolean(),
  })
  .strict();
export type PublishingDestinationView = z.infer<typeof PublishingDestinationView>;

/** An authorised external account and its health. Never carries a credential. */
export const TenantConnectionView = z
  .object({
    id: Uuid,
    provider: ConnectionProviderSchema,
    externalAccountId: z.string(),
    displayName: z.string(),
    status: ConnectionStatusSchema,
    statusDetail: z.string().nullable(),
    grantedScopes: z.array(z.string()),
    capabilities: z.array(z.string()),
    credentialsExpiresAt: z.string().nullable(),
    lastCheckedAt: z.string().nullable(),
    connectedBy: z.string(),
    connectedAt: z.string(),
    destinations: z.array(PublishingDestinationView),
  })
  .strict();
export type TenantConnectionView = z.infer<typeof TenantConnectionView>;

export const TenantConnectionsView = z
  .object({
    /** Which providers HipNumber has platform credentials for. A provider that
     *  is not configured shows "Not available yet", never a dead button. */
    providersConfigured: z.object({ META: z.boolean(), GOOGLE: z.boolean() }).strict(),
    connections: z.array(TenantConnectionView),
  })
  .strict();
export type TenantConnectionsView = z.infer<typeof TenantConnectionsView>;

export const DisconnectConnectionBody = z.object({ confirm: z.literal(true).optional() }).strict();
export const RefreshConnectionBody = z.object({ confirm: z.literal(true).optional() }).strict();
export const SetDestinationBody = z.object({ enabled: z.boolean(), confirm: z.literal(true).optional() }).strict();

export const MarketingManifestSummary = z
  .object({
    formats: z.array(CreativeFormatSchema),
    creativeSpecCount: z.number().int().nonnegative(),
    copySpecCount: z.number().int().nonnegative(),
    auctionName: z.string(),
    publicUrl: z.string(),
  })
  .strict();

export const MarketingCampaignDetail = z
  .object({
    campaign: MarketingCampaignView,
    assets: z.array(MarketingCampaignAssetView),
    manifestSummary: MarketingManifestSummary.nullable(),
  })
  .strict();
export type MarketingCampaignDetail = z.infer<typeof MarketingCampaignDetail>;

/** Platform-aware copy for one channel, composed from the campaign's frozen
 *  auction truth (ADR-56 cut 6). */
export const MarketingCampaignCopy = z.object({ surface: CopySurfaceSchema, copy: GeneratedCopySchema, source: z.enum(['DETERMINISTIC', 'LLM']) }).strict();
export const MarketingScheduleSlot = z.object({ channel: MarketingChannelSchema, atUtc: IsoDate }).strict();
export const MarketingScheduleView = z
  .object({ displayTimezone: z.string(), slots: z.array(MarketingScheduleSlot) })
  .strict();
export type MarketingScheduleView = z.infer<typeof MarketingScheduleView>;

/** A live, unsaved preview of the primary format for the Create Campaign panel. */
export const PreviewCampaignBody = z
  .object({ objective: CampaignObjectiveSchema, direction: MarketingCreativeDirection })
  .strict();
export const MarketingCreativePreview = z
  .object({ format: CreativeFormatSchema.nullable(), svg: z.string().nullable() })
  .strict();
export type MarketingCreativePreview = z.infer<typeof MarketingCreativePreview>;

export const routes = {
  // public
  site: r({ method: 'GET', path: '/site', request: NoBody, access: 'public', summary: 'Public site manifest: tenant name, theme, published sales.',
    response: z.object({
      slug: z.string(), name: z.string(), theme: z.record(z.unknown()), sales: z.array(PublicSale),
      /** The public consignor web form (ADR-50), when online consignment is on for this company. */
      consign: z.object({ url: z.string() }).nullable(),
    }) }),
  publicCatalog: r({ method: 'GET', path: '/catalog/:saleSlug', request: NoBody, access: 'public', summary: 'Public catalog for a sale (no reserves, no consignors, no drafts).',
    response: z.object({ sale: PublicSale, lots: z.array(PublicLot) }) }),

  // tenant settings (migration 0041)
  tenantSettings: r({ method: 'GET', path: '/settings', request: NoBody, response: TenantSettingsView, access: OFFICE, summary: 'Tenant settings: organization identity and company-wide sale defaults (null = never configured).' }),
  updateTenantSettings: r({ method: 'PUT', path: '/settings', request: UpdateTenantSettingsBody, response: z.union([TenantSettingsView, Preview(z.record(z.unknown()))]), access: ['admin'], confirmable: true, summary: 'Replace the tenant settings wholesale (and optionally rename the tenant). Omit confirm for a preview (ADR-29).' }),

  // sales
  listSales: r({ method: 'GET', path: '/sales', request: NoBody, response: z.array(SaleListItem), access: STAFF_ALL, summary: 'List the tenant\'s sales with lot/registration/consignor counts.' }),
  createSale: r({ method: 'POST', path: '/sales', request: CreateSaleBody, response: z.union([Sale, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Create a sale (draft). paddleRanges/onlineBidAccept prefill from tenant settings when omitted; money fields start NULL = inherit.' }),
  updateSale: r({ method: 'PATCH', path: '/sales/:saleId', request: UpdateSaleBody, response: z.union([SaleWithConfig, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Update sale settings. Money fields: null = inherit the company default, an object overrides it wholesale. Once live/settled only descriptionMd and location may change (409 otherwise).' }),
  saleSettings: r({ method: 'GET', path: '/sales/:saleId/settings', request: NoBody, response: SaleSettingsView, access: OFFICE, summary: 'One sale\'s editable settings, the tenant defaults that would apply, and the resolved effective values.' }),
  saleReadiness: r({ method: 'GET', path: '/sales/:saleId/readiness', request: NoBody, response: SaleReadiness, access: STAFF_ALL, summary: 'The setup checklist for one sale (ADR-52): one step per thing the office must get right, each ready / attention / blocked / unknown, which gate it blocks, and what fixes it. Derived every call; canPublish / canGoLive mirror exactly what the lifecycle routes would refuse.' }),
  saleBrief: r({ method: 'GET', path: '/sales/:saleId/brief', request: NoBody, response: SaleBrief, access: STAFF_ALL, summary: 'Sale overview: counts by kind/status, registrations by status, terms, ring provisioning.' }),
  saleTimedPlan: r({ method: 'GET', path: '/sales/:saleId/timed-plan', request: NoBody, response: TimedPlanView, access: STAFF_ALL, summary: 'A TIMED sale\'s close plan (ADR-69 Phase 2): the effective schedule, each lot\'s planned close, the ring lifetime, and the live phase per lot once the lots have opened. Derived every call.' }),
  reopenTimedLots: r({ method: 'POST', path: '/sales/:saleId/timed-recovery', request: TimedRecoveryBody, response: z.union([TimedRecoveryAccepted, Preview(TimedRecoveryPreview)]), access: OFFICE, confirmable: true, summary: 'Reopen timed lots that closed with no sale (ADR-70 cut 6i, hitlist 17.4) on fresh closes: the two-minute notice plus a grace of 0 to 120 minutes, then the sale\'s own close pattern. The board comes back as it left. Per lot, the preview says what would reopen and what would not (sold, still open, awaiting staff, changed since). One request at a time; the worker delivers it to the ring and records its answer on the timed plan.' }),
  publishSale: r({ method: 'POST', path: '/sales/:saleId/publish', request: TransitionBody, response: z.union([Sale, Preview(TransitionPreview.extend({ title: z.string(), termsVersion: z.number().int() }))]), access: OFFICE, confirmable: true, summary: 'Publish a sale. Requires published terms and ordered paddle ranges (409 otherwise).' }),
  rehearse: r({ method: 'POST', path: '/sales/:saleId/rehearse', request: RehearseBody, response: z.object({ saleId: Uuid, rehearsedAt: z.string().nullable() }), access: OFFICE, summary: 'Record a completed rehearsal (the go-live gate, plan 10).' }),
  goLive: r({ method: 'POST', path: '/sales/:saleId/go-live', request: GoLiveBody, response: z.union([Sale, Preview(TransitionPreview.extend({ ringEndpoint: z.string() }))]), access: OFFICE, confirmable: true, summary: 'Take a published sale live; records the ring endpoint. Requires a rehearsal.' }),
  closeSale: r({ method: 'POST', path: '/sales/:saleId/close', request: TransitionBody, response: z.union([Sale, Preview(TransitionPreview)]), access: OFFICE, confirmable: true, summary: 'Close a live sale (live to settled).' }),
  deleteSale: r({ method: 'DELETE', path: '/sales/:saleId', request: TransitionBody, response: z.union([SaleDeleted, Preview(SaleDeletePreview)]), access: OFFICE, confirmable: true, summary: 'Delete a sale that is still only a plan: draft or published, with no lots, no bidders and no consignment submissions. Its terms, documents and ring setup go with it. A sale with anything on record stays (409, in words).' }),
  createTerms: r({ method: 'POST', path: '/sales/:saleId/terms', request: TermsBody, response: z.union([SaleTerms, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Publish a new terms version for a sale.' }),
  saleHealth: r({ method: 'GET', path: '/sales/:saleId/health', request: NoBody, response: SaleHealth, access: STAFF_ALL, summary: 'Sale health (ADR-70 cut 6g): the readiness checklist and the integrity rules as one report - errors, warnings, advisories, passed, and what was not checked - each finding with its remediation, the lots it names and a stable fingerprint. Derived every call.' }),
  integrity: r({ method: 'GET', path: '/sales/:saleId/integrity', request: NoBody, response: IntegrityReport, access: STAFF_ALL, summary: 'Integrity findings: missing docs, unresolved lots, paddle/registration problems, reserve issues - each with a proposed fix.' }),
  ledgerAnomalies: r({ method: 'GET', path: '/sales/:saleId/ledger-anomalies', request: NoBody, response: IntegrityReport.extend({ note: z.string().optional() }), access: OFFICE, summary: 'Post-sale ledger sweep: invoices vs settlements vs hammer events, each with a proposed fix. (Empty until the M6 ledger lands.)' }),

  // lots
  listLots: r({ method: 'GET', path: '/sales/:saleId/lots', request: NoBody, response: z.array(Lot), access: STAFF_ALL, summary: 'List a sale\'s lots in run order.' }),
  createLot: r({ method: 'POST', path: '/sales/:saleId/lots', request: CreateLotBody, response: z.union([Lot, Preview(z.record(z.unknown()))]), access: LOT_EDIT, confirmable: true, summary: 'Create a lot; attributes validated against the kind. Omit confirm for a preview (ADR-29).' }),
  // Sellers, for the people who take horses in. There was no way to list or
  // create a consignor outside Annie's office-only invite flow, so a horse
  // received at the gate got a lot with consignorId NULL - a horse nobody can
  // be paid for. LOT_EDIT (which includes clerk and cataloger) because the
  // person receiving a walk-up horse is the person who knows who brought it.
  listConsignors: r({ method: 'GET', path: '/sellers', request: NoBody, response: z.array(Consignor), access: STAFF_ALL, summary: 'Sellers in this tenant, for attributing a lot at check-in.' }),
  setBidderCardRequest: r({ method: 'POST', path: '/sellers/:consignorId/bidder-card-request', request: BidderCardRequestBody, response: Consignor, access: LOT_EDIT, summary: 'Raise or clear a walk-up seller request for a bidder card.' }),
  setConsignorTaxId: r({ method: 'PATCH', path: '/consignors/:consignorId', request: UpdateConsignorBody, response: Consignor, access: OFFICE, summary: 'HUMAN ONLY: set or remove a seller\'s tax id. Write-only - the value is never returned, only whether one is on file. Payouts above the company\'s tax-id limit need one.' }),
  createConsignor: r({ method: 'POST', path: '/sellers', request: CreateConsignorBody, response: z.union([Consignor, Preview(z.record(z.unknown()))]), access: LOT_EDIT, confirmable: true, summary: 'Record a walk-up seller with the minimum to attribute a horse. The office completes tax detail before settlement.' }),
  moveLotsToRing: r({ method: 'POST', path: '/sales/:saleId/lots/ring', request: MoveLotsToRingBody, response: z.union([MoveLotsToRingResult, Preview(z.record(z.unknown()))]), access: LOT_EDIT, confirmable: true, summary: 'Put lots in a ring (ADR-70), or take them out of every ring with ring: null. A sold lot, or one up in a ring right now, is refused by hip.' }),
  setLotsConsignor: r({ method: 'POST', path: '/sales/:saleId/lots/consignor', request: SetLotsConsignorBody, response: z.union([SetLotsConsignorResult, Preview(z.record(z.unknown()))]), access: CATALOG, confirmable: true, summary: 'Set one consignor on many lots (C4). A lot that has sold is left alone and listed with the reason (SOLD_LOT_CONSIGNOR_LOCKED, or CONSIGNOR_LOCKED_BY_PAYOUT with the statement id once its proceeds are on a payout), and so is a lot that already has a seller while the auction is live (SALE_IS_LIVE); each change is recorded in the audit trail.' }),
  patchLot: r({ method: 'PATCH', path: '/lots/:lotId', request: PatchLotBody, response: z.union([Lot, Preview(z.record(z.unknown()))]), access: LOT_EDIT, confirmable: true, summary: 'Update a lot; attributes re-validated against the (possibly new) kind. A consignorId change is refused 422 UNKNOWN_CONSIGNOR for a seller that is not on file, and 409 once the lot has sold (SOLD_LOT_CONSIGNOR_LOCKED, or CONSIGNOR_LOCKED_BY_PAYOUT with the statement id once its proceeds are on a payout) or while the auction is live and the lot already has a seller (SALE_IS_LIVE; giving a seller to a lot that has none is still allowed); a seller sent unchanged is never written; a change that goes through is recorded in the audit trail.' }),
  // ---- ADR-65 cut 2: the office door and the reversal ledger --------------
  // Recording refund and clawback tender is deliberately OFF this table, like
  // checkout and payout: settling money is never an agent power.
  listReversals: r({ method: 'GET', path: '/sales/:saleId/reversals', request: NoBody, response: z.array(ReversalView), access: OFFICE, summary: 'The reversal ledger for a sale (ADR-65): every reversed purchase with the refund and clawback it computed, where each stands, and the refund recorded against it. The typed client narrows to one buyer with query { registrationId }.' }),
  reversePurchase: r({ method: 'POST', path: '/sales/:saleId/purchases/:purchaseId/reverse', request: ReversePurchaseBody, response: z.union([ReversalView, Preview(ReversePurchasePreview)]), access: OFFICE, confirmable: true, summary: 'Reverse a purchase through the office door (ADR-65): the purchase becomes VOID, the lot turned_back, and the refund and clawback due are computed and frozen. Open only once the sale is settled (409 RING_IS_AUTHORITATIVE while live: record it at the ring, or close the sale first); collected money or a refund override needs admin.' }),
  reattributePurchase: r({ method: 'POST', path: '/sales/:saleId/purchases/:purchaseId/reattribute', request: ReattributePurchaseBody, response: z.union([ReversalView, Preview(ReattributePurchasePreview)]), access: OFFICE, confirmable: true, summary: 'Move an OWED purchase to the right buyer (ADR-65, money-neutral): records a BUYER_CORRECTION and re-attributes the row. Open only once the sale is settled; 409 COLLECTED_MONEY once anything was collected; one office correction per purchase.' }),
  lotBrief: r({ method: 'GET', path: '/lots/:lotId/brief', request: NoBody, response: LotBrief, access: STAFF_ALL, summary: 'Everything about one lot: specs, media, documents, release status, consignor, neighbors.' }),
  releaseStatus: r({ method: 'GET', path: '/lots/:lotId/release-status', request: NoBody, access: STAFF_ALL, summary: 'Which release documents are still missing for a lot.',
    response: z.object({ lotId: Uuid, kind: z.string(), docsOnFile: z.array(z.string()), missing: z.array(DocRequirement), releasable: z.boolean() }) }),
  addDocument: r({ method: 'POST', path: '/lots/:lotId/documents', request: DocumentBody, response: LotDocument, access: OFFICE, summary: 'Record a release document on file for a lot.' }),
  listMedia: r({ method: 'GET', path: '/lots/:lotId/media', request: NoBody, response: z.array(LotMedia), access: STAFF_ALL, summary: 'List a lot\'s media.' }),
  attachMedia: r({ method: 'POST', path: '/lots/:lotId/media/attach', request: AttachBody, response: LotMedia, access: CATALOG, summary: 'Attach an uploaded image after the direct PUT. Records the photo and a render job (the variant ladder runs on the media worker, or inline on a laptop); the photo comes back with rendered false until the job is done. Poll listMedia. Attaching the same uploadKey to the same lot again answers with the same photo and adds nothing, so a resent request is safe.' + PHOTO_LOCK_NOTE }),
  orderMedia: r({ method: 'PUT', path: '/lots/:lotId/media/order', request: MediaOrderBody, response: z.array(LotMedia), access: CATALOG, summary: 'Put a lot\'s photos in order (positions rewritten 0..n in one step). The first is the cover. Every id must be a photo of this lot; one left out keeps its place after the ones listed.' + PHOTO_LOCK_NOTE }),
  deleteMedia: r({ method: 'DELETE', path: '/lots/:lotId/media/:mediaId', request: NoPayload, response: z.array(LotMedia), access: CATALOG, summary: 'Remove one media item from a lot and close the gap in the order, so the next photo becomes the cover. Returns what is left.' + PHOTO_LOCK_NOTE }),
  addVideo: r({ method: 'POST', path: '/lots/:lotId/media/video', request: VideoBody, response: LotMedia, access: [...CATALOG, 'consignor'], summary: 'Attach a video URL (normalized).' }),
  setMediaFocal: r({ method: 'POST', path: '/lots/:lotId/media/:mediaId/focal', request: LotMediaFocalBody, response: LotMedia, access: [...CATALOG, 'consignor'], summary: 'Set a photo\'s focal point by hand (normalized x, y). Every marketing format crops around it; campaigns generated later freeze it.' }),
  presign: r({ method: 'POST', path: '/media/presign', request: PresignBody, access: [...CATALOG, 'consignor'], summary: 'Presigned direct-upload URL.',
    response: z.object({ key: z.string(), url: z.string(), headers: z.record(z.string()) }) }),
  brandingAsset: r({ method: 'POST', path: '/branding/assets', request: BrandingAssetBody, response: BrandingAssetJob, access: OFFICE, summary: 'Render an uploaded branding image (ADR-55): a hero photograph or a company logo, presigned and PUT first. Records a render job; in production the media worker renders it (202, poll brandingAssetJob), on a laptop it renders inline (201, done). The public renditions go in tenant or sale branding.' }),
  brandingAssetJob: r({ method: 'GET', path: '/branding/assets/:jobId', request: NoBody, response: BrandingAssetJob, access: OFFICE, summary: 'One branding render job by id: queued or running (keep polling), done (image ready) or failed (error says why).' }),

  // C3: the run order of a sale's lots
  setLotOrder: r({ method: 'PATCH', path: '/sales/:saleId/lots/order', request: SetLotOrderBody, response: z.union([SetLotOrderResult, Preview(z.record(z.unknown()))]), access: CATALOG, confirmable: true, summary: 'Put every lot of a sale in a new run order (lotIds: all of them, once, first to run first; a repeated lot is a 422 DUPLICATE_LOT). Send basis, the lotOrderBasis of the lots as you loaded them: a preview without one is answered with the current basis, a confirm without one is a 422 BASIS_REQUIRED, and a basis that is no longer the order the sale is in (a reorder, a renumber, a lot added or removed since) is a 409 ORDER_STALE. Once a lot has run (sold, no sale, passed) or is up in a ring right now, every lot up to and including the last of those keeps its place (409 ORDER_KEEPS_RUN_LOTS); only the lots after it can be reordered. A live sale reads the ring itself to find the lot that is up, and answers 503 RING_LOG_UNREADABLE when it cannot. A completed sale is a 409 SALE_CLOSED, a timed auction whose bidding has opened is a 409 ORDER_TIMED_OPEN (its closes are already set), and a change that met another change to the same lots is a 409 LOTS_BUSY with nothing written.' }),
  renumberLots: r({ method: 'POST', path: '/sales/:saleId/lots/renumber', request: RenumberLotsBody, response: z.union([RenumberLotsResult, Preview(z.record(z.unknown()))]), access: CATALOG, confirmable: true, summary: 'Hand the numeric lot numbers the sale already uses back out in run order. A number with letters in it (12A) keeps its number and is listed as skipped. Refused (409 RENUMBER_AFTER_START) once the sale is live, completed, or has a lot that sold, because those numbers have been announced. Send basis (from the preview, or lotOrderBasis of the lots as you loaded them): a confirm without one is a 422 BASIS_REQUIRED, and a basis that is no longer the order the sale is in is a 409 ORDER_STALE. A change that met another change to the same lots is a 409 LOTS_BUSY with nothing written.' }),

  // import
  importPlan: r({ method: 'POST', path: '/sales/:saleId/import/plan', request: ImportBody, response: ImportPlan, access: CATALOG, summary: 'Dry-run a CSV catalog import; returns the plan with issues.' }),
  importCommit: r({ method: 'POST', path: '/sales/:saleId/import/commit', request: ImportBody, access: CATALOG, summary: 'Apply a CSV import atomically (409 if the plan has errors). A row never changes the seller of a lot that has sold; the plan warns and the log counts them as sellersKept.',
    response: z.object({ applied: z.object({ created: z.number(), updated: z.number() }), log: ImportLog }) }),
  importLogs: r({ method: 'GET', path: '/sales/:saleId/import/logs', request: NoBody, response: z.array(ImportLog), access: STAFF_ALL, summary: 'Import history for a sale, newest first.' }),

  // registration
  register: r({ method: 'POST', path: '/sales/:saleId/register', request: RegisterBody, response: z.union([Registration, Preview(z.record(z.unknown()))]), access: 'bidder', confirmable: true, summary: 'Register the signed-in bidder for a sale (must accept current terms).' }),
  listRegistrations: r({ method: 'GET', path: '/sales/:saleId/registrations', request: NoBody, response: z.array(Registration), access: STAFF_ALL, summary: 'Registration review queue.' }),
  pendingRegistrations: r({ method: 'GET', path: '/registrations/pending', request: NoBody, response: PendingRegistrations, access: STAFF_ALL, summary: 'Every registration waiting for office review across published and live sales, oldest first, with the count for the badge.' }),
  walkUp: r({ method: 'POST', path: '/sales/:saleId/walk-up', request: WalkUpBody, response: z.union([Registration, Preview(z.record(z.unknown()))]), access: FLOOR_STAFF, confirmable: true, summary: 'Register a walk-up bidder APPROVED with a live paddle (terms accepted on paper at the ring).' }),
  approve: r({ method: 'POST', path: '/registrations/:registrationId/approve', request: ApproveBody, response: z.union([Registration, Preview(RegistrationTransitionPreview)]), access: OFFICE, confirmable: true, summary: 'Approve a registration (bumps version; notifies the ring).' }),
  suspend: r({ method: 'POST', path: '/registrations/:registrationId/suspend', request: TransitionBody, response: z.union([Registration, Preview(RegistrationTransitionPreview)]), access: [...OFFICE, 'clerk'], confirmable: true, summary: 'Suspend a bidder mid-sale.' }),
  revoke: r({ method: 'POST', path: '/registrations/:registrationId/revoke', request: TransitionBody, response: z.union([Registration, Preview(RegistrationTransitionPreview)]), access: OFFICE, confirmable: true, summary: 'Revoke a registration.' }),

  // ADR-29 approvals. recordApproval is HUMAN-only (the API refuses x-ai-agent
  // callers); it is in the table for the typed client / host UI, and the MCP
  // server deliberately does not expose it as a tool.
  recordApproval: r({ method: 'POST', path: '/ai/approvals', request: ApprovalBody, response: AiApproval, access: STAFF_ALL, summary: 'HUMAN ONLY: record approval of one exact preview (by previewHash) so an AI agent may confirm it once.' }),
  checkApproval: r({ method: 'GET', path: '/ai/approvals/:previewHash', request: NoBody, access: STAFF_ALL, summary: 'Is there an unused, unexpired human approval for this previewHash?',
    response: z.object({ previewHash: z.string(), approved: z.boolean() }).passthrough() }),

  // Annie (ADR-39). Reads for the office; invite is a human act (sends a
  // text - not confirmable, so agents cannot call it); materialize is the
  // gated intake→lot bridge.
  annieThreads: r({ method: 'GET', path: '/annie/threads', request: NoBody, response: z.array(AnnieThreadSummary), access: STAFF_ALL, summary: 'Annie conversations (one per phone): who, state, last activity.' }),
  annieThread: r({ method: 'GET', path: '/annie/threads/:threadId', request: NoBody, response: AnnieThreadDetail, access: STAFF_ALL, summary: 'One Annie conversation as a timeline: voice turns, texts, media, jobs.' }),
  annieIntake: r({ method: 'GET', path: '/annie/intakes/:intakeId', request: NoBody, response: AnnieIntakeCard, access: STAFF_ALL, summary: 'An Annie horse intake: facts with provenance, media, contradictions, what is missing, and the proposed lot.' }),
  annieQueue: r({ method: 'GET', path: '/annie/queue', request: NoBody, response: z.array(Row), access: STAFF_ALL, summary: 'Annie needs-a-human queue: open escalations and review flags.' }),
  annieInvite: r({ method: 'POST', path: '/annie/invite', request: AnnieInviteBody, response: AnnieInviteResult, access: OFFICE, summary: 'HUMAN ONLY: create a consignor (name/phone/horses) and text them Annie\'s invitation. Requires the consignor\'s consent to be texted.' }),
  annieMaterialize: r({ method: 'POST', path: '/annie/intakes/:intakeId/materialize', request: AnnieMaterializeBody, response: z.union([Lot, Preview(z.record(z.unknown()))]), access: CATALOG, confirmable: true, summary: 'Publish an ACCEPTED Annie intake to the catalog (facts → attributes, media, documents). Omit confirm for a preview (ADR-29). Requires the office to have accepted it first (ADR-40).' }),
  annieReviewQueue: r({ method: 'GET', path: '/annie/review-queue', request: NoBody, response: z.array(z.record(z.unknown())), access: CATALOG, summary: 'Consignments the seller approved and that await an auction-house decision, each with the immutable approved revision (ADR-40).' }),
  annieAcceptedUnpublished: r({ method: 'GET', path: '/annie/accepted-unpublished', request: NoBody, response: z.array(z.record(z.unknown())), access: CATALOG, summary: 'Consignments the office accepted (or a legacy consignor-approved COMPLETE) that never became a catalog lot - normally empty now that an accept auto-publishes; each can be published from here (ADR-40).' }),
  annieReview: r({ method: 'POST', path: '/annie/intakes/:intakeId/review', request: AnnieReviewBody, response: z.object({ ok: z.boolean(), status: z.string().optional(), decision: z.string() }), access: OFFICE, summary: 'HUMAN ONLY: the auction house accepts / requests changes / declines a consignor-approved listing. Request-changes and decline require a customer-facing reason Annie relays by SMS (ADR-40).' }),
  annieDescribe: r({ method: 'POST', path: '/annie/intakes/:intakeId/describe', request: z.object({}).strict(), response: AnnieDescription, access: CATALOG, summary: 'Write (or rewrite) the catalog description for an Annie intake from its current facts, with a source map; stored on the intake (staging), copied to the lot at materialization.' }),

  // Staff provisioning (the enroll → activate → re-auth ceremony).
  staffList: r({ method: 'GET', path: '/staff', request: NoBody, access: OFFICE, summary: "List this tenant's staff with TOTP enrollment and activation state.",
    response: z.array(z.object({ username: z.string(), email: z.string().optional(), name: z.string().optional(), role: StaffRole, enabled: z.boolean(), mfaEnrolled: z.boolean(), activatedAt: z.number().nullable() })) }),
  staffInvite: r({ method: 'POST', path: '/staff/invite', request: z.object({ email: z.string().email(), role: StaffRole }).strict(), access: ['admin'], summary: 'Invite a staff member (creates the Cognito user, adds the tenant role group).',
    response: z.object({ username: z.string(), created: z.boolean(), role: StaffRole, next: z.string() }) }),
  staffActivateSelf: r({ method: 'POST', path: '/staff/me/activate', request: z.object({}).strict(), access: 'bidder', summary: 'After enrolling TOTP: activate my own staff authority (then sign in again).',
    response: z.object({ activatedAt: z.number(), next: z.string() }) }),
  staffActivate: r({ method: 'POST', path: '/staff/:username/activate', request: z.object({}).strict(), access: ['admin'], summary: 'Admin override: activate a staff member who has enrolled TOTP.',
    response: z.object({ username: z.string(), activatedAt: z.number() }) }),
  staffDeactivate: r({ method: 'POST', path: '/staff/:username/deactivate', request: z.object({}).strict(), access: ['admin'], summary: 'Remove staff authority: clear activation and all role groups.',
    response: z.object({ username: z.string(), deactivated: z.literal(true) }) }),
  // Invoicing (ADR-51). Reads for the office; every mutation is
  // preview->confirm so an agent proposes and a human commits (ADR-29).
  // Onboarding/refreshing the payment connection and recording offline
  // payments are OFF this table on purpose (see routes/invoicing.ts).
  paymentConnection: r({ method: 'GET', path: '/payment-connection', request: NoBody, response: PaymentConnectionView, access: OFFICE, summary: 'The tenant\'s payment account: connected / action required / not connected, and whether online payment is usable right now. Customer payments settle to the tenant, never to HipNumber.' }),
  listInvoiceFees: r({ method: 'GET', path: '/invoice-fees', request: NoBody, response: z.array(InvoiceFeeDefinition), access: OFFICE, summary: 'The tenant\'s fee catalog (company-wide and per-sale): the only source an automated invoice may price from.' }),
  createInvoiceFee: r({ method: 'POST', path: '/invoice-fees', request: CreateInvoiceFeeBody, response: z.union([InvoiceFeeDefinition, Preview(z.record(z.unknown()))]), access: ['admin'], confirmable: true, summary: 'Add a fee to the catalog ("Consignment fee $350"), optionally for one sale. Omit confirm for a preview.' }),
  updateInvoiceFee: r({ method: 'PATCH', path: '/invoice-fees/:feeId', request: UpdateInvoiceFeeBody, response: z.union([InvoiceFeeDefinition, Preview(z.record(z.unknown()))]), access: ['admin'], confirmable: true, summary: 'Edit a fee definition. Existing invoices keep the amount they were created with.' }),
  listInvoices: r({ method: 'GET', path: '/invoices', request: NoBody, response: z.array(InvoiceSummary), access: OFFICE, summary: 'Invoices, newest first: who, what for, amount, status.' }),
  createInvoice: r({ method: 'POST', path: '/invoices', request: CreateInvoiceBody, response: z.union([InvoiceDetail, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Create a DRAFT invoice for a customer, from explicit lines or from a catalog fee. Nothing is sent. Omit confirm for a preview.' }),
  invoice: r({ method: 'GET', path: '/invoices/:invoiceId', request: NoBody, response: InvoiceDetail, access: OFFICE, summary: 'One invoice with its lines, deliveries, payments and full timeline.' }),
  updateInvoice: r({ method: 'PATCH', path: '/invoices/:invoiceId', request: UpdateInvoiceBody, response: z.union([InvoiceDetail, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Edit a DRAFT invoice (409 once issued: a sent invoice is a record). Omit confirm for a preview.' }),
  sendInvoice: r({ method: 'POST', path: '/invoices/:invoiceId/send', request: SendInvoiceBody, response: z.union([InvoiceDetail, Preview(SendInvoicePreview)]), access: OFFICE, confirmable: true, summary: 'Issue and send (or resend) an invoice by SMS and/or email. The preview shows exactly who gets what text; confirm sends. An AI agent needs a human approval of that exact preview.' }),
  voidInvoice: r({ method: 'POST', path: '/invoices/:invoiceId/void', request: VoidInvoiceBody, response: z.union([InvoiceDetail, Preview(z.object({ invoiceId: Uuid, invoiceNumber: z.string(), from: InvoiceStatus, reason: z.string() }))]), access: OFFICE, confirmable: true, summary: 'Void an unpaid invoice with a reason. A paid or partially paid invoice cannot be voided (that is a refund).' }),
  consignorInvoices: r({ method: 'GET', path: '/sellers/:consignorId/invoices', request: NoBody, response: z.array(InvoiceSummary), access: OFFICE, summary: 'Invoices addressed to one seller.' }),

  // Marketing Studio (ADR-56). Reads for staff; create / generate / approve /
  // regenerate are preview->confirm so an agent proposes and a human commits
  // (ADR-29). Rendering an asset needs the deterministic renderer (a later
  // cut), so Generate leaves the asset slots QUEUED.
  previewMarketingCreative: r({ method: 'POST', path: '/sales/:saleId/marketing/preview', request: PreviewCampaignBody, response: MarketingCreativePreview, access: STAFF_ALL, summary: 'Compose a live, unsaved preview of the primary output format for the Create Campaign panel. Persists nothing.' }),
  listMarketingCampaigns: r({ method: 'GET', path: '/sales/:saleId/marketing/campaigns', request: NoBody, response: z.array(MarketingCampaignSummary), access: STAFF_ALL, summary: 'Marketing campaigns for a sale, newest first, with asset counts.' }),
  createMarketingCampaign: r({ method: 'POST', path: '/sales/:saleId/marketing/campaigns', request: CreateCampaignBody, response: z.union([MarketingCampaignView, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Create a DRAFT campaign from the four-step Create Campaign selections (objective, creative mode, channels, style). Omit confirm for a preview (ADR-29).' }),
  marketingCampaign: r({ method: 'GET', path: '/marketing/campaigns/:campaignId', request: NoBody, response: MarketingCampaignDetail, access: STAFF_ALL, summary: 'One campaign: status, the tenant\'s selections, the generated asset slots, and a manifest summary.' }),
  generateMarketingCampaign: r({ method: 'POST', path: '/marketing/campaigns/:campaignId/generate', request: GenerateCampaignBody, response: z.union([MarketingCampaignDetail, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Freeze the brand and auction-truth snapshots into an immutable manifest and create one asset slot per output format (QUEUED until the renderer runs). Omit confirm for a preview.' }),
  listMarketingCampaignAssets: r({ method: 'GET', path: '/marketing/campaigns/:campaignId/assets', request: NoBody, response: z.array(MarketingCampaignAssetView), access: STAFF_ALL, summary: 'The campaign\'s asset slots with their active version and workflow status.' }),
  campaignCopy: r({ method: 'GET', path: '/marketing/campaigns/:campaignId/copy', request: NoBody, response: z.array(MarketingCampaignCopy), access: STAFF_ALL, summary: 'Platform-aware copy for each channel, composed from the campaign\'s frozen auction truth. Deterministic; an LLM polish pass is a later enhancement.' }),
  campaignSchedule: r({ method: 'GET', path: '/marketing/campaigns/:campaignId/schedule', request: NoBody, response: MarketingScheduleView, access: STAFF_ALL, summary: 'The recommended posting schedule (deterministic): one slot per channel, anchored to the sale\'s dates, in the sale timezone.' }),
  polishMarketingCopy: r({ method: 'POST', path: '/marketing/campaigns/:campaignId/copy/polish', request: z.object({}).strict(), response: z.array(MarketingCampaignCopy), access: OFFICE, summary: 'Rewrite the campaign copy with AI (xAI) for warmth, preserving every locked fact (name, dates, URL). 503 when no LLM is configured.' }),
  approveMarketingAsset: r({ method: 'POST', path: '/marketing/assets/:assetId/approve', request: ApproveAssetBody, response: z.union([MarketingCampaignAssetView, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Approve a reviewed asset (must be READY or NEEDS_REVIEW). Omit confirm for a preview.' }),
  regenerateMarketingAsset: r({ method: 'POST', path: '/marketing/assets/:assetId/regenerate', request: RegenerateAssetBody, response: z.union([MarketingCampaignAssetView, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Append a new immutable version of an asset with a regeneration scope; prior versions are never overwritten (spec 16-17). Omit confirm for a preview.' }),
  exportMarketingAsset: r({ method: 'POST', path: '/marketing/assets/:assetId/export', request: ExportAssetBody, response: z.union([MarketingCampaignAssetView, Preview(z.record(z.unknown()))]), access: OFFICE, confirmable: true, summary: 'Rasterize an APPROVED asset to a PNG at its format size (photos inlined), store it content-addressed, and return its download URL. Omit confirm for a preview.' }),

  // Tenant Connections (ADR-57). Starting an OAuth flow is an off-contract
  // admin route (it answers with a provider URL the browser is sent to); the
  // callback is public. These are the reads and the mutations.
  listConnections: r({ method: 'GET', path: '/connections', request: NoBody, response: TenantConnectionsView, access: OFFICE, summary: 'The tenant\'s connected external accounts (Facebook/Instagram, YouTube) with health and publishing destinations. Never returns credentials.' }),
  disconnectConnection: r({ method: 'POST', path: '/connections/:connectionId/disconnect', request: DisconnectConnectionBody, response: z.union([TenantConnectionView, Preview(z.record(z.unknown()))]), access: ['admin'], confirmable: true, summary: 'Disconnect an external account: credentials are wiped and its destinations disabled. Omit confirm for a preview.' }),
  refreshConnection: r({ method: 'POST', path: '/connections/:connectionId/refresh', request: RefreshConnectionBody, response: z.union([TenantConnectionView, Preview(z.record(z.unknown()))]), access: ['admin'], confirmable: true, summary: 'Re-check a connection against the provider: health, and the destinations it can still publish to. Omit confirm for a preview.' }),
  setDestination: r({ method: 'PATCH', path: '/connections/destinations/:destinationId', request: SetDestinationBody, response: z.union([PublishingDestinationView, Preview(z.record(z.unknown()))]), access: ['admin'], confirmable: true, summary: 'Enable or disable one publishing destination (a Page, an Instagram account, a channel). Omit confirm for a preview.' }),
} as const;

/** Routes that must never become MCP tools: human acts (approval) and staff
 *  authority changes (invite/activate/deactivate) - an agent must never be
 *  able to mint or revoke a staffer, even with an admin's token. A seller's
 *  tax id is the same kind of thing: a government identifier that never
 *  passes through an AI agent (D1). */
export const NEVER_TOOLS: ReadonlySet<RouteName> = new Set<RouteName>([
  'recordApproval', 'staffInvite', 'staffActivateSelf', 'staffActivate', 'staffDeactivate', 'setConsignorTaxId',
]);

export type Routes = typeof routes;
export type RouteName = keyof Routes;

// ---------------------------------------------------------------------------
// Platform operator surface
//
// These routes live OUTSIDE /t/:slug and are deliberately NOT in the tenant
// `routes` table, so they can never be derived into MCP tools - administering
// tenants (and AI workers) is never an agent power. They carry no `access`
// staff-role list: every one requires platform-operator authority, enforced by
// `requireOperator` in control-api. The typed client reaches them through
// `platformCall` (see ./client.ts). This table is the one place the contract
// knows they exist.
// ---------------------------------------------------------------------------

export const TenantStatus = z.enum(['active', 'suspended', 'archived']);
export const TenantProvisioning = z.enum(['provisioning', 'ready', 'failed']);

/** Tenant settings as the OPERATOR surface round-trips them. The tenant-scoped
 *  `TenantSettings` deliberately omits `annie` (managed on its own surface), but
 *  a wholesale operator write must not silently drop stored `settings.annie`, so
 *  this variant carries it opaquely - edited fields stay typed, annie survives. */
export const PlatformTenantSettings = TenantSettings.extend({
  annie: z.record(z.unknown()).optional(),
});

/** The full tenants row as the operator surface returns it (create/status/
 *  provision). jsonb columns are opaque here - the operator UI reads the parsed
 *  `settings` off `PlatformTenantDetail`, not this raw shape. */
export const PlatformTenant = z.object({
  id: Uuid,
  slug: z.string(),
  name: z.string(),
  status: TenantStatus,
  provisioning: TenantProvisioning,
  provisioningError: z.string().nullable(),
  firstAdminEmail: z.string().nullable(),
  provisionedAt: z.string().nullable(),
  theme: z.record(z.unknown()),
  defaultBidderFinancialRequirements: z.unknown().nullable(),
  defaultCheckPolicy: z.unknown().nullable(),
  settings: z.unknown().nullable(),
  customDomain: z.string().nullable(),
  createdAt: z.string(),
  /** Set only on a deleted tenant (migration 0084), which no route returns, so
   *  always null here. Optional so an API that predates the columns still
   *  parses. */
  deletedAt: z.string().nullable().optional(),
  deletedSlug: z.string().nullable().optional(),
});

/** One row of the tenant directory (listTenants): curated columns + counts. */
export const PlatformTenantSummary = z.object({
  id: Uuid,
  slug: z.string(),
  name: z.string(),
  status: TenantStatus,
  provisioning: TenantProvisioning,
  provisioningError: z.string().nullable(),
  firstAdminEmail: z.string().nullable(),
  customDomain: z.string().nullable(),
  createdAt: z.string(),
  /** The tenant's company-default storefront branding is on (ADR-55). A quick
   *  operator signal, and the read behind "turn a broken one off". */
  brandingOn: z.boolean(),
  counts: z.object({ sales: z.number().int(), registrations: z.number().int() }),
});

/** ADR-70 cut 6f (hitlist 10.3): the controls under operator intent signals. */
export const IntentControls = z.object({
  /** show_watchers: how many people are watching the ring (a count). */
  showWatchers: z.boolean(),
  /** show_bidder_presence: which approved bidders have the sale open. */
  showBidderPresence: z.boolean(),
  /** show_hover_intent: a bidder's pointer resting on Bid. */
  showHoverIntent: z.boolean(),
  /** show_confirm_intent: a bidder's bid confirmation open. */
  showConfirmIntent: z.boolean(),
  /** show_bid_submitted: a bidder just pressed Bid. */
  showBidSubmitted: z.boolean(),
  /** show_bidder_identity: paddle and name instead of "Online bidder 3". */
  showBidderIdentity: z.boolean(),
}).strict();
export type IntentControls = z.infer<typeof IntentControls>;

/**
 * ADR-70 cut 6e (hitlist 9.2): a tenant's live-bidding capabilities. Off
 * until a Super Admin turns one on; a tenant admin has no route to them.
 * `version` counts the changes (0 = never set) and is what each ring fences
 * on, so a late or repeated message never undoes a newer one.
 */
export const TenantCapabilities = z.object({
  /** live_bidding.operator_max_bid_visibility: the ring console may show the
   *  standing max bids on the current lot, within the viewer's scopes (an
   *  admin sees bidder, amount and what is left above the high bid; a clerk
   *  sees that one stands and whose). Every view is recorded. */
  operatorMaxBidVisibility: z.boolean(),
  /** ADR-70 cut 6f (hitlist 10 and 11, Q12): live_bidding.operator_intent_signals.
   *  The clerk's console shows an Online interest panel: who is watching,
   *  which approved bidders have the sale open, and what they are about to do
   *  on the lot in the ring. Never stored; the bidder page says so first. */
  operatorIntentSignals: z.boolean(),
  /** What the panel shows once it is on (each defaults to on). */
  intentControls: IntentControls,
  version: z.number().int().min(0),
});
export type TenantCapabilities = z.infer<typeof TenantCapabilities>;

/** Tenant detail (getTenant): identity + lifecycle + parsed settings + counts.
 *  `settings` is the PARSED object (settings.ts posture: malformed reads as
 *  null), so the operator settings form binds it directly. */
export const PlatformTenantDetail = z.object({
  id: Uuid,
  slug: z.string(),
  name: z.string(),
  status: TenantStatus,
  provisioning: TenantProvisioning,
  provisioningError: z.string().nullable(),
  firstAdminEmail: z.string().nullable(),
  customDomain: z.string().nullable(),
  provisionedAt: z.string().nullable(),
  createdAt: z.string(),
  annieEnabled: z.boolean(),
  /** ADR-72: whether staff must use an authenticator app ('optional' by default). */
  staffMfa: z.enum(['optional', 'required']),
  /** ADR-70 cut 6e: the live-bidding capabilities only a Super Admin sets. */
  capabilities: TenantCapabilities,
  // Read-tolerant like TenantSettingsView above (ADR-73 cut 2a).
  settings: (PlatformTenantSettings.extend({ saleDefaults: TenantSaleDefaultsRead }) as unknown as z.ZodType<z.infer<typeof PlatformTenantSettings>>).nullable(),
  counts: z.object({ sales: z.number().int(), registrations: z.number().int(), lots: z.number().int() }),
});

/** One public host of a tenant (listTenantHosts): the <slug>.hipnum.com row
 *  the platform claimed plus any CUSTOM host an operator added. `product` is
 *  the brand shell the host serves (ADR-69 Phase 3); until a LotNumber apex
 *  exists, a LotNumber host is a CUSTOM row with product LOTNUMBER. */
export const PlatformTenantHost = z.object({
  id: Uuid,
  hostname: z.string(),
  kind: z.string(),
  status: z.string(),
  product: z.string(),
  isCanonical: z.boolean(),
  verifiedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PlatformTenantHost = z.infer<typeof PlatformTenantHost>;

/** Add a CUSTOM public host to a tenant under a product shell. This records
 *  which tenant and which shell the host serves so the storefront resolves
 *  it; DNS and the Cloudflare custom domain stay the operator step (HN-013). */
export const AddTenantHostBody = z.object({
  hostname: z.string().min(1).max(253),
  product: ProductKey,
}).strict();
export type AddTenantHostBody = z.infer<typeof AddTenantHostBody>;

/** One sale under a tenant (listTenantSales). The deep-edit screens live in
 *  web-admin; this is just enough to list and deep-link. */
export const PlatformSaleRow = z.object({
  id: Uuid,
  slug: z.string(),
  title: z.string(),
  status: SaleStatus,
  startsAt: z.string().nullable(),
  counts: z.object({ lots: z.number().int(), registrations: z.number().int() }),
});

/** The three-source truth for one sale's ring format (ADR-48), for the operator
 *  console: what the tenant REQUESTED, what the ring is PROVISIONED for, and what
 *  the running engine is OBSERVED to be - plus the generation and a derived
 *  status. `observed*` is null when runtime truth cannot be established (which is
 *  itself a fail-closed condition at go-live). */
export const PlatformSaleProvisioning = z.object({
  saleId: Uuid,
  title: z.string(),
  saleStatus: SaleStatus,
  requestedMode: AuctionMode,
  provisionedMode: AuctionMode.nullable(),
  provisioningId: z.string().nullable(),
  observedMode: AuctionMode.nullable(),
  observedProvisioningId: z.string().nullable(),
  biddingOpened: z.boolean(),
  status: z.enum(['matched', 'awaiting_provisioning', 'mode_mismatch', 'runtime_mode_mismatch', 'generation_mismatch', 'observed_unavailable']),
  /** ADR-70 cut 5b: the same ladder for each ring the sale runs, in ring
   *  order. The top-level observed and status are the first ring that is not
   *  matched (else the first ring), so a one-ring sale reads as it always has. */
  rings: z.array(z.object({
    ringId: z.string(),
    name: z.string(),
    observedMode: AuctionMode.nullable(),
    observedProvisioningId: z.string().nullable(),
    status: z.enum(['matched', 'awaiting_provisioning', 'mode_mismatch', 'runtime_mode_mismatch', 'generation_mismatch', 'observed_unavailable']),
  })).optional(),
});

/** One staff member under a tenant (listTenantStaff) - same shape as the
 *  tenant-scoped `staffList`, read here with operator authority. */
export const PlatformStaffMember = z.object({
  /** The directory's own id (a UUID in Cognito, where email is a sign-in alias). */
  username: z.string(),
  email: z.string().optional(),
  /** The person's name, when the directory holds one. */
  name: z.string().optional(),
  role: StaffRole,
  enabled: z.boolean(),
  mfaEnrolled: z.boolean(),
  activatedAt: z.number().nullable(),
});

export const PlatformAuditRow = z.object({
  id: Uuid,
  operator: z.string(),
  action: z.string(),
  tenantId: Uuid.nullable(),
  actingAs: z.string().nullable(),
  method: z.string().nullable(),
  path: z.string().nullable(),
  detail: z.unknown(),
  at: z.string(),
});

export const PlatformAccount = z.object({
  id: Uuid,
  email: z.string(),
  displayName: z.string().nullable(),
  createdAt: z.string(),
});

export const CreateTenantBody = z.object({
  slug: z.string().min(2).regex(/^[a-z0-9-]+$/),
  name: z.string().min(1),
  theme: z.record(z.unknown()).optional(),
  /** Invite the first admin in the same call. */
  adminEmail: z.string().email().optional(),
}).strict();
export const SetTenantStatusBody = z.object({ status: TenantStatus, reason: z.string().optional() }).strict();
/**
 * Moving a tenant to a new address: its slug (the /t/:slug routes, the admin
 * `?t=` link, the platform URL) and its <slug>.hipnum.com public host change
 * together, in one transaction, or not at all. Its id, staff, sales and media
 * never move; only the name on the door does. Old bookmarks stop working.
 */
export const SetTenantSlugBody = z.object({ slug: z.string().min(2).max(63).regex(/^[a-z0-9-]+$/), reason: z.string().optional() }).strict();

/**
 * Deleting a tenant (migration 0084). A deleted tenant is REMOVED, not erased:
 * it leaves the directory and every surface, its people and links stop
 * working at once, and its slug and public host are freed - but its records
 * (sales, invoices, audit, evidence) stay in the database, hidden. It is
 * terminal: no route brings it back.
 */
export const TenantDeletionIssue = z.object({ code: z.string(), message: z.string() });

/** What is switched off or freed the moment a tenant is deleted. */
export const TenantDeletionReleases = z.object({
  /** Public hostnames (<slug>.hipnum.com) freed for reuse. */
  hosts: z.array(z.string()),
  /** Annie numbers switched off. Each can be assigned to another tenant. */
  phoneNumbers: z.array(z.string()),
  /** Display sticks unbound; each drops to a pairing screen. */
  displays: z.number().int(),
  buyerCards: z.number().int(),
  payLinks: z.number().int(),
  annieLinks: z.number().int(),
  /** Connected accounts (Meta, Google) disconnected, credentials wiped. */
  connections: z.number().int(),
});

/** The read before the confirm (tenantDeletionPreview). Changes nothing. */
export const TenantDeletionPreview = z.object({
  tenant: z.object({ id: Uuid, slug: z.string(), name: z.string(), status: TenantStatus }),
  /** While any blocker stands the delete is refused (409). */
  blockers: z.array(TenantDeletionIssue),
  /** Worth knowing first; none of them refuses the delete. */
  warnings: z.array(TenantDeletionIssue),
  releases: TenantDeletionReleases,
  /** Staff memberships that will be removed; null when the staff directory
   *  cannot be read here. */
  staff: z.number().int().nullable(),
  /** Records that stay in the database, hidden. Nothing is erased. */
  keeps: z.object({ sales: z.number().int(), lots: z.number().int(), registrations: z.number().int(), invoices: z.number().int() }),
});

export const DeleteTenantBody = z.object({
  /** The tenant's slug, typed again by the operator. */
  confirmSlug: z.string().min(1),
  reason: z.string().trim().min(1).max(500),
}).strict();

export const TenantDeletionResult = z.object({
  tenantId: Uuid,
  /** The slug it had, now free for a new tenant. */
  slug: z.string(),
  name: z.string(),
  deletedAt: z.string(),
  released: TenantDeletionReleases,
  /** Staff memberships removed from the directory after the delete committed.
   *  Best effort: a failure is reported here and in the audit trail, and a
   *  leftover membership reaches nothing, because the tenant is gone. */
  staff: z.object({ removed: z.number().int(), failed: z.array(z.string()), note: z.string().nullable() }),
});

export const PlatformInviteStaffBody = z.object({ email: z.string().email(), role: StaffRole }).strict();

/**
 * The staff user pool's password policy (infra core-stack), stated once so
 * the operator console and the API refuse the same passwords with the same
 * words: at least 12 characters with an upper-case letter, a lower-case
 * letter and a digit. Symbols are welcome, not required.
 */
export const STAFF_PASSWORD_RULE = 'At least 12 characters, with an upper-case letter, a lower-case letter and a digit.';
export const StaffPassword = z.string()
  .min(12, STAFF_PASSWORD_RULE)
  .max(256, 'At most 256 characters.')
  .refine((p) => /[A-Z]/.test(p) && /[a-z]/.test(p) && /\d/.test(p), STAFF_PASSWORD_RULE);

/**
 * Create a staff member with a password the operator chose, for the call
 * where the operator reads the sign-in details out over the phone. Nothing
 * is emailed. `mustChangePassword` makes it a temporary password: the person
 * chooses their own the first time they sign in (the pool gives a temporary
 * password 3 days). The password is never written to the audit trail.
 */
export const PlatformCreateStaffBody = z.object({
  email: z.string().email(),
  role: StaffRole,
  /** Shown in place of the directory id wherever staff are listed. */
  name: z.string().trim().min(1).max(120).optional(),
  password: StaffPassword,
  mustChangePassword: z.boolean(),
}).strict();

/** Replace a staff member's password (an operator reset). Same rules. */
export const PlatformSetStaffPasswordBody = z.object({
  password: StaffPassword,
  mustChangePassword: z.boolean(),
}).strict();
/** Operator settings edit - no preview/confirm (never an agent path); replaces
 *  tenants.settings wholesale, optionally renaming the tenant. */
export const PlatformUpdateSettingsBody = z.object({
  name: z.string().min(1).max(200).optional(),
  settings: PlatformTenantSettings,
}).strict();
export const SetStaffRolesBody = z.object({
  add: z.array(StaffRole).optional(),
  remove: z.array(StaffRole).optional(),
}).strict();

const EmptyBody = z.object({}).strict();

// ---------------------------------------------------------------------------
// Incident Operations (ADR-58): INC / CHG / RITM.
//
// The wire shapes and request bodies. Where they sit in the route tables is
// the point: every MUTATION is in PLATFORM_ROUTES (operator-only, never
// derivable into an MCP tool) and every READ is in DIAGNOSTIC_ROUTES (GET-only
// by construction, derivable). An agent can therefore read the operational
// record - "is there an open incident on this ring" is observation - and can
// never create, edit or transition one. Agent-authored records arrive in a
// later phase through the same preview/approval mechanism ADR-29 uses.
//
// Dates are ISO strings on the wire, as everywhere else in this file.
// ---------------------------------------------------------------------------

export const OpsTimelineEntry = z.object({
  id: Uuid,
  recordKind: RecordKindSchema,
  recordId: Uuid,
  eventType: TimelineEventTypeSchema,
  actorType: ActorTypeSchema,
  actor: z.string(),
  summary: z.string(),
  payload: z.unknown(),
  at: z.string(),
});
export type OpsTimelineEntry = z.infer<typeof OpsTimelineEntry>;

export const OpsImpact = z.object({
  id: Uuid,
  recordKind: z.enum(['INC', 'CHG']),
  recordId: Uuid,
  resourceType: ImpactResourceTypeSchema,
  resourceId: z.string(),
  label: z.string(),
  impactType: ImpactTypeSchema,
  metadata: z.unknown(),
  createdAt: z.string(),
});
export type OpsImpact = z.infer<typeof OpsImpact>;

/**
 * A link as seen FROM one record. `direction` is OUT when this record is the
 * source and IN when another record points at it; `other` is always the far
 * end, resolved to a title and status when it is an ops record.
 */
export const OpsLinkView = z.object({
  id: Uuid,
  relationship: LinkRelationshipSchema,
  direction: z.enum(['OUT', 'IN']),
  other: z.object({
    kind: LinkTargetKindSchema,
    ref: z.string(),
    title: z.string().nullable(),
    status: z.string().nullable(),
  }),
  note: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
});
export type OpsLinkView = z.infer<typeof OpsLinkView>;

export const OpsIncident = z.object({
  id: Uuid,
  number: z.string(),
  title: z.string(),
  description: z.string(),
  severity: IncidentSeveritySchema,
  status: IncidentStatusSchema,
  source: IncidentSourceSchema,
  owner: z.string().nullable(),
  incidentCommander: z.string().nullable(),
  publicImpact: z.boolean(),
  rootCause: z.string().nullable(),
  resolutionSummary: z.string().nullable(),
  detectedAt: z.string(),
  acknowledgedAt: z.string().nullable(),
  identifiedAt: z.string().nullable(),
  mitigatedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Where the conversation is (cut 5): the Slack war room, when one was created. */
  slackChannelId: z.string().nullable(),
  slackChannelName: z.string().nullable(),
});
export type OpsIncident = z.infer<typeof OpsIncident>;

/** A diagnostic fingerprint the sweep is tracking against this incident (ADR-58 cut 2). */
export const OpsSignal = z.object({
  fingerprint: z.string(),
  code: z.string(),
  severity: DiagSeverity,
  message: z.string(),
  scope: z.unknown(),
  evidence: z.unknown(),
  status: SignalStatusSchema,
  consecutiveSeen: z.number().int(),
  consecutiveClear: z.number().int(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  clearedAt: z.string().nullable(),
});
export type OpsSignal = z.infer<typeof OpsSignal>;

/** The numbers behind a ring verdict, so a reader can check the arithmetic (ADR-49). */
export const RingDiagnosticPosition = z.object({
  ringLastSeq: z.number().nullable(),
  lastProjectedSeq: z.number().nullable(),
  /** ringLastSeq - lastProjectedSeq. The subtraction nothing made before ADR-49. */
  lag: z.number().nullable(),
  currentLotId: z.string().nullable(),
  lotState: z.string().nullable(),
  activeEngineId: z.string().nullable(),
  observedMode: z.string().nullable(),
  generation: z.string().nullable(),
  heartbeatAgeMs: z.number().nullable(),
  // ---- ADR-60 cut 4: the live runtime and its crowd (null = not observed) ----
  onlineStatus: z.string().nullable(),
  pauseReason: z.string().nullable(),
  confirmMode: z.boolean().nullable(),
  pendingBidAgeMs: z.number().nullable(),
  operatorConnected: z.boolean().nullable(),
  bidderConnections: z.number().nullable(),
  floorConnections: z.number().nullable(),
  observerConnections: z.number().nullable(),
  reconnectsLastMinute: z.number().nullable(),
  resyncsLastMinute: z.number().nullable(),
  decisionP95Ms: z.number().nullable(),
  clientRttP95Ms: z.number().nullable(),
  bidCommands: z.number().nullable(),
  // ---- ADR-60 cut 10: the live command center (null = not observed) ----
  engineEpoch: z.number().nullable(),
  /** False when the ring did not report its board: then currentAsk,
   *  currentHighBid, highBidderRef and pendingBidAmount are unobserved,
   *  not empty. */
  boardObserved: z.boolean(),
  currentAsk: z.number().nullable(),
  currentHighBid: z.number().nullable(),
  /** Masked, as the public wire shows it: 'Floor' | 'Online' | null. */
  highBidderRef: z.string().nullable(),
  pendingBidAmount: z.number().nullable(),
  mediaConfigured: z.boolean().nullable(),
  mediaProvider: z.string().nullable(),
  mediaReporters: z.number().nullable(),
  mediaFailing: z.number().nullable(),
});

/**
 * The payload of a DIAGNOSTIC timeline entry: what "Run diagnostics" saw. It
 * is a snapshot, kept on the record so it can be read after recovery. The
 * screen parses `payload` with this schema; the wire carries it as unknown
 * like every other timeline payload.
 */
export const OpsDiagnosticPayload = z.object({
  generatedAt: z.string(),
  health: DiagHealth,
  probes: z.array(z.object({
    id: z.string(),
    title: z.string(),
    health: DiagHealth,
    evidence: z.enum(['observed', 'stale', 'absent']),
    detail: z.record(z.unknown()).nullable(),
    why: z.string().nullable(),
    tookMs: z.number(),
  })),
  rings: z.array(z.object({
    tenantSlug: z.string(),
    saleId: z.string(),
    ringId: z.string(),
    saleTitle: z.string(),
    depth: z.enum(['liveness', 'full']),
    health: DiagHealth,
    position: RingDiagnosticPosition,
    findings: z.array(DiagFinding),
    blindSpots: z.array(z.string()),
  })),
  summary: DiagFindingSummary,
  blindSpots: z.array(z.string()),
  notJudged: z.array(z.string()),
});
export type OpsDiagnosticPayload = z.infer<typeof OpsDiagnosticPayload>;

// ADR-61 cut 1: the declared surface a change touches, in the closed vocabulary the human-line classifier reads.
export const ChangeSurfaceSchema = z.object({
  areas: z.array(z.enum(SURFACE_AREAS)).max(20),
  blastRadius: z.object({ tenants: z.number().int().nonnegative(), sales: z.number().int().nonnegative(), rings: z.number().int().nonnegative() }).optional(),
  rollbackProven: z.boolean().optional(),
  pattern: z.string().max(120).optional(),
  biddingOpenAtExecution: z.boolean().optional(),
  telemetryCovered: z.boolean().optional(),
}).strict();
export const ChangeApprovalBody = z.object({
  method: z.enum(APPROVAL_METHODS),
  reason: z.string().min(1).max(2_000),
}).strict();
export const OpsChangeGate = z.object({
  id: Uuid,
  gate: z.enum(GATES),
  verdict: z.enum(GATE_VERDICTS),
  evidence: z.record(z.unknown()),
  evaluatedBy: z.string(),
  evaluatedAt: z.string(),
});
export const AddChangeGateBody = z.object({
  gate: z.enum(GATES),
  verdict: z.enum(GATE_VERDICTS),
  /** { source, observed, required, note, ... }: whatever the gate read. Never empty for PASS or FAIL. */
  evidence: z.record(z.unknown()),
}).strict();
/** ADR-61 cut 2: ask the platform to evaluate the gates it can read itself. Each verdict becomes a row with what was observed and why. */
export const EvaluateChangeGatesBody = z.object({
  /** Default: every platform-evaluable gate. */
  gates: z.array(z.enum(PLATFORM_EVALUABLE_GATES)).min(1).optional(),
  /** The loadtest harness result file, verbatim, for BENCHMARK_CLEAN. A summary is not evidence. */
  benchmark: z.unknown().optional(),
}).strict();
/** ADR-61 cut 2: what the record says right now, from the latest row per gate against the policy in force. */
export const ChangeCase = z.object({
  policy: z.object({ key: z.string(), version: z.number().int() }).nullable(),
  requiredGates: z.array(z.enum(GATES)),
  latest: z.array(z.object({ gate: z.enum(GATES), verdict: z.enum(GATE_VERDICTS), evaluatedAt: z.string(), evaluatedBy: z.string() })),
  missing: z.array(z.enum(GATES)),
  failed: z.array(z.enum(GATES)),
  notApplicable: z.array(z.enum(GATES)),
  gatesSatisfied: z.boolean(),
  risk: z.object({ score: z.number().nullable(), ceiling: z.number().nullable(), within: z.boolean().nullable() }),
  humanLines: z.array(z.enum(HUMAN_LINE_KEYS)),
  verdict: z.enum(['READY', 'HUMAN_DECISION', 'NOT_READY']),
  statement: z.string(),
});
/** ADR-61 cut 3: the ONE action family the first executor knows. */
export const ExecuteChangeBody = z.object({
  action: z.literal('RING_PUBLIC_SNAPSHOT_WAVE'),
  /** The value to set (forward), or omit with rollback: true to execute the recorded reverse step. */
  desired: z.enum(['always', 'coalesced', 'never']).optional(),
  rollback: z.boolean().optional(),
  reason: z.string().min(1).max(2_000),
}).strict();
export const ChangeExecutionStep = z.object({ at: z.string(), step: z.string(), detail: z.record(z.unknown()) });
export const ChangeExecution = z.object({
  action: z.string(),
  direction: z.enum(['FORWARD', 'ROLLBACK']),
  /** SUCCEEDED: new epoch observed running the desired policy. FAILED: known not to have taken effect (prior observed value restored). UNCONFIRMED: past the point of no return and unproven inside the budget; nothing reverted, ring held until reconciled. REFUSED: nothing touched. */
  outcome: z.enum(['SUCCEEDED', 'REFUSED', 'FAILED', 'UNCONFIRMED']),
  reason: z.string(),
  startedAt: z.string(),
  finishedAt: z.string(),
  target: z.object({ tenantSlug: z.string(), saleId: z.string(), ringId: z.string() }).nullable(),
  before: z.record(z.unknown()).nullable(),
  after: z.record(z.unknown()).nullable(),
  steps: z.array(ChangeExecutionStep),
  case: ChangeCase.nullable(),
});
/** ADR-61 cut 4: one execution row of a change in the post-execution state machine. */
export const ChangeExecutionRow = z.object({
  id: Uuid, changeId: Uuid, tenantId: Uuid, saleId: Uuid, ringId: z.string(), owner: z.string(),
  status: z.enum(['ACTIVE', 'UNCONFIRMED', 'RELEASED']),
  phase: z.enum(EXECUTION_PHASES),
  direction: z.enum(['FORWARD', 'ROLLBACK']).nullable(),
  outcome: z.string().nullable(),
  desired: z.string().nullable(), prior: z.string().nullable(),
  policyKey: z.string().nullable(), policyVersion: z.number().int().nullable(),
  validation: z.record(z.unknown()),
  execution: z.record(z.unknown()).nullable(),
  reconcileAttempts: z.number().int(),
  incidentId: Uuid.nullable(),
  acquiredAt: z.string(), expiresAt: z.string(), releasedAt: z.string().nullable(),
});
export const PolicyApprovalOutcome = z.object({ number: z.string(), granted: z.boolean(), because: z.string() });
export const EvaluatedChangeGates = z.object({
  gates: z.array(OpsChangeGate),
  case: ChangeCase,
  riskScore: z.number().int().nullable(),
});
export const GovernancePolicyVersion = z.object({
  id: Uuid, key: z.string(), version: z.number().int(), definition: z.record(z.unknown()), approvedBy: z.string(), reason: z.string(), supersedesVersion: z.number().int().nullable(), createdAt: z.string(),
});
export const CreatePolicyVersionBody = z.object({
  key: z.string().min(1).max(120).regex(/^[A-Z0-9_]+$/),
  definition: z.record(z.unknown()),
  reason: z.string().min(1).max(2_000),
}).strict();
export const GovernancePattern = z.object({
  id: Uuid, key: z.string(), version: z.number().int(), title: z.string(), policyKey: z.string(), policyVersion: z.number().int(), rollback: z.record(z.unknown()), validation: z.record(z.unknown()),
  registeredBy: z.string(), reason: z.string(), registeredFromChange: z.string().nullable(), active: z.boolean(), createdAt: z.string(),
});
export const RegisterPatternBody = z.object({
  key: z.string().min(1).max(120).regex(/^[A-Z0-9_]+$/),
  title: z.string().min(1).max(200),
  policyKey: z.string().min(1).max(120),
  policyVersion: z.number().int().positive(),
  rollback: z.record(z.unknown()),
  validation: z.record(z.unknown()),
  /** The completed, validated change this pattern is registered from (CHG000184). */
  fromChange: z.string().min(1).max(20).optional(),
  reason: z.string().min(1).max(2_000),
}).strict();
export const GovernanceAuditEntry = z.object({
  seq: z.number().int(), actor: z.string(), stepUpAt: z.string(), action: z.string(), tableName: z.string(), recordId: z.string().nullable(), reason: z.string(), detail: z.record(z.unknown()), prevHash: z.string(), hash: z.string(), at: z.string(),
});
export const GovernanceAuditView = z.object({
  entries: z.array(GovernanceAuditEntry),
  /** Verified on read: every row's hash chains from the previous. A break is named by seq. */
  chain: z.object({ ok: z.boolean(), length: z.number().int(), brokenAt: z.number().int().nullable() }),
});


export const OpsChange = z.object({
  id: Uuid,
  number: z.string(),
  title: z.string(),
  description: z.string(),
  type: ChangeTypeSchema,
  risk: ChangeRiskSchema,
  status: ChangeStatusSchema,
  requestedBy: z.string(),
  owner: z.string().nullable(),
  plannedStart: z.string().nullable(),
  plannedEnd: z.string().nullable(),
  implementationPlan: z.string(),
  validationPlan: z.string(),
  rollbackPlan: z.string(),
  deploymentSha: z.string().nullable(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // ---- ADR-61 cut 1 ----
  surface: ChangeSurfaceSchema,
  /** The human lines this change crosses at its last write (empty = eligible for policy approval once a policy exists). */
  humanLines: z.array(z.enum(HUMAN_LINE_KEYS)),
  governance: z.boolean(),
  pattern: z.string().nullable(),
  riskScore: z.number().int().nullable(),
  riskInputs: z.record(z.unknown()).nullable(),
  blastRadius: z.record(z.unknown()).nullable(),
  decisionOwner: z.string().nullable(),
  rollbackOwner: z.string().nullable(),
  approvalMethod: z.enum(APPROVAL_METHODS).nullable(),
  approval: z.record(z.unknown()).nullable(),
  execution: z.record(z.unknown()).nullable(),
  validation: z.record(z.unknown()).nullable(),
});
export type OpsChange = z.infer<typeof OpsChange>;

export const OpsChangeDetail = OpsChange.extend({
  impacts: z.array(OpsImpact),
  links: z.array(OpsLinkView),
  timeline: z.array(OpsTimelineEntry),
  allowedTransitions: z.array(ChangeStatusSchema),
  /** The SEV1 freeze verdict for THIS change right now, computed on read (never stored). */
  freeze: FreezeVerdictSchema,
  /** The auction-aware guard (cut 3): whether entering IMPLEMENTING needs a written override because bidding is open. */
  guard: GuardVerdictSchema,
  /** ADR-61 cut 2: the platform's case from the latest row per gate. */
  case: ChangeCase,
  /** ADR-61 cut 4: every execution row, newest first, with its phase and validation. */
  executions: z.array(ChangeExecutionRow),
  /** ADR-61 cut 1: every gate evaluation, newest first, with its evidence. */
  gates: z.array(OpsChangeGate),
});
export type OpsChangeDetail = z.infer<typeof OpsChangeDetail>;

/** What the public has been told about an incident (cut 4). Never a tenant, host, SHA or diagnostic number. */
export const OpsPublicUpdate = z.object({
  id: z.number().int(),
  state: PublicStateSchema,
  message: z.string(),
  publishedBy: z.string(),
  publishedAt: z.string(),
  delivered: z.boolean(),
  error: z.string().nullable(),
});
export const OpsPublicStatus = z.object({
  components: z.array(PublicComponentIdSchema),
  impact: PublicImpactSchema,
  title: z.string(),
  state: PublicStateSchema,
  firstPublishedAt: z.string(),
  lastPublishedAt: z.string(),
  /** Newest first. */
  updates: z.array(OpsPublicUpdate),
});
export type OpsPublicStatus = z.infer<typeof OpsPublicStatus>;

export const PublishStatusBody = z.object({
  components: z.array(PublicComponentIdSchema).min(1).max(7),
  impact: PublicImpactSchema,
  state: PublicStateSchema,
  /** Defaults to the deterministic template for the impact and components. */
  title: z.string().min(1).max(200).optional(),
  /** Defaults to the deterministic template for the state. */
  message: z.string().min(1).max(2_000).optional(),
}).strict();

export const OpsIncidentDetail = OpsIncident.extend({
  impacts: z.array(OpsImpact),
  links: z.array(OpsLinkView),
  /** Newest first. */
  timeline: z.array(OpsTimelineEntry),
  /** What the lifecycle permits from here - the buttons the screen draws. */
  allowedTransitions: z.array(IncidentStatusSchema),
  /** The sweep's signals on this incident, most recently seen first. Empty for a hand-opened incident nothing has fired on. */
  signals: z.array(OpsSignal),
  /**
   * "What changed?" - changes that entered production in the 24 hours before
   * this incident was detected, or were still in flight when it was, newest
   * first. The first question in any incident, answered before it is asked.
   */
  recentChanges: z.array(OpsChange),
  /** The public projection (cut 4): what status.hipnum.com has been told, or null when nothing was published. */
  public: OpsPublicStatus.nullable(),
  /** Whether this control-api can publish at all (a publisher is configured) and where the page is. */
  statusPage: z.object({ configured: z.boolean(), url: z.string().nullable(), suggestedComponents: z.array(PublicComponentIdSchema) }),
  /** The response surfaces (cut 5): Slack and GitHub, what is configured and what exists for this incident. */
  integrations: z.object({
    slack: z.object({ configured: z.boolean(), opsChannel: z.string().nullable(), warRoom: z.object({ id: z.string(), name: z.string() }).nullable() }),
    github: z.object({ configured: z.boolean(), repo: z.string().nullable(), issues: z.array(z.object({ number: z.number().int(), url: z.string() })) }),
  }),
});
export type OpsIncidentDetail = z.infer<typeof OpsIncidentDetail>;

/** One live sale and what is at stake on it (cut 3). */
export const OpsExposedSale = z.object({
  tenantSlug: z.string(),
  tenantName: z.string(),
  saleId: Uuid,
  title: z.string(),
  biddingOpen: z.boolean(),
  biddingOpenedAt: z.string().nullable(),
  approvedBidders: z.number().int(),
  lotsUnsold: z.number().int(),
});
export const OpsExposure = z.object({
  generatedAt: z.string(),
  sales: z.array(OpsExposedSale),
  totals: LiveExposureSchema,
});
export type OpsExposure = z.infer<typeof OpsExposure>;

export const OpsRequest = z.object({
  id: Uuid,
  number: z.string(),
  title: z.string(),
  description: z.string(),
  category: RequestCategorySchema,
  status: RequestStatusSchema,
  requestedBy: z.string(),
  requester: z.string().nullable(),
  tenantSlug: z.string().nullable(),
  tenantName: z.string().nullable(),
  owner: z.string().nullable(),
  dueAt: z.string().nullable(),
  fulfilledAt: z.string().nullable(),
  closedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OpsRequest = z.infer<typeof OpsRequest>;

export const OpsRequestDetail = OpsRequest.extend({
  links: z.array(OpsLinkView),
  timeline: z.array(OpsTimelineEntry),
  allowedTransitions: z.array(RequestStatusSchema),
});
export type OpsRequestDetail = z.infer<typeof OpsRequestDetail>;

/** The command center's first screen: is anything wrong, and is it safe to change things. */
export const OpsSummary = z.object({
  generatedAt: z.string(),
  incidents: z.object({
    active: z.number().int(),
    /** Active incidents per severity; every severity is a key, zero included. */
    bySeverity: z.record(z.number().int()),
    byStatus: z.record(z.number().int()),
  }),
  changes: z.object({
    open: z.number().int(),
    awaitingApproval: z.number().int(),
    scheduled: z.number().int(),
    inFlight: z.number().int(),
  }),
  requests: z.object({
    open: z.number().int(),
    waiting: z.number().int(),
  }),
  /** The SEV1 freeze: active while any SEV1 incident is, naming them. */
  freeze: z.object({
    active: z.boolean(),
    incidents: z.array(z.object({ number: z.string(), title: z.string(), severity: IncidentSeveritySchema })),
  }),
  /** The worst active SEV1 / SEV2, for the banner every operator sees. Null = no banner. */
  banner: z.object({ number: z.string(), title: z.string(), severity: IncidentSeveritySchema, status: IncidentStatusSchema }).nullable(),
  /** Active incidents, worst first, for the overview list. */
  activeIncidents: z.array(OpsIncident),
  /**
   * When the platform was last looked at by the sweep. `lastRunAt` null
   * means it has never run here, which the screen says out loud: a quiet
   * incident list is only good news when something is actually looking.
   */
  sweep: z.object({
    lastRunAt: z.string().nullable(),
    lastHealth: z.string().nullable(),
    lastError: z.string().nullable(),
    openSignals: z.number().int(),
  }),
});
export type OpsSummary = z.infer<typeof OpsSummary>;

const OpsImpactInput = z.object({
  resourceType: ImpactResourceTypeSchema,
  resourceId: z.string().min(1).max(200),
  label: z.string().min(1).max(200),
  impactType: ImpactTypeSchema.optional(),
  metadata: z.record(z.unknown()).optional(),
}).strict();

export const CreateIncidentBody = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(20_000).optional(),
  severity: IncidentSeveritySchema,
  /** Defaults to OPERATOR: Phase 1 incidents are logged by a person. */
  source: IncidentSourceSchema.optional(),
  /** When the problem started, if known to differ from now. */
  detectedAt: IsoDate.optional(),
  publicImpact: z.boolean().optional(),
  owner: z.string().max(200).optional(),
  impacts: z.array(OpsImpactInput).max(50).optional(),
}).strict();

export const UpdateIncidentBody = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(20_000).optional(),
  severity: IncidentSeveritySchema.optional(),
  owner: z.string().max(200).nullable().optional(),
  incidentCommander: z.string().max(200).nullable().optional(),
  publicImpact: z.boolean().optional(),
  rootCause: z.string().max(20_000).nullable().optional(),
  resolutionSummary: z.string().max(20_000).nullable().optional(),
}).strict();

export const IncidentTransitionBody = z.object({
  to: IncidentStatusSchema,
  note: z.string().max(5_000).optional(),
}).strict();

export const CreateChangeBody = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(20_000).optional(),
  type: ChangeTypeSchema,
  risk: ChangeRiskSchema.optional(),
  owner: z.string().max(200).optional(),
  plannedStart: IsoDate.optional(),
  plannedEnd: IsoDate.optional(),
  implementationPlan: z.string().max(20_000).optional(),
  validationPlan: z.string().max(20_000).optional(),
  rollbackPlan: z.string().max(20_000).optional(),
  deploymentSha: z.string().regex(/^[0-9a-f]{7,40}$/i).optional(),
  // ADR-61 cut 1
  surface: ChangeSurfaceSchema.optional(),
  decisionOwner: z.string().max(200).optional(),
  rollbackOwner: z.string().max(200).optional(),
}).strict();

export const UpdateChangeBody = z.object({
  // ADR-61 cut 1 (update)
  surface: ChangeSurfaceSchema.optional(),
  decisionOwner: z.string().min(1).max(200).nullable().optional(),
  rollbackOwner: z.string().min(1).max(200).nullable().optional(),
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(20_000).optional(),
  type: ChangeTypeSchema.optional(),
  risk: ChangeRiskSchema.optional(),
  owner: z.string().max(200).nullable().optional(),
  plannedStart: IsoDate.nullable().optional(),
  plannedEnd: IsoDate.nullable().optional(),
  implementationPlan: z.string().max(20_000).optional(),
  validationPlan: z.string().max(20_000).optional(),
  rollbackPlan: z.string().max(20_000).optional(),
  deploymentSha: z.string().regex(/^[0-9a-f]{7,40}$/i).nullable().optional(),
}).strict();

export const ChangeTransitionBody = z.object({
  to: ChangeStatusSchema,
  note: z.string().max(5_000).optional(),
  /** ADR-61 cut 1: required to enter APPROVED. HUMAN by a named person, or (once the engine exists) POLICY by the engine. */
  approval: ChangeApprovalBody.optional(),
  /** Required to enter IMPLEMENTING while bidding is open on any sale (the auction-aware guard). Goes on the timeline. */
  overrideReason: z.string().min(1).max(2_000).optional(),
}).strict();

export const CreateRequestBody = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(20_000).optional(),
  category: RequestCategorySchema,
  requester: z.string().max(200).optional(),
  tenantSlug: z.string().min(1).max(100).optional(),
  owner: z.string().max(200).optional(),
  dueAt: IsoDate.optional(),
}).strict();

export const UpdateRequestBody = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(20_000).optional(),
  category: RequestCategorySchema.optional(),
  requester: z.string().max(200).nullable().optional(),
  tenantSlug: z.string().min(1).max(100).nullable().optional(),
  owner: z.string().max(200).nullable().optional(),
  dueAt: IsoDate.nullable().optional(),
}).strict();

export const RequestTransitionBody = z.object({
  to: RequestStatusSchema,
  note: z.string().max(5_000).optional(),
}).strict();

export const OpsNoteBody = z.object({ body: z.string().min(1).max(20_000) }).strict();
export const AddOpsImpactBody = OpsImpactInput;
export const AddOpsLinkBody = z.object({
  relationship: LinkRelationshipSchema,
  targetKind: LinkTargetKindSchema,
  /** A record number (INC000184), a GitHub number, a SHA or a URL, per targetKind. */
  targetRef: z.string().min(1).max(500),
  note: z.string().max(1_000).optional(),
}).strict();
const OpsRemoved = z.object({ removed: z.literal(true) });

// ---------------------------------------------------------------------------
// ADR-60 cut 7: safe remote support commands (brief sections 41 to 43).
// The allowlist is restated here from @hipnumber/protocol on purpose: the
// contract package cannot depend on the ring wire, and a control-api test
// pins the two lists equal so they cannot drift.
// ---------------------------------------------------------------------------
export const SUPPORT_COMMAND_TYPES = [
  'REFRESH_RUNTIME_STATE', 'RECONNECT_REALTIME', 'RESTART_MEDIA_PLAYER', 'TEST_CAMERA', 'TEST_MICROPHONE',
  'RUN_NETWORK_DIAGNOSTIC', 'CAPTURE_CLIENT_DIAGNOSTICS', 'CLEAR_RUNTIME_CACHE', 'RELOAD_APPLICATION', 'REPORT_DEVICE_INFO',
] as const;
export type SupportCommandType = (typeof SUPPORT_COMMAND_TYPES)[number];
export const SUPPORT_COMMAND_STATUSES = ['REQUESTED', 'SENT', 'ACKNOWLEDGED', 'SUCCEEDED', 'FAILED', 'EXPIRED'] as const;
export const SupportTarget = z.object({
  /** ACTOR = one bidder / staff actor id on the ring; ROLE = every socket in that role. */
  type: z.enum(['ACTOR', 'ROLE']),
  id: z.string().min(1).max(200),
});
export const SendSupportCommandBody = z.object({
  command: z.enum(SUPPORT_COMMAND_TYPES),
  target: SupportTarget,
  /** Shown to the person on the receiving end, verbatim. Required: no silent commands. */
  reason: z.string().min(1).max(500),
  /** Link the command to an incident (INC000184) so it shows on the incident's timeline. */
  incidentNumber: z.string().min(1).max(20).optional(),
});
export const OpsSupportCommand = z.object({
  id: Uuid,
  commandId: z.string(),
  command: z.enum(SUPPORT_COMMAND_TYPES),
  tenantSlug: z.string(),
  saleId: Uuid,
  ringId: z.string(),
  target: SupportTarget,
  requestedBy: z.string(),
  incidentNumber: z.string().nullable(),
  reason: z.string(),
  status: z.enum(SUPPORT_COMMAND_STATUSES),
  /** Sockets the ring delivered it to. 0 with status SENT means nobody matched. */
  deliveredTo: z.number().int(),
  resultSummary: z.string().nullable(),
  /** The client's sanitized bundle (identifiers and technical metadata only). */
  diagnostics: z.record(z.unknown()).nullable(),
  requestedAt: z.string(),
  sentAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});
export type OpsSupportCommand = z.infer<typeof OpsSupportCommand>;

// ---------------------------------------------------------------------------
// ADR-60 cut 8: the synthetic canary's record (brief section 75).
// ---------------------------------------------------------------------------
export const CANARY_STEP_STATUSES = ['PASSED', 'FAILED', 'SKIPPED'] as const;
export const CanaryStep = z.object({
  name: z.string().min(1).max(60),
  status: z.enum(CANARY_STEP_STATUSES),
  ms: z.number().int().nonnegative(),
  detail: z.string().max(1_000).nullable(),
});
export const CanaryRing = z.object({
  endpoint: z.string().max(500),
  /** The ring's OWN identity from /api/runtime (a laptop ring says "demo"). */
  tenantId: z.string().max(200),
  saleId: z.string().max(200),
  ringId: z.string().max(200),
  engineId: z.string().max(200).nullable(),
});
export const RecordCanaryRunBody = z.object({
  ring: CanaryRing,
  runner: z.string().min(1).max(200),
  startedAt: z.string(),
  finishedAt: z.string(),
  passed: z.boolean(),
  failedStep: z.string().max(60).nullable(),
  steps: z.array(CanaryStep).max(50),
  /** The runner's cadence, so "stale" is judged against it (null: one-off). */
  intervalMs: z.number().int().positive().nullable(),
});
export const OpsCanaryRun = RecordCanaryRunBody.extend({ id: Uuid, recordedAt: z.string() });
export type OpsCanaryRun = z.infer<typeof OpsCanaryRun>;

// ---------------------------------------------------------------------------
// Hardware Operations (ADR-63) - the wire shapes.
//
// GLOBAL, operator-owned. Ownership, custody, assignment and kit membership are
// separate; this cut carries ownership and custody. Every serialized asset view
// denormalizes its catalog and location for a readable list; the detail adds
// the append-only event stream, the location history, and the status
// transitions the lifecycle currently allows (drawn from the same
// hardware-contract table the API refuses illegal steps with).
// ---------------------------------------------------------------------------

/** A wire date without a time (purchase, warranty): YYYY-MM-DD, or null. */
const HwDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const HardwareCatalogItem = z.object({
  id: Uuid,
  sku: z.string(),
  name: z.string(),
  description: z.string(),
  category: HardwareCategorySchema,
  subcategory: z.string().nullable(),
  trackingType: TrackingTypeSchema,
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  manufacturerPartNumber: z.string().nullable(),
  defaultOwnerType: OwnerTypeSchema,
  active: z.boolean(),
  replacementSku: z.string().nullable(),
  supersededBySku: z.string().nullable(),
  unitCostCents: z.number().int().nullable(),
  suggestedSalePriceCents: z.number().int().nullable(),
  suggestedRentalPriceCents: z.number().int().nullable(),
  warrantyMonths: z.number().int().nullable(),
  notes: z.string(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HardwareCatalogItem = z.infer<typeof HardwareCatalogItem>;

/** A stock line for a quantity SKU at one location; `available` is derived. */
export const HardwareInventoryLine = z.object({
  id: Uuid,
  locationId: Uuid,
  locationName: z.string(),
  onHand: z.number().int(),
  reserved: z.number().int(),
  available: z.number().int(),
  reorderThreshold: z.number().int(),
  reorderQuantity: z.number().int(),
  lowStock: z.boolean(),
  updatedAt: z.string(),
});
export type HardwareInventoryLine = z.infer<typeof HardwareInventoryLine>;

/** The catalog list row: the item plus the count of what exists against it. */
export const HardwareCatalogRow = HardwareCatalogItem.extend({
  /** Serialized items: how many assets exist. */
  assetCount: z.number().int(),
  /** Quantity items: total on hand and available across locations. */
  totalOnHand: z.number().int(),
  totalAvailable: z.number().int(),
  lowStock: z.boolean(),
});
export type HardwareCatalogRow = z.infer<typeof HardwareCatalogRow>;

export const HardwareCatalogItemDetail = HardwareCatalogRow.extend({
  inventory: z.array(HardwareInventoryLine),
});
export type HardwareCatalogItemDetail = z.infer<typeof HardwareCatalogItemDetail>;

export const HardwareLocation = z.object({
  id: Uuid,
  locationType: LocationTypeSchema,
  name: z.string(),
  tenantId: Uuid.nullable(),
  tenantSlug: z.string().nullable(),
  tenantName: z.string().nullable(),
  saleId: Uuid.nullable(),
  address: z.string().nullable(),
  active: z.boolean(),
  notes: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HardwareLocation = z.infer<typeof HardwareLocation>;

/** The locations list row: a location plus what is sitting there now. */
export const HardwareLocationRow = HardwareLocation.extend({
  assetCount: z.number().int(),
  inventoryLines: z.number().int(),
});
export type HardwareLocationRow = z.infer<typeof HardwareLocationRow>;

/** A serialized asset, with its catalog and location denormalized for display. */
export const HardwareAsset = z.object({
  id: Uuid,
  assetTag: z.string(),
  catalogItemId: Uuid,
  catalogSku: z.string(),
  catalogName: z.string(),
  category: HardwareCategorySchema,
  serialNumber: z.string().nullable(),
  manufacturerSerialNumber: z.string().nullable(),
  status: AssetStatusSchema,
  ownerType: OwnerTypeSchema,
  ownerTenantId: Uuid.nullable(),
  ownerTenantSlug: z.string().nullable(),
  ownerTenantName: z.string().nullable(),
  ownerExternalName: z.string().nullable(),
  condition: AssetConditionSchema,
  currentLocationId: Uuid.nullable(),
  currentLocationName: z.string().nullable(),
  purchaseDate: z.string().nullable(),
  purchaseCostCents: z.number().int().nullable(),
  warrantyStart: z.string().nullable(),
  warrantyEnd: z.string().nullable(),
  notes: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type HardwareAsset = z.infer<typeof HardwareAsset>;

export const HardwareAssetEvent = z.object({
  id: Uuid,
  assetId: Uuid,
  eventType: AssetEventTypeSchema,
  actorType: z.string(),
  actor: z.string(),
  summary: z.string(),
  payload: z.unknown(),
  at: z.string(),
});
export type HardwareAssetEvent = z.infer<typeof HardwareAssetEvent>;

export const HardwareLocationMove = z.object({
  id: Uuid,
  fromLocationId: Uuid.nullable(),
  fromLocationName: z.string().nullable(),
  toLocationId: Uuid,
  toLocationName: z.string(),
  reason: z.string(),
  movedBy: z.string(),
  movedAt: z.string(),
});
export type HardwareLocationMove = z.infer<typeof HardwareLocationMove>;

/** A maintenance or repair event on an asset or a kit (Cut 4). */
export const MaintenanceEvent = z.object({
  id: Uuid,
  targetKind: z.enum(['ASSET', 'KIT']),
  targetId: Uuid,
  targetRef: z.string(),
  targetName: z.string(),
  assetId: Uuid.nullable(),
  kitInstanceId: Uuid.nullable(),
  eventType: MaintenanceEventTypeSchema,
  status: MaintenanceStatusSchema,
  open: z.boolean(),
  problemDescription: z.string(),
  diagnosis: z.string().nullable(),
  resolution: z.string().nullable(),
  vendor: z.string().nullable(),
  costCents: z.number().int().nullable(),
  partsUsed: z.string().nullable(),
  openedAt: z.string(),
  completedAt: z.string().nullable(),
  createdBy: z.string(),
  completedBy: z.string().nullable(),
});
export type MaintenanceEvent = z.infer<typeof MaintenanceEvent>;

/**
 * An assignment: a tenant's (or department's) right to use an asset or a kit.
 * Denormalized with the target and recipient resolved. The target is exactly
 * one of an asset or a kit; `assignmentType` is the relationship, never the
 * ownership source of truth.
 */
export const HardwareAssignment = z.object({
  id: Uuid,
  targetKind: z.enum(['ASSET', 'KIT']),
  targetId: Uuid,
  targetRef: z.string(),
  targetName: z.string(),
  assetId: Uuid.nullable(),
  kitInstanceId: Uuid.nullable(),
  tenantId: Uuid.nullable(),
  tenantSlug: z.string().nullable(),
  tenantName: z.string().nullable(),
  internalDepartment: z.string().nullable(),
  assignmentType: AssignmentTypeSchema,
  status: AssignmentStatusSchema,
  startAt: z.string(),
  expectedReturnAt: z.string().nullable(),
  actualReturnAt: z.string().nullable(),
  returnRequired: z.boolean(),
  billingMode: BillingModeSchema,
  billingAmountCents: z.number().int().nullable(),
  billingInterval: z.string().nullable(),
  deploymentLocationId: Uuid.nullable(),
  deploymentLocationName: z.string().nullable(),
  notes: z.string(),
  returnCondition: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  closedBy: z.string().nullable(),
  closedAt: z.string().nullable(),
});
export type HardwareAssignment = z.infer<typeof HardwareAssignment>;

/** One inspection run against an asset or a kit, with its computed result (Cut 5). */
export const InspectionRun = z.object({
  id: Uuid,
  targetKind: z.enum(['ASSET', 'KIT']),
  targetId: Uuid,
  targetRef: z.string(),
  targetName: z.string(),
  assetId: Uuid.nullable(),
  kitInstanceId: Uuid.nullable(),
  templateId: Uuid,
  templateName: z.string(),
  result: InspectionResultSchema,
  notes: z.string(),
  performedBy: z.string(),
  performedAt: z.string(),
});
export type InspectionRun = z.infer<typeof InspectionRun>;

export const InspectionRunItem = z.object({
  id: Uuid,
  label: z.string(),
  required: z.boolean(),
  outcome: InspectionItemOutcomeSchema,
  note: z.string().nullable(),
});
export type InspectionRunItem = z.infer<typeof InspectionRunItem>;

export const InspectionRunDetail = InspectionRun.extend({
  items: z.array(InspectionRunItem),
});
export type InspectionRunDetail = z.infer<typeof InspectionRunDetail>;

export const HardwareAssetDetail = HardwareAsset.extend({
  events: z.array(HardwareAssetEvent),
  locationHistory: z.array(HardwareLocationMove),
  /** The status changes the operator may make from here, from the lifecycle table. */
  allowedStatusTransitions: z.array(AssetStatusSchema),
  /** The open assignment, if any, and the full append-only assignment history. */
  currentAssignment: HardwareAssignment.nullable(),
  assignmentHistory: z.array(HardwareAssignment),
  /** Maintenance and repair events, newest first (Cut 4). */
  maintenance: z.array(MaintenanceEvent),
  /** Inspection runs, newest first (Cut 5). */
  inspections: z.array(InspectionRun),
});
export type HardwareAssetDetail = z.infer<typeof HardwareAssetDetail>;

/** One row of the inventory screen: a SKU at a location. */
export const HardwareInventoryLevel = z.object({
  id: Uuid,
  catalogItemId: Uuid,
  sku: z.string(),
  name: z.string(),
  category: HardwareCategorySchema,
  locationId: Uuid,
  locationName: z.string(),
  onHand: z.number().int(),
  reserved: z.number().int(),
  available: z.number().int(),
  reorderThreshold: z.number().int(),
  reorderQuantity: z.number().int(),
  lowStock: z.boolean(),
  updatedAt: z.string(),
});
export type HardwareInventoryLevel = z.infer<typeof HardwareInventoryLevel>;

export const HardwareLowStockRow = z.object({
  catalogItemId: Uuid,
  sku: z.string(),
  name: z.string(),
  locationId: Uuid,
  locationName: z.string(),
  available: z.number().int(),
  reorderThreshold: z.number().int(),
});

export const HardwareDashboard = z.object({
  assets: z.object({
    total: z.number().int(),
    /** status -> count, for the states that have any. */
    byStatus: z.record(z.number().int()),
    inRepair: z.number().int(),
    retired: z.number().int(),
  }),
  catalog: z.object({
    total: z.number().int(),
    serialized: z.number().int(),
    quantity: z.number().int(),
    active: z.number().int(),
  }),
  locations: z.number().int(),
  lowStock: z.array(HardwareLowStockRow),
  recentAssets: z.array(HardwareAsset),
  kits: z.object({
    total: z.number().int(),
    inService: z.number().int(),
    ready: z.number().int(),
    incomplete: z.number().int(),
  }),
  maintenance: z.object({
    open: z.number().int(),
  }),
});
export type HardwareDashboard = z.infer<typeof HardwareDashboard>;

// ---------------------------------------------------------------------------
// Hardware Operations Cut 2 (ADR-63) - kits.
//
// A template is a versioned BOM; an instance is a real kit with an HN-CODE
// identity built from one template version. Readiness is derived from
// membership against the BOM, never stored. Kit membership is the fourth
// relationship, separate from ownership, assignment and custody.
// ---------------------------------------------------------------------------

export const KitTemplate = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  version: z.string(),
  description: z.string(),
  kitType: z.string().nullable(),
  active: z.boolean(),
  isCurrentVersion: z.boolean(),
  caseCatalogItemId: Uuid.nullable(),
  documentationVersion: z.string().nullable(),
  notes: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KitTemplate = z.infer<typeof KitTemplate>;

export const KitTemplateComponent = z.object({
  id: Uuid,
  catalogItemId: Uuid,
  sku: z.string(),
  name: z.string(),
  category: HardwareCategorySchema,
  catalogTrackingType: TrackingTypeSchema,
  quantityRequired: z.number().int(),
  required: z.boolean(),
  componentRole: z.string().nullable(),
  trackingRequirement: TrackingRequirementSchema,
  displayOrder: z.number().int(),
  notes: z.string(),
});
export type KitTemplateComponent = z.infer<typeof KitTemplateComponent>;

export const KitTemplateRow = KitTemplate.extend({
  componentCount: z.number().int(),
  instanceCount: z.number().int(),
});
export type KitTemplateRow = z.infer<typeof KitTemplateRow>;

export const KitTemplateDetail = KitTemplateRow.extend({
  components: z.array(KitTemplateComponent),
  /** True once an instance has been built from this version: the BOM is frozen. */
  frozen: z.boolean(),
});
export type KitTemplateDetail = z.infer<typeof KitTemplateDetail>;

export const KitInstance = z.object({
  id: Uuid,
  kitCode: z.string(),
  kitTemplateId: Uuid,
  templateCode: z.string(),
  templateVersion: z.string(),
  templateName: z.string(),
  status: KitInstanceStatusSchema,
  readiness: KitReadinessSchema,
  ownerType: OwnerTypeSchema,
  ownerTenantId: Uuid.nullable(),
  ownerTenantSlug: z.string().nullable(),
  ownerTenantName: z.string().nullable(),
  ownerExternalName: z.string().nullable(),
  currentLocationId: Uuid.nullable(),
  currentLocationName: z.string().nullable(),
  assemblyDate: z.string().nullable(),
  assembledBy: z.string().nullable(),
  notes: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type KitInstance = z.infer<typeof KitInstance>;

export const KitMemberAsset = z.object({
  membershipId: Uuid,
  assetId: Uuid,
  assetTag: z.string(),
  catalogItemId: Uuid,
  sku: z.string(),
  name: z.string(),
  category: HardwareCategorySchema,
  status: AssetStatusSchema,
  role: z.string().nullable(),
  installedAt: z.string(),
});
export type KitMemberAsset = z.infer<typeof KitMemberAsset>;

/** One BOM line's fulfillment in an instance: what the template asks and what the kit has. */
export const KitBomLine = z.object({
  catalogItemId: Uuid,
  sku: z.string(),
  name: z.string(),
  category: HardwareCategorySchema,
  requirement: TrackingRequirementSchema,
  /** How the line is actually filled, once EITHER is resolved by the catalog item. */
  effectiveMode: z.enum(['SERIALIZED', 'QUANTITY']),
  required: z.boolean(),
  requiredQuantity: z.number().int(),
  actualQuantity: z.number().int(),
  met: z.boolean(),
});
export type KitBomLine = z.infer<typeof KitBomLine>;

export const KitEvent = z.object({
  id: Uuid,
  kitInstanceId: Uuid,
  eventType: z.string(),
  actorType: z.string(),
  actor: z.string(),
  summary: z.string(),
  payload: z.unknown(),
  at: z.string(),
});
export type KitEvent = z.infer<typeof KitEvent>;

export const KitInstanceDetail = KitInstance.extend({
  memberAssets: z.array(KitMemberAsset),
  bom: z.array(KitBomLine),
  missing: z.array(KitBomLine),
  allowedTransitions: z.array(KitInstanceStatusSchema),
  events: z.array(KitEvent),
  currentAssignment: HardwareAssignment.nullable(),
  assignmentHistory: z.array(HardwareAssignment),
  maintenance: z.array(MaintenanceEvent),
  inspections: z.array(InspectionRun),
});
export type KitInstanceDetail = z.infer<typeof KitInstanceDetail>;

// -- kit request bodies -----------------------------------------------------

const KitComponentInput = z.object({
  catalogItemId: Uuid,
  quantityRequired: z.number().int().nonnegative().optional(),
  required: z.boolean().optional(),
  componentRole: z.string().max(100).optional(),
  trackingRequirement: TrackingRequirementSchema.optional(),
  displayOrder: z.number().int().optional(),
  notes: z.string().max(2_000).optional(),
}).strict();

export const CreateKitTemplateBody = z.object({
  code: z.string().min(1).max(16).regex(/^[A-Z][A-Z0-9]*$/),
  name: z.string().min(1).max(200),
  version: z.string().min(1).max(40),
  description: z.string().max(20_000).optional(),
  kitType: z.string().max(100).optional(),
  caseCatalogItemId: Uuid.optional(),
  documentationVersion: z.string().max(100).optional(),
  isCurrentVersion: z.boolean().optional(),
  notes: z.string().max(20_000).optional(),
  components: z.array(KitComponentInput).max(200).optional(),
}).strict();

export const UpdateKitTemplateBody = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(20_000).optional(),
  kitType: z.string().max(100).nullable().optional(),
  caseCatalogItemId: Uuid.nullable().optional(),
  documentationVersion: z.string().max(100).nullable().optional(),
  active: z.boolean().optional(),
  isCurrentVersion: z.boolean().optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const SetKitTemplateComponentsBody = z.object({
  components: z.array(KitComponentInput).max(200),
}).strict();

export const CreateKitInstanceBody = z.object({
  kitTemplateId: Uuid,
  ownerType: OwnerTypeSchema.optional(),
  ownerTenantId: Uuid.optional(),
  ownerExternalName: z.string().max(200).optional(),
  currentLocationId: Uuid.optional(),
  assemblyDate: HwDate.optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const SetKitStatusBody = z.object({
  status: KitInstanceStatusSchema,
  note: z.string().max(5_000).optional(),
}).strict();

export const AddKitAssetBody = z.object({
  assetId: z.string().min(1).max(200),
  role: z.string().max(100).optional(),
}).strict();

export const RemoveKitAssetBody = z.object({
  assetId: z.string().min(1).max(200),
  reason: z.string().max(2_000).optional(),
}).strict();

export const SetKitComponentBody = z.object({
  catalogItemId: Uuid,
  actualQuantity: z.number().int().nonnegative(),
}).strict();

// -- request bodies ---------------------------------------------------------

export const CreateHardwareCatalogItemBody = z.object({
  sku: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  description: z.string().max(20_000).optional(),
  category: HardwareCategorySchema,
  subcategory: z.string().max(100).optional(),
  trackingType: TrackingTypeSchema,
  manufacturer: z.string().max(200).optional(),
  model: z.string().max(200).optional(),
  manufacturerPartNumber: z.string().max(200).optional(),
  defaultOwnerType: OwnerTypeSchema.optional(),
  replacementSku: z.string().max(100).optional(),
  supersededBySku: z.string().max(100).optional(),
  unitCostCents: Cents.optional(),
  suggestedSalePriceCents: Cents.optional(),
  suggestedRentalPriceCents: Cents.optional(),
  warrantyMonths: z.number().int().nonnegative().optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const UpdateHardwareCatalogItemBody = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(20_000).optional(),
  category: HardwareCategorySchema.optional(),
  subcategory: z.string().max(100).nullable().optional(),
  manufacturer: z.string().max(200).nullable().optional(),
  model: z.string().max(200).nullable().optional(),
  manufacturerPartNumber: z.string().max(200).nullable().optional(),
  defaultOwnerType: OwnerTypeSchema.optional(),
  active: z.boolean().optional(),
  replacementSku: z.string().max(100).nullable().optional(),
  supersededBySku: z.string().max(100).nullable().optional(),
  unitCostCents: Cents.nullable().optional(),
  suggestedSalePriceCents: Cents.nullable().optional(),
  suggestedRentalPriceCents: Cents.nullable().optional(),
  warrantyMonths: z.number().int().nonnegative().nullable().optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const CreateHardwareAssetBody = z.object({
  catalogItemId: Uuid,
  serialNumber: z.string().max(200).optional(),
  manufacturerSerialNumber: z.string().max(200).optional(),
  status: AssetStatusSchema.optional(),
  ownerType: OwnerTypeSchema.optional(),
  ownerTenantId: Uuid.optional(),
  ownerExternalName: z.string().max(200).optional(),
  condition: AssetConditionSchema.optional(),
  currentLocationId: Uuid.optional(),
  purchaseDate: HwDate.optional(),
  purchaseCostCents: Cents.optional(),
  warrantyStart: HwDate.optional(),
  warrantyEnd: HwDate.optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const UpdateHardwareAssetBody = z.object({
  serialNumber: z.string().max(200).nullable().optional(),
  manufacturerSerialNumber: z.string().max(200).nullable().optional(),
  ownerType: OwnerTypeSchema.optional(),
  ownerTenantId: Uuid.nullable().optional(),
  ownerExternalName: z.string().max(200).nullable().optional(),
  condition: AssetConditionSchema.optional(),
  purchaseDate: HwDate.nullable().optional(),
  purchaseCostCents: Cents.nullable().optional(),
  warrantyStart: HwDate.nullable().optional(),
  warrantyEnd: HwDate.nullable().optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const SetHardwareAssetStatusBody = z.object({
  status: AssetStatusSchema,
  note: z.string().max(5_000).optional(),
}).strict();

export const MoveHardwareAssetBody = z.object({
  toLocationId: Uuid,
  reason: z.string().max(2_000).optional(),
}).strict();

export const CreateHardwareLocationBody = z.object({
  locationType: LocationTypeSchema,
  name: z.string().min(1).max(200),
  tenantSlug: z.string().min(1).max(100).optional(),
  saleId: Uuid.optional(),
  address: z.string().max(2_000).optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const UpdateHardwareLocationBody = z.object({
  name: z.string().min(1).max(200).optional(),
  tenantSlug: z.string().min(1).max(100).nullable().optional(),
  saleId: Uuid.nullable().optional(),
  address: z.string().max(2_000).nullable().optional(),
  active: z.boolean().optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const SetHardwareInventoryBody = z.object({
  catalogItemId: Uuid,
  locationId: Uuid,
  quantityOnHand: z.number().int().nonnegative(),
  quantityReserved: z.number().int().nonnegative().optional(),
  reorderThreshold: z.number().int().nonnegative().optional(),
  reorderQuantity: z.number().int().nonnegative().optional(),
}).strict();

/** One thing a tenant owns outright (owner is the tenant), for the tenant view. */
export const TenantOwnedItem = z.object({
  kind: z.enum(['ASSET', 'KIT']),
  id: Uuid,
  ref: z.string(),
  name: z.string(),
  status: z.string(),
});
export type TenantOwnedItem = z.infer<typeof TenantOwnedItem>;

/** A tenant's hardware (design section 35): what it is using now, and what it owns. */
export const TenantHardware = z.object({
  tenantSlug: z.string(),
  tenantName: z.string(),
  assignments: z.array(HardwareAssignment),
  owned: z.array(TenantOwnedItem),
});
export type TenantHardware = z.infer<typeof TenantHardware>;

export const AssignHardwareBody = z.object({
  /** Exactly one target: an asset (id or HN-AST tag) or a kit (id or HN-CODE). */
  assetId: z.string().min(1).max(200).optional(),
  kitId: z.string().min(1).max(200).optional(),
  /** At least one recipient. */
  tenantSlug: z.string().min(1).max(100).optional(),
  internalDepartment: z.string().min(1).max(200).optional(),
  assignmentType: AssignmentTypeSchema,
  expectedReturnAt: IsoDate.optional(),
  returnRequired: z.boolean().optional(),
  billingMode: BillingModeSchema.optional(),
  billingAmountCents: Cents.optional(),
  billingInterval: z.string().max(100).optional(),
  deploymentLocationId: Uuid.optional(),
  /** Also move the item's custody to the deployment location in the same act. */
  moveCustody: z.boolean().optional(),
  notes: z.string().max(20_000).optional(),
}).strict();

export const ReturnHardwareBody = z.object({
  returnCondition: AssetConditionSchema.optional(),
  /** Where the item goes back to (moves custody, if given). */
  toLocationId: Uuid.optional(),
  notes: z.string().max(5_000).optional(),
}).strict();

export const OpenMaintenanceBody = z.object({
  /** Exactly one target: an asset (id or HN-AST tag) or a kit (id or HN-CODE). */
  assetId: z.string().min(1).max(200).optional(),
  kitId: z.string().min(1).max(200).optional(),
  eventType: MaintenanceEventTypeSchema,
  problemDescription: z.string().max(20_000).optional(),
  vendor: z.string().max(200).optional(),
}).strict();

export const UpdateMaintenanceBody = z.object({
  status: MaintenanceStatusSchema.optional(),
  problemDescription: z.string().max(20_000).optional(),
  diagnosis: z.string().max(20_000).nullable().optional(),
  resolution: z.string().max(20_000).nullable().optional(),
  vendor: z.string().max(200).nullable().optional(),
  costCents: Cents.nullable().optional(),
  partsUsed: z.string().max(20_000).nullable().optional(),
}).strict();

// ---------------------------------------------------------------------------
// Hardware Operations Cut 5 (ADR-63 Phase 2) - inspection templates and runs.
// ---------------------------------------------------------------------------

export const InspectionTemplate = z.object({
  id: Uuid,
  name: z.string(),
  description: z.string(),
  active: z.boolean(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type InspectionTemplate = z.infer<typeof InspectionTemplate>;

export const InspectionTemplateItem = z.object({
  id: Uuid,
  label: z.string(),
  required: z.boolean(),
  displayOrder: z.number().int(),
});
export type InspectionTemplateItem = z.infer<typeof InspectionTemplateItem>;

export const InspectionTemplateRow = InspectionTemplate.extend({
  itemCount: z.number().int(),
  runCount: z.number().int(),
});
export type InspectionTemplateRow = z.infer<typeof InspectionTemplateRow>;

export const InspectionTemplateDetail = InspectionTemplateRow.extend({
  items: z.array(InspectionTemplateItem),
});
export type InspectionTemplateDetail = z.infer<typeof InspectionTemplateDetail>;

const InspectionItemInput = z.object({
  label: z.string().min(1).max(300),
  required: z.boolean().optional(),
  displayOrder: z.number().int().optional(),
}).strict();

export const CreateInspectionTemplateBody = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(20_000).optional(),
  items: z.array(InspectionItemInput).max(200).optional(),
}).strict();

export const UpdateInspectionTemplateBody = z.object({
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(20_000).optional(),
  active: z.boolean().optional(),
}).strict();

export const SetInspectionTemplateItemsBody = z.object({
  items: z.array(InspectionItemInput).max(200),
}).strict();

export const CreateInspectionRunBody = z.object({
  templateId: Uuid,
  /** Exactly one target: an asset (id or HN-AST tag) or a kit (id or HN-CODE). */
  assetId: z.string().min(1).max(200).optional(),
  kitId: z.string().min(1).max(200).optional(),
  notes: z.string().max(20_000).optional(),
  items: z.array(z.object({
    label: z.string().min(1).max(300),
    required: z.boolean(),
    outcome: InspectionItemOutcomeSchema,
    note: z.string().max(2_000).optional(),
  })).min(1).max(200),
}).strict();

// Annie phone-number provisioning (P0 SMS/MMS): the destination-number -> tenant
// mapping. Operator authority, never MCP-derivable (an agent must not reassign a
// tenant's Annie number).
export const TenantPhoneCapabilities = z.object({ sms: z.boolean(), mms: z.boolean(), voice: z.boolean() });
export const PlatformTenantPhoneNumber = z.object({
  phoneE164: z.string(),
  provider: z.string(),
  capabilities: TenantPhoneCapabilities.partial(),
  label: z.string(),
  status: z.string(),
  createdAt: z.number(),
});
export type PlatformTenantPhoneNumberView = z.infer<typeof PlatformTenantPhoneNumber>;
export const AssignTenantPhoneNumberBody = z.object({
  phoneE164: z.string().regex(/^\+[1-9]\d{6,14}$/),
  provider: z.enum(['twilio', 'aws', 'xai', 'mock']),
  capabilities: TenantPhoneCapabilities.partial().optional(),
  label: z.string().min(1).max(80).optional(),
  status: z.enum(['active', 'inactive']).optional(),
}).strict();

/** ADR-62 cutover: flip one flow's LEGACY -> SHADOW -> FLOW state for a tenant. */
export const FlowMigrationStateBody = z.object({
  state: z.enum(['LEGACY', 'SHADOW', 'FLOW']),
  reason: z.string().max(500).optional(),
}).strict();
export const FlowMigrationStateResult = z.object({
  tenantSlug: z.string(),
  flowKey: z.string(),
  previousState: z.string(),
  state: z.string(),
});

/**
 * ADR-72: does this tenant demand an authenticator app (TOTP) of its staff?
 * 'optional' (the default): a password signs staff in; anyone may turn TOTP
 * on for their own account. 'required': the pre-ADR-72 rule for everyone at
 * the company. Platform operators are always required, whatever this says.
 */
export const StaffMfaPolicy = z.enum(['optional', 'required']);
export type StaffMfaPolicy = z.infer<typeof StaffMfaPolicy>;
export const StaffMfaBody = z.object({ staffMfa: StaffMfaPolicy }).strict();
export const StaffMfaResult = z.object({ tenantSlug: z.string(), staffMfa: StaffMfaPolicy });

/**
 * GET /auth/staff-requirements (bearer): what the sign-in page needs after a
 * password sign-in - is this person already enrolled, and must they be?
 * `required` is true for a platform operator or for a group in any tenant
 * whose policy is 'required'; only then is enrollment forced.
 */
export const StaffRequirements = z.object({
  enrolled: z.boolean(),
  required: z.boolean(),
  operator: z.boolean(),
  tenants: z.array(z.object({ id: Uuid, slug: z.string(), name: z.string(), staffMfa: StaffMfaPolicy })),
});
export type StaffRequirements = z.infer<typeof StaffRequirements>;

/** ADR-62: the Super Admin toggle for the consignment approval-link automation. */
export const ConsignmentAutomationBody = z.object({
  autoApprovalLinks: z.boolean(),
  approvalReminderHours: z.number().int().min(1).max(168).optional(),
}).strict();
export const ConsignmentAutomationResult = z.object({
  tenantSlug: z.string(),
  autoApprovalLinks: z.boolean(),
  approvalReminderHours: z.number(),
});

/** ADR-70 cut 6e: the Super Admin switch for operator max-bid visibility;
 *  6f: or for operator intent signals, with any of its controls (a control
 *  not named keeps what it was). One capability per call. */
export const TenantCapabilitiesBody = z.union([
  z.object({
    operatorMaxBidVisibility: z.boolean(),
  }).strict(),
  z.object({
    operatorIntentSignals: z.boolean(),
    intentControls: IntentControls.partial().optional(),
  }).strict(),
]);
/** What the switch did, and what each live ring of the tenant was told.
 *  `rings.skipped`: a sale whose ring is not up yet (go-live tells it);
 *  `rings.failed`: a ring that could not be reached (the next change or
 *  go-live tells it). */
export const TenantCapabilitiesResult = z.object({
  tenantSlug: z.string(),
  capabilities: TenantCapabilities,
  changed: z.boolean(),
  rings: z.object({ sent: z.number().int(), skipped: z.number().int(), failed: z.number().int() }),
});

export interface PlatformRouteDef<Req extends z.ZodTypeAny, Res extends z.ZodTypeAny> {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Absolute path - NOT prefixed by /t/:tenantSlug. `:name` segments are params. */
  readonly path: string;
  readonly request: Req;
  readonly response: Res;
  readonly summary: string;
}
const pr = <Req extends z.ZodTypeAny, Res extends z.ZodTypeAny>(d: PlatformRouteDef<Req, Res>) => d;

export const PLATFORM_ROUTES = {
  ...FLOW_OBSERVATION_ROUTES,
  listTenants: pr({ method: 'GET', path: '/platform/tenants', request: NoBody, response: z.array(PlatformTenantSummary), summary: 'Every tenant with lifecycle status and per-tenant counts.' }),
  getTenant: pr({ method: 'GET', path: '/platform/tenants/:slug', request: NoBody, response: PlatformTenantDetail, summary: 'One tenant: identity, lifecycle, parsed settings, Annie on/off, counts.' }),
  createTenant: pr({ method: 'POST', path: '/platform/tenants', request: CreateTenantBody, response: z.object({ tenant: PlatformTenant, retry: z.string().optional() }), summary: 'Create a tenant (PROVISIONING then READY/FAILED); optionally invite its first admin.' }),
  provisionTenant: pr({ method: 'POST', path: '/platform/tenants/:slug/provision', request: EmptyBody, response: z.object({ tenant: PlatformTenant, note: z.string().optional() }), summary: 'Retry provisioning for a FAILED or stuck tenant (idempotent).' }),
  setTenantStatus: pr({ method: 'POST', path: '/platform/tenants/:slug/status', request: SetTenantStatusBody, response: PlatformTenant, summary: 'Set tenant lifecycle: active | suspended | archived.' }),
  setTenantSlug: pr({ method: 'POST', path: '/platform/tenants/:slug/slug', request: SetTenantSlugBody, response: PlatformTenant, summary: "Move a tenant to a new slug: its /t/:slug routes, admin ?t= link, platform URL and <slug>.hipnum.com public host change together in one transaction. The id, staff, sales and media stay. Refuses a reserved or malformed label, a slug or host another tenant holds, and the 'deleted-' prefix. Operator authority, audited as tenant.slug." }),
  tenantDeletionPreview: pr({ method: 'GET', path: '/platform/tenants/:slug/deletion-preview', request: NoBody, response: TenantDeletionPreview, summary: 'What deleting this tenant would switch off, free and keep, and anything that blocks it.' }),
  deleteTenant: pr({ method: 'DELETE', path: '/platform/tenants/:slug', request: DeleteTenantBody, response: TenantDeletionResult, summary: 'Delete a tenant: removed from every surface, slug and host freed, records kept. Terminal.' }),
  updateTenantSettings: pr({ method: 'PUT', path: '/platform/tenants/:slug/settings', request: PlatformUpdateSettingsBody, response: TenantSettingsView, summary: 'Replace tenant settings wholesale (and optionally rename), operator authority.' }),
  disableTenantBranding: pr({ method: 'POST', path: '/platform/tenants/:slug/branding/disable', request: EmptyBody, response: z.object({ changed: z.boolean(), brandingOn: z.boolean() }), summary: 'Turn OFF a tenant\'s company-default storefront branding (ADR-55), keeping its uploaded assets so the tenant can re-enable. Operator authority, audited, idempotent.' }),
  listTenantPhoneNumbers: pr({ method: 'GET', path: '/platform/tenants/:slug/phone-numbers', request: NoBody, response: z.array(PlatformTenantPhoneNumber), summary: "A tenant's Annie phone numbers (the destination-number -> tenant mapping Annie SMS/MMS resolves on inbound)." }),
  assignTenantPhoneNumber: pr({ method: 'POST', path: '/platform/tenants/:slug/phone-numbers', request: AssignTenantPhoneNumberBody, response: PlatformTenantPhoneNumber, summary: 'Assign an Annie phone number (E.164) to a tenant with provider and SMS/MMS/voice capabilities. Upsert on the number (one number per tenant); reassigning moves it. Operator authority, audited. This is the SMS/MMS provisioning path - not a migration.' }),
  listTenantHosts: pr({ method: 'GET', path: '/platform/tenants/:slug/hosts', request: NoBody, response: z.array(PlatformTenantHost), summary: "A tenant's public hosts with the brand shell each serves (ADR-69 Phase 3): the platform subdomain plus any custom host, canonical first." }),
  addTenantHost: pr({ method: 'POST', path: '/platform/tenants/:slug/hosts', request: AddTenantHostBody, response: PlatformTenantHost, summary: 'Add a CUSTOM public host to a tenant under a product shell (HipNumber or LotNumber). Refuses a malformed host, a platform host (those come from the slug) and a host another tenant holds. Records only: DNS and the Cloudflare custom domain stay the operator step. Operator authority, audited as tenant.host.' }),
  setFlowMigrationState: pr({ method: 'POST', path: '/platform/tenants/:slug/flows/:flowKey/migration-state', request: FlowMigrationStateBody, response: FlowMigrationStateResult, summary: 'Flip one flow\'s LEGACY -> SHADOW -> FLOW migration state for a tenant (ADR-62 cutover). Operator authority, audited. Resets the migration fence so a flow entering SHADOW does not replay history. The interim cutover lever until the full flow editor.' }),
  setConsignmentAutomation: pr({ method: 'POST', path: '/platform/tenants/:slug/consignment-automation', request: ConsignmentAutomationBody, response: ConsignmentAutomationResult, summary: 'Turn the consignment approval-link automation on or off for a tenant, and set the reminder window (ADR-62). When on, the worker texts the seller the listing-approval link once photos are complete, plus a reminder if unapproved after approvalReminderHours. Operator authority, audited. Stored on theme.annie.' }),
  setStaffMfa: pr({ method: 'POST', path: '/platform/tenants/:slug/staff-mfa', request: StaffMfaBody, response: StaffMfaResult, summary: "Set whether this tenant's staff must sign in with an authenticator app (ADR-72). optional (the default) lets a password sign staff in and leaves TOTP for each person to turn on; required restores the enroll-then-activate ceremony for everyone at the company. Operator authority, audited; takes effect within a minute." }),
  setTenantCapabilities: pr({ method: 'POST', path: '/platform/tenants/:slug/capabilities', request: TenantCapabilitiesBody, response: TenantCapabilitiesResult, summary: 'Turn a live-bidding capability on or off for a tenant (ADR-70 cut 6e): operator max-bid visibility lets the ring console show standing max bids on the current lot, an admin seeing bidder, amount and remaining capacity, a clerk seeing that one stands and whose. Or (cut 6f) operator intent signals: the console\'s Online interest panel (watchers, approved bidders present, a pointer on Bid, a confirmation open, a bid pressed, by paddle or not), each part a control of its own; never stored. Super Admin only; a tenant admin cannot turn either on. Audited as tenant.capabilities, and every max-bid view on the ring is recorded. Sent to every ring of the tenant that is up; a ring that starts later hears it at go-live.' }),
  listTenantSales: pr({ method: 'GET', path: '/platform/tenants/:slug/sales', request: NoBody, response: z.array(PlatformSaleRow), summary: "A tenant's sales with lot/registration counts, for the drill-in list." }),
  saleProvisioning: pr({ method: 'GET', path: '/platform/tenants/:slug/sales/:saleId/provisioning', request: NoBody, response: PlatformSaleProvisioning, summary: 'The three-source auction-format truth for one sale (ADR-48): requested vs provisioned vs observed, with generation and status.' }),
  listTenantStaff: pr({ method: 'GET', path: '/platform/tenants/:slug/staff', request: NoBody, response: z.array(PlatformStaffMember), summary: "A tenant's staff with TOTP enrollment and activation state." }),
  inviteStaff: pr({ method: 'POST', path: '/platform/tenants/:slug/staff/invite', request: PlatformInviteStaffBody, response: z.object({ username: z.string(), created: z.boolean(), role: StaffRole, tenantId: Uuid }), summary: 'Invite a staff member into a tenant (creates the Cognito user, adds the role group).' }),
  createStaff: pr({ method: 'POST', path: '/platform/tenants/:slug/staff', request: PlatformCreateStaffBody, response: z.object({ username: z.string(), email: z.string(), role: StaffRole, tenantId: Uuid, mustChangePassword: z.boolean() }), summary: 'Create a staff member with a password the operator chose and add the role group; nothing is emailed. With mustChangePassword the person picks their own password at first sign-in. Refuses an email that already has an account (invite that person instead, then set their password). The password is never audited.' }),
  setStaffPassword: pr({ method: 'POST', path: '/platform/tenants/:slug/staff/:username/password', request: PlatformSetStaffPasswordBody, response: z.object({ username: z.string(), mustChangePassword: z.boolean() }), summary: "Replace a staff member's password (operator reset), optionally making it temporary so they choose their own at first sign-in. The password is never audited." }),
  setStaffRoles: pr({ method: 'POST', path: '/platform/tenants/:slug/staff/:username/roles', request: SetStaffRolesBody, response: z.object({ username: z.string(), add: z.array(StaffRole), remove: z.array(StaffRole) }), summary: 'Add and/or remove tenant role groups for a staff member.' }),
  activateStaff: pr({ method: 'POST', path: '/platform/tenants/:slug/staff/:username/activate', request: EmptyBody, response: z.object({ username: z.string(), activatedAt: z.number() }), summary: 'Activate a staff member who has enrolled TOTP (operator override).' }),
  deactivateStaff: pr({ method: 'POST', path: '/platform/tenants/:slug/staff/:username/deactivate', request: EmptyBody, response: z.object({ username: z.string(), deactivated: z.literal(true) }), summary: 'Remove staff authority: clear activation and all role groups.' }),
  findAccounts: pr({ method: 'GET', path: '/platform/accounts', request: NoBody, response: z.array(PlatformAccount), summary: 'Global account search by email substring (>=3 chars) across all tenants.' }),
  audit: pr({ method: 'GET', path: '/platform/audit', request: NoBody, response: z.array(PlatformAuditRow), summary: 'The platform operator audit trail, newest first.' }),
  // AI workers (ADR-41). Declared for completeness; the console operations board
  // drives these via raw fetch, so their shapes stay loose here. Being outside
  // `routes` is what makes "an AI agent administers AI workers" structurally
  // impossible, not merely forbidden.
  aiWorkers: pr({ method: 'GET', path: '/platform/ai-workers', request: NoBody, response: z.unknown(), summary: 'AI worker roster.' }),
  aiWorkerProviders: pr({ method: 'GET', path: '/platform/ai-workers/providers', request: NoBody, response: z.unknown(), summary: 'Available AI providers.' }),
  aiWorkerStatus: pr({ method: 'POST', path: '/platform/ai-workers/:slug/status', request: z.record(z.unknown()), response: z.unknown(), summary: 'Set an AI worker status.' }),
  aiWorkerCapabilities: pr({ method: 'POST', path: '/platform/ai-workers/:slug/capabilities', request: z.record(z.unknown()), response: z.unknown(), summary: 'Set an AI worker capabilities.' }),
  aiWorkerRun: pr({ method: 'POST', path: '/platform/ai-workers/:slug/run', request: z.record(z.unknown()), response: z.unknown(), summary: 'Run an AI worker task.' }),
  aiWorkerRuns: pr({ method: 'GET', path: '/platform/ai-workers/runs', request: NoBody, response: z.unknown(), summary: 'Recent AI worker runs.' }),
  aiWorkerRunDetail: pr({ method: 'GET', path: '/platform/ai-workers/runs/:runId', request: NoBody, response: z.unknown(), summary: 'One AI worker run.' }),
  aiWorkerFindings: pr({ method: 'GET', path: '/platform/ai-workers/findings', request: NoBody, response: z.unknown(), summary: 'AI worker findings.' }),
  aiWorkerApprovals: pr({ method: 'GET', path: '/platform/ai-workers/approvals', request: NoBody, response: z.unknown(), summary: 'AI worker parked actions awaiting approval.' }),
  aiWorkerDecide: pr({ method: 'POST', path: '/platform/ai-workers/approvals/:actionId', request: z.record(z.unknown()), response: z.unknown(), summary: 'Approve or reject a parked AI worker action.' }),
  aiWorkerCosts: pr({ method: 'GET', path: '/platform/ai-workers/costs', request: NoBody, response: z.unknown(), summary: 'AI worker cost breakdown.' }),
  // Incident Operations (ADR-58) - the MUTATIONS. Reads are in DIAGNOSTIC_ROUTES.
  // Being here is what makes "an agent files or closes an incident"
  // structurally impossible in Phase 1, not merely forbidden.
  createIncident: pr({ method: 'POST', path: '/platform/ops/incidents', request: CreateIncidentBody, response: OpsIncident, summary: 'Open an incident (INC): title, severity, source, optional impacts. Numbered by the database.' }),
  recordCanaryRun: pr({
    method: 'POST', path: '/platform/ops/canary-runs', request: RecordCanaryRunBody, response: OpsCanaryRun,
    summary: 'ADR-60: record one synthetic canary run (every scripted step with its verdict). Operator or the CANARY_TOKEN bearer. The synthetic-canary probe judges the latest run per ring.',
  }),
  sendSupportCommand: pr({
    method: 'POST', path: '/platform/tenants/:slug/sales/:saleId/rings/:ringId/support-commands', request: SendSupportCommandBody, response: OpsSupportCommand,
    summary: 'ADR-60: send one allowlisted support command (refresh, reconnect, capture diagnostics, reload...) to a bidder or a role on a live ring, recorded and audited before it is sent, with the result if the client answered within a few seconds.',
  }),
  updateIncident: pr({ method: 'PATCH', path: '/platform/ops/incidents/:number', request: UpdateIncidentBody, response: OpsIncident, summary: 'Edit an incident\'s fields (severity, owner, commander, public impact, root cause, resolution). Every change lands on the timeline.' }),
  transitionIncident: pr({ method: 'POST', path: '/platform/ops/incidents/:number/transition', request: IncidentTransitionBody, response: OpsIncident, summary: 'Move an incident through its lifecycle. Refused (409) when the lifecycle does not allow the step.' }),
  createChange: pr({ method: 'POST', path: '/platform/ops/changes', request: CreateChangeBody, response: OpsChange, summary: 'Record a change (CHG): type, risk, plans, planned window, deployment SHA.' }),
  updateChange: pr({ method: 'PATCH', path: '/platform/ops/changes/:number', request: UpdateChangeBody, response: OpsChange, summary: 'Edit a change\'s fields and plans.' }),
  addChangeGate: pr({ method: 'POST', path: '/platform/ops/changes/:number/gates', request: AddChangeGateBody, response: OpsChangeGate, summary: 'ADR-61: record one gate evaluation on a change with the evidence that produced the verdict. Append-only; never a boolean in text.' }),
  approveChangeByPolicy: pr({ method: 'POST', path: '/platform/ops/changes/:number/approve-by-policy', request: EmptyBody, response: PolicyApprovalOutcome, summary: 'ADR-61 cut 4: ask the policy in force to approve a change AWAITING_APPROVAL. Granted only from an immutable snapshot (surface, policy version and evidence set pinned), only with no human line crossed, only when the case is READY within the risk ceiling, and only when the change engine is operational. The decision is recorded either way.' }),
  executeChange: pr({ method: 'POST', path: '/platform/ops/changes/:number/execute', request: ExecuteChangeBody, response: ChangeExecution, summary: 'ADR-61 cut 3: the first executor. One action family: set a ring\'s public snapshot wave policy and restart the ring between lots. Re-evaluates the case immediately before acting and refuses when the world has moved (bidding opened, a SEV1, the ring changed state, the policy version changed, a line crossed without a HUMAN approval). Records before and after, and writes REHEARSAL_CLEAN and, on a rollback, ROLLBACK_PROVEN from what it observed.' }),
  evaluateChangeGates: pr({ method: 'POST', path: '/platform/ops/changes/:number/gates/evaluate', request: EvaluateChangeGatesBody, response: EvaluatedChangeGates, summary: 'ADR-61 cut 2: the platform evaluates the gates it can read itself (the linked PR and its checks, the attached benchmark file, ring telemetry, the freeze, the guard, the blast radius against the policy, the human lines) and writes one immutable row per gate with what it observed and why. Reads only; approves nothing; executes nothing.' }),
  // ADR-61 cut 1, the governance plane. A separate identity (GOVERNANCE_APPROVER with a fresh step-up), a separate route, a separate database role. Never callable by the change engine.
  createPolicyVersion: pr({ method: 'POST', path: '/platform/governance/policies', request: CreatePolicyVersionBody, response: GovernancePolicyVersion, summary: 'Governance: append a new version of a policy (never edits one). Requires the governance grant and a fresh step-up; hash-chained audit.' }),
  registerPattern: pr({ method: 'POST', path: '/platform/governance/patterns', request: RegisterPatternBody, response: GovernancePattern, summary: 'Governance: register a standard-change pattern from a completed, validated change. Requires the governance grant and a fresh step-up; hash-chained audit.' }),
  transitionChange: pr({ method: 'POST', path: '/platform/ops/changes/:number/transition', request: ChangeTransitionBody, response: OpsChange, summary: 'Move a change through its lifecycle. NORMAL changes must pass approval; entering IMPLEMENTING is refused (409 change_frozen) while a SEV1 is active unless the change is an EMERGENCY linked to it, and refused (409 live_bidding_guard) while bidding is open on any sale unless overrideReason is given or the change is an EMERGENCY.' }),
  createRequest: pr({ method: 'POST', path: '/platform/ops/requests', request: CreateRequestBody, response: OpsRequest, summary: 'Log a request item (RITM): category, requester, the tenant it concerns, due date.' }),
  updateRequest: pr({ method: 'PATCH', path: '/platform/ops/requests/:number', request: UpdateRequestBody, response: OpsRequest, summary: 'Edit a request\'s fields.' }),
  transitionRequest: pr({ method: 'POST', path: '/platform/ops/requests/:number/transition', request: RequestTransitionBody, response: OpsRequest, summary: 'Move a request through its lifecycle.' }),
  addOpsNote: pr({ method: 'POST', path: '/platform/ops/records/:kind/:number/notes', request: OpsNoteBody, response: OpsTimelineEntry, summary: 'Append a note to a record\'s timeline (kind = incidents | changes | requests).' }),
  addOpsImpact: pr({ method: 'POST', path: '/platform/ops/records/:kind/:number/impacts', request: AddOpsImpactBody, response: OpsImpact, summary: 'Name a resource an incident or change touches (tenant, auction, ring, service, integration, ...).' }),
  removeOpsImpact: pr({ method: 'DELETE', path: '/platform/ops/records/:kind/:number/impacts/:impactId', request: EmptyBody, response: OpsRemoved, summary: 'Remove an impact row. Recorded on the timeline.' }),
  addOpsLink: pr({ method: 'POST', path: '/platform/ops/records/:kind/:number/links', request: AddOpsLinkBody, response: OpsLinkView, summary: 'Link a record to another record (by number), a GitHub issue or PR, a deployment SHA or a URL. Both ops records get a timeline entry.' }),
  removeOpsLink: pr({ method: 'DELETE', path: '/platform/ops/records/:kind/:number/links/:linkId', request: EmptyBody, response: OpsRemoved, summary: 'Remove a link from either end. Recorded on both timelines.' }),
  publishIncidentStatus: pr({ method: 'POST', path: '/platform/ops/incidents/:number/public', request: PublishStatusBody, response: OpsPublicStatus, summary: 'Publish (or update) the public projection of an incident to status.hipnum.com: components, impact, state, and a message that defaults to the deterministic template. The push and its outcome are recorded on the incident.' }),
  createWarRoom: pr({ method: 'POST', path: '/platform/ops/incidents/:number/war-room', request: EmptyBody, response: OpsIncident, summary: 'Create the Slack war room for an incident that did not get one at open (a SEV3 that grew, or Slack was down). Idempotent. 409 when Slack is not configured.' }),
  createGithubIssue: pr({ method: 'POST', path: '/platform/ops/incidents/:number/github-issue', request: EmptyBody, response: OpsLinkView, summary: 'Open a GitHub engineering issue from the incident (summary, impact, latest diagnostics, record link) and link it ENGINEERING_ISSUE. One per incident; 409 when one exists or GitHub is not configured.' }),
  runIncidentDiagnostics: pr({ method: 'POST', path: '/platform/ops/incidents/:number/diagnostics', request: EmptyBody, response: OpsTimelineEntry, summary: 'Run the diagnostics overview now, plus a full ring diagnosis for every auction and ring this incident names, and keep the snapshot on the timeline as a DIAGNOSTIC entry. Reads only; changes nothing but the record.' }),
  // Hardware Operations (ADR-63) - the MUTATIONS. Reads are in
  // DIAGNOSTIC_ROUTES. Being here keeps hardware administration off the MCP
  // surface: an agent may see what hardware exists and where, and may not
  // create, edit, move or retire any of it.
  createHardwareCatalogItem: pr({ method: 'POST', path: '/platform/hardware/catalog', request: CreateHardwareCatalogItemBody, response: HardwareCatalogItem, summary: 'Add a catalog item (a TYPE of hardware): SKU, name, category, serialized or quantity-tracked, optional pricing and warranty.' }),
  updateHardwareCatalogItem: pr({ method: 'PATCH', path: '/platform/hardware/catalog/:id', request: UpdateHardwareCatalogItemBody, response: HardwareCatalogItem, summary: 'Edit a catalog item. Tracking type and SKU are fixed once created; everything else, including active, is editable.' }),
  createHardwareAsset: pr({ method: 'POST', path: '/platform/hardware/assets', request: CreateHardwareAssetBody, response: HardwareAsset, summary: 'Register a serialized asset against a SERIALIZED catalog item. The HN-AST tag is minted by the database. Ownership and custody are set here and are independent of each other.' }),
  updateHardwareAsset: pr({ method: 'PATCH', path: '/platform/hardware/assets/:id', request: UpdateHardwareAssetBody, response: HardwareAsset, summary: 'Edit an asset: serials, condition, ownership, purchase and warranty, notes. Status is changed through its own route; custody through move. Ownership changes leave an OWNER_CHANGED event.' }),
  setHardwareAssetStatus: pr({ method: 'POST', path: '/platform/hardware/assets/:id/status', request: SetHardwareAssetStatusBody, response: HardwareAsset, summary: 'Move an asset through its status lifecycle. Refused (409) when the lifecycle does not allow the step (a SOLD or DISPOSED asset is final; a RETIRED one cannot be put back into service directly). Assignment-driven statuses are not settable here.' }),
  moveHardwareAsset: pr({ method: 'POST', path: '/platform/hardware/assets/:id/move', request: MoveHardwareAssetBody, response: HardwareAsset, summary: 'Record a change of custody: the asset is now at another location. Appends to the append-only location history and leaves a LOCATION_CHANGED event. Ownership and assignment do not change.' }),
  createHardwareLocation: pr({ method: 'POST', path: '/platform/hardware/locations', request: CreateHardwareLocationBody, response: HardwareLocation, summary: 'Add a custody location (warehouse, office, tenant facility, auction event, repair center, ...). A tenant or sale reference is context, not a boundary.' }),
  updateHardwareLocation: pr({ method: 'PATCH', path: '/platform/hardware/locations/:id', request: UpdateHardwareLocationBody, response: HardwareLocation, summary: 'Edit a custody location, including deactivating it.' }),
  setHardwareInventory: pr({ method: 'POST', path: '/platform/hardware/inventory', request: SetHardwareInventoryBody, response: HardwareInventoryLevel, summary: 'Set the on-hand, reserved and reorder levels for a QUANTITY catalog item at one location (upsert on SKU + location). Available is derived, never stored.' }),
  // Hardware Operations Cut 2 (ADR-63) - kit MUTATIONS. Reads in DIAGNOSTIC_ROUTES.
  createKitTemplate: pr({ method: 'POST', path: '/platform/hardware/kit-templates', request: CreateKitTemplateBody, response: KitTemplate, summary: 'Define a kit template version (a BOM): code, name, version, optional case and components. Making it the current version supersedes the prior current version of the same code.' }),
  updateKitTemplate: pr({ method: 'PATCH', path: '/platform/hardware/kit-templates/:id', request: UpdateKitTemplateBody, response: KitTemplate, summary: 'Edit a template version\'s metadata (name, active, current-version flag, case, docs). Code and version are identity and never change; the BOM is set through its own route.' }),
  setKitTemplateComponents: pr({ method: 'PUT', path: '/platform/hardware/kit-templates/:id/components', request: SetKitTemplateComponentsBody, response: KitTemplateDetail, summary: 'Replace a template version\'s BOM. Refused (409) once an instance has been built from this version: the version is frozen, and a change is a new version (design section 38).' }),
  createKitInstance: pr({ method: 'POST', path: '/platform/hardware/kits', request: CreateKitInstanceBody, response: KitInstance, summary: 'Assemble a kit instance from a template version. Mints the per-code HN-CODE id, snapshots the template code and version, and seeds the quantity BOM lines at zero. Ownership and custody are set here and are independent.' }),
  setKitInstanceStatus: pr({ method: 'POST', path: '/platform/hardware/kits/:id/status', request: SetKitStatusBody, response: KitInstance, summary: 'Move a kit through its lifecycle (ASSEMBLING, IN_SERVICE, RETIRED). Refused (409) for a step the lifecycle does not allow; RETIRED is final.' }),
  addKitAsset: pr({ method: 'POST', path: '/platform/hardware/kits/:id/assets', request: AddKitAssetBody, response: KitInstanceDetail, summary: 'Add a serialized asset to a kit (by id or HN-AST tag). Refused (409) if the asset is retired or already in another kit. Sets the asset\'s kit membership and leaves KIT_ADDED on both stories.' }),
  removeKitAsset: pr({ method: 'POST', path: '/platform/hardware/kits/:id/assets/remove', request: RemoveKitAssetBody, response: KitInstanceDetail, summary: 'Remove a serialized asset from a kit. Closes the membership (append-only), clears the asset\'s kit pointer, and leaves KIT_REMOVED on both stories.' }),
  setKitComponent: pr({ method: 'POST', path: '/platform/hardware/kits/:id/components', request: SetKitComponentBody, response: KitInstanceDetail, summary: 'Set the actual count of a quantity component in a kit (a verification). Stamps last verified. Readiness is recomputed on read, never stored.' }),
  // Hardware Operations Cut 3 (ADR-63) - assignment MUTATIONS. Reads in DIAGNOSTIC_ROUTES.
  assignHardware: pr({ method: 'POST', path: '/platform/hardware/assignments', request: AssignHardwareBody, response: HardwareAssignment, summary: 'Assign an asset or a kit to a tenant or an internal department: the relationship (rental, loaner, lease, ...), an expected return, billing metadata, and a deployment location. Refused (409) if the target already has an active assignment or is retired. Sets the item status; ownership is untouched.' }),
  returnHardware: pr({ method: 'POST', path: '/platform/hardware/assignments/:id/return', request: ReturnHardwareBody, response: HardwareAssignment, summary: 'Close an active assignment: record the return, its condition, and optionally move custody back. The item returns to stock; the assignment row is closed, never deleted (append-only history).' }),
  // Hardware Operations Cut 4 (ADR-63 Phase 2) - maintenance MUTATIONS.
  openMaintenance: pr({ method: 'POST', path: '/platform/hardware/maintenance', request: OpenMaintenanceBody, response: MaintenanceEvent, summary: 'Open a maintenance or repair event on an asset or a kit (a repair, a failure, a battery swap, a cleaning). A kit with an unresolved event reads NEEDS_MAINTENANCE; the event shows on the item detail. Status and assignment are left as they are.' }),
  updateMaintenance: pr({ method: 'PATCH', path: '/platform/hardware/maintenance/:id', request: UpdateMaintenanceBody, response: MaintenanceEvent, summary: 'Edit a maintenance event and move it through its lifecycle (OPEN, IN_PROGRESS, COMPLETED, CANCELLED). Completing stamps the time and who. Refused (409) for a step the lifecycle does not allow.' }),
  // Hardware Operations Cut 5 (ADR-63 Phase 2) - inspection MUTATIONS.
  createInspectionTemplate: pr({ method: 'POST', path: '/platform/hardware/inspection-templates', request: CreateInspectionTemplateBody, response: InspectionTemplate, summary: 'Create a reusable inspection template (a checklist): a name and its items, each required or optional.' }),
  updateInspectionTemplate: pr({ method: 'PATCH', path: '/platform/hardware/inspection-templates/:id', request: UpdateInspectionTemplateBody, response: InspectionTemplate, summary: 'Edit a template\'s name, description, or active flag. Items are set through their own route.' }),
  setInspectionTemplateItems: pr({ method: 'PUT', path: '/platform/hardware/inspection-templates/:id/items', request: SetInspectionTemplateItemsBody, response: InspectionTemplateDetail, summary: 'Replace a template\'s checklist items. A template is a living checklist; past runs snapshot their items and are unaffected.' }),
  createInspectionRun: pr({ method: 'POST', path: '/platform/hardware/inspection-runs', request: CreateInspectionRunBody, response: InspectionRunDetail, summary: 'Record an inspection of a kit or an asset against a template: the per-item outcomes, from which the result is COMPUTED (a required FAIL fails the run). A kit whose latest run failed reads NEEDS_INSPECTION.' }),
} as const;

export type PlatformRoutes = typeof PLATFORM_ROUTES;
export type PlatformRouteName = keyof PlatformRoutes;

// ---------------------------------------------------------------------------
// Diagnostic surface (ADR-49)
//
// The THIRD table, and the reason it exists is worth stating precisely.
//
// `PLATFORM_ROUTES` above is deliberately underivable into MCP tools, which is
// what makes "an AI agent administers tenants" structurally impossible rather
// than merely forbidden. A diagnostics MCP looks like it contradicts that. It
// does not, because observation and administration go in different tables:
//
//   routes             tenant     DERIVABLE       role-filtered, ADR-29 confirm
//   PLATFORM_ROUTES    operator   NEVER           administration
//   DIAGNOSTIC_ROUTES  operator   DERIVABLE       observation, GET-only
//
// Both properties then hold at once and both are tested: an agent can never
// administer the platform, and an agent can observe all of it.
//
// GET-only is enforced by construction, not by review: `dr()` is the only way
// to build an entry, it fixes `method` to the literal 'GET' and `request` to
// `NoBody`, and neither is a parameter. A mutating diagnostic route is not
// something a reviewer has to catch, because it cannot be written down.
// ---------------------------------------------------------------------------

export interface DiagnosticRouteDef<Res extends z.ZodTypeAny> {
  readonly method: 'GET';
  /** Absolute path, like the platform table. `:name` segments are params. */
  readonly path: string;
  readonly request: typeof NoBody;
  readonly response: Res;
  readonly summary: string;
  /**
   * What this answers and when to reach for it. The MCP tool description is
   * `summary` plus this, because a model choosing between eleven observation
   * tools needs to know which question each one settles - and because an
   * operator reading a tool list deserves the same.
   */
  readonly meaning: string;
}

const dr = <Res extends z.ZodTypeAny>(
  d: Omit<DiagnosticRouteDef<Res>, 'method' | 'request'>,
): DiagnosticRouteDef<Res> => ({ ...d, method: 'GET', request: NoBody });

/**
 * The wire shapes. Every field a handler sends must appear here or the strict
 * field-drop detector fails the build under vitest (engineering rule 1).
 *
 * `Finding`, `ProbeResult`, `InvariantResult` and `RunbookPreview` are imported
 * from `@hipnumber/diagnostics-contract` rather than restated, so the wire and
 * the engine cannot drift into two vocabularies - which is the exact problem
 * ADR-49 exists to fix, and it would be embarrassing to reintroduce it here.
 */
export const DiagnosticInvariantDoc = z.object({
  code: z.string(),
  title: z.string(),
  severity: DiagSeverity,
  /** Runbook ids that address this finding, if any. Empty means no repair is offered. */
  runbooks: z.array(z.string()),
});

/** The counts. `findings` is not repeated here; it is on the report already. */
export const DiagnosticInvariantReport = z.object({
  results: z.array(DiagInvariantResult),
  held: z.number().int(),
  failed: z.number().int(),
  /** The subject did not exist. Neutral. */
  notApplicable: z.number().int(),
  /** We lacked the evidence. This is what stops `health` being `healthy`. */
  notChecked: z.number().int(),
});

export const RingDiagnosticScope = z.object({
  tenantSlug: z.string(),
  tenantId: Uuid,
  saleId: Uuid,
  /** The ENGINE ring id (rings.engine_ring_id), not the rings.id uuid. */
  ringId: z.string(),
});

// `RingDiagnosticPosition` is declared with the ops shapes above (the
// DIAGNOSTIC timeline payload carries it, and it must exist before use).

export const RingDiagnosis = z.object({
  scope: RingDiagnosticScope,
  generatedAt: z.number(),
  health: DiagHealth,
  saleTitle: z.string(),
  saleStatus: z.string(),
  position: RingDiagnosticPosition,
  findings: z.array(DiagFinding),
  summary: DiagFindingSummary,
  invariants: DiagnosticInvariantReport,
  /** What could NOT be judged, named. Never empty when health is `unknown`. */
  blindSpots: z.array(z.string()),
});
export type RingDiagnosis = z.infer<typeof RingDiagnosis>;

/** One line per ring, for the platform roll-up. */
export const RingDiagnosisLine = z.object({
  scope: RingDiagnosticScope,
  health: DiagHealth,
  saleTitle: z.string(),
  saleStatus: z.string(),
  position: RingDiagnosticPosition,
  summary: DiagFindingSummary,
  /** Errors only, so the overview stays readable at a glance. */
  topFindings: z.array(DiagFinding),
});

export const SaleDiagnosis = z.object({
  tenantSlug: z.string(),
  saleId: Uuid,
  saleTitle: z.string(),
  saleStatus: z.string(),
  generatedAt: z.number(),
  health: DiagHealth,
  rings: z.array(RingDiagnosis),
  summary: DiagFindingSummary,
});

export const SystemOverview = z.object({
  generatedAt: z.number(),
  /** Worst of every probe and every observed ring. `unknown` when anything was unobservable. */
  health: DiagHealth,
  probes: z.array(DiagProbeResult),
  rings: z.array(RingDiagnosisLine),
  summary: DiagFindingSummary,
  tenantsObserved: z.number().int(),
  liveSalesObserved: z.number().int(),
  /** Readings that could not be taken. Empty is meaningful: nothing was blind. */
  blindSpots: z.array(z.string()),
  /**
   * Invariant codes deliberately OUT OF SCOPE at this depth, because they need
   * the canonical event log and the sweep does not read it. Distinct from
   * `blindSpots`: these were not attempted, not attempted-and-failed. Drill into
   * a ring with `ringDiagnosis` to have them judged.
   */
  notJudged: z.array(z.string()),
});

const DIAG = '/platform/diagnostics';


/** Invoicing diagnosis (ADR-51 over ADR-49). One row per tenant, read one
 *  tenant at a time under RLS; never a customer name, amount or secret. */
export const InvoicingTenantDiagnosis = z.object({
  tenantSlug: z.string(),
  connection: z.object({ status: PaymentConnectionStatus, usable: z.boolean(), chargesEnabled: z.boolean(), payoutsEnabled: z.boolean(), lastSyncedAt: z.string().nullable() }).nullable(),
  invoicesByStatus: z.record(z.number().int()),
  /** Invoices in a payable status while the connection is not usable: a customer holding a link that cannot pay. */
  sentWithoutUsableConnection: z.number().int(),
  /** Provider checkouts INITIATED longer ago than the stuck threshold with no outcome recorded. */
  stuckAttempts: z.number().int(),
  /** The newest provider event applied for this tenant, as a reading. */
  lastProviderEvent: DiagProbeResult,
  unresolvedProviderEvents: z.number().int(),
  findings: z.array(DiagFinding),
});
export const InvoicingDiagnosis = z.object({
  observedAt: z.string(),
  providerConfigured: z.boolean(),
  webhooksConfigured: z.boolean(),
  stuckAfterMinutes: z.number().int(),
  tenants: z.array(InvoicingTenantDiagnosis),
  health: DiagHealth,
});

export const DIAGNOSTIC_ROUTES = {
  invariantCatalog: dr({
    path: `${DIAG}/invariants`,
    response: z.array(DiagnosticInvariantDoc),
    summary: 'Every invariant the platform checks, with what it means and which runbook addresses it.',
    meaning: 'Read this first to know what IS and IS NOT checked. An invariant absent from this list is not being watched by anything.',
  }),
  systemOverview: dr({
    path: `${DIAG}/overview`,
    response: SystemOverview,
    summary: 'Platform health: dependency probes, plus one line per ring on a live sale.',
    meaning: 'The "what is wrong with HipNumber right now" answer. `health` is unknown rather than healthy whenever something could not be observed.',
  }),
  saleDiagnosis: dr({
    path: `${DIAG}/tenants/:slug/sales/:saleId`,
    response: SaleDiagnosis,
    summary: 'Full diagnosis of every ring on one sale.',
    meaning: 'The sale-level forensic view: format truth, projector lag, and the settlement invariants for each ring.',
  }),
  ringDiagnosis: dr({
    path: `${DIAG}/tenants/:slug/sales/:saleId/rings/:ringId`,
    response: RingDiagnosis,
    summary: 'Full diagnosis of one ring, including the event-log invariants.',
    meaning: 'Use when a specific ring looks wrong. Reads the canonical log, so it is the expensive one; `blindSpots` names anything it could not judge.',
  }),
  runbookPreview: dr({
    path: `${DIAG}/tenants/:slug/sales/:saleId/rings/:ringId/runbooks/:runbookId/preview`,
    response: RunbookPreviewSchema,
    summary: 'What a runbook WOULD change, established by reading only. Never executes anything.',
    meaning: 'A preview is diagnosis, not repair: it reports willChange, willNotChange, and any precondition that is not met. There is no execute endpoint (ADR-49 Phase 1).',
  }),
  invoicingOverview: dr({
    path: `${DIAG}/invoicing`,
    response: InvoicingDiagnosis,
    summary: 'Invoicing health per tenant: payment connection state, invoice counts by status, invoices sent with no usable connection, stuck payment attempts, webhook freshness.',
    meaning: 'Read-only. "Sent with no usable connection" and "stuck attempts" are the two findings that mean a customer is trying to pay and cannot. Webhook freshness is a reading, not a verdict: absent means no event has ever been recorded, which is normal for a tenant that has not been paid yet.',
  }),
  // Incident Operations (ADR-58) - the READS. An agent may know what is
  // already known and being handled; it may not write any of it (the
  // mutations are in PLATFORM_ROUTES).
  opsSummary: dr({
    path: `${DIAG}/ops/summary`,
    response: OpsSummary,
    summary: 'The operations command center in one read: active incidents by severity and status, changes in flight, open requests, whether the SEV1 change freeze is on, and the banner incident.',
    meaning: 'Read this before proposing any change: `freeze.active` true means no change may enter production except an EMERGENCY linked to the named SEV1. `activeIncidents` is worst first.',
  }),
  liveExposure: dr({
    path: `${DIAG}/ops/exposure`,
    response: OpsExposure,
    summary: 'What is at stake right now: every live sale, whether its bidding is open, approved bidders and lots still to sell, with totals.',
    meaning: 'Read this before proposing a production change. `totals.biddingOpenSales` above zero means a NORMAL or STANDARD change needs a written override to enter IMPLEMENTING. Connected bidders and the current bid are NOT here (they need the ring runtime); `blindSpots` says so.',
  }),
  listIncidents: dr({
    path: `${DIAG}/ops/incidents`,
    response: z.array(OpsIncident),
    summary: 'Incidents (INC), active ones by default, worst first. ?scope=all for history (newest first), ?severity=SEV1 to filter, ?tenant=<slug> ?saleId= ?ringId= to keep only incidents whose recorded impacts name that tenant, auction and ring (all given filters must match), ?limit=N (max 500).',
    meaning: 'Check here before treating a symptom as new: an active incident on the same service or tenant means the problem is already known, and the right move is to add to its record, not open a second one.',
  }),
  getIncident: dr({
    path: `${DIAG}/ops/incidents/:number`,
    response: OpsIncidentDetail,
    summary: 'One incident by number (INC000184) with its impacts, links, full timeline and the transitions its lifecycle currently allows.',
    meaning: 'The timeline is append-only and newest first; `impacts` names the tenants, auctions, rings and services involved; `links` are the changes, requests, GitHub items and deployments tied to it.',
  }),
  listCanaryRuns: dr({
    path: `${DIAG}/ops/canary-runs`,
    response: z.array(OpsCanaryRun),
    summary: 'Synthetic canary runs, newest first. ?tenantId= ?saleId= ?ringId= (the ring\'s own identity, all three together name one ring; a ring id alone is not unique across sales), ?limit=N (max 500).',
    meaning: 'The canary is a scripted bidder and clerk on the synthetic ring: connect, snapshot, open, bid, held bid, accept, sell, advance, reconnect, max bid, media. A failed step names the first thing a real bidder would have hit.',
  }),
  listSupportCommands: dr({
    path: `${DIAG}/ops/support-commands`,
    response: z.array(OpsSupportCommand),
    summary: 'Remote support commands sent to live-ring clients, newest first. ?saleId=, ?ringId= (with saleId: one ring of a multi-ring sale), ?incident=INC000184, ?limit=N (max 500).',
    meaning: 'Every command support sent to a person\'s device, with why, who, how many sockets it reached and how it ended. A FAILED row with "ring unreachable" is a finding about the ring, not the device.',
  }),
  listChangeGates: dr({
    path: `${DIAG}/ops/changes/:number/gates`,
    response: z.array(OpsChangeGate),
    summary: 'ADR-61: every gate evaluation on a change, newest first, with its evidence.',
    meaning: 'A gate that is not here was not evaluated, and an unevaluated gate is not a pass.',
  }),
  listPolicies: dr({
    path: `${DIAG}/governance/policies`,
    response: z.array(GovernancePolicyVersion),
    summary: 'Governance: every policy version, newest first. Read-only here; versions are appended through the governance route.',
    meaning: 'What the control plane is permitted to do, as data. The engine reads this and can never write it.',
  }),
  listPatterns: dr({
    path: `${DIAG}/governance/patterns`,
    response: z.array(GovernancePattern),
    summary: 'Governance: registered standard-change patterns, newest first.',
    meaning: 'A pattern is the right to reuse a proven change without the first instance\'s ceremony; it is registered by a human from a completed, validated change.',
  }),
  governanceAudit: dr({
    path: `${DIAG}/governance/audit`,
    response: GovernanceAuditView,
    summary: 'Governance: the append-only, hash-chained audit of every governance write, verified on read.',
    meaning: 'If the chain is broken, somebody edited or deleted a governance row; that is an incident in itself.',
  }),
  listChanges: dr({
    path: `${DIAG}/ops/changes`,
    response: z.array(OpsChange),
    summary: 'Changes (CHG), open ones by default, newest first. ?scope=all for history, ?saleId= ?ringId= to keep only changes whose impacts name that auction and ring or that are linked to an incident that does, ?limit=N.',
    meaning: 'The first question in any incident is "what changed?". Changes in IMPLEMENTING or VALIDATING, or COMPLETED recently, are the suspects.',
  }),
  getChange: dr({
    path: `${DIAG}/ops/changes/:number`,
    response: OpsChangeDetail,
    summary: 'One change by number (CHG000057) with its affected resources, links, timeline, allowed transitions and its current SEV1 freeze verdict.',
    meaning: '`freeze.blocked` true means this change cannot enter IMPLEMENTING right now and `freeze.reason` says why. The verdict is computed on read, never stored.',
  }),
  listRequests: dr({
    path: `${DIAG}/ops/requests`,
    response: z.array(OpsRequest),
    summary: 'Request items (RITM), open ones by default, newest first. ?scope=all for history, ?limit=N.',
    meaning: 'Operational asks that are not incidents: tenant setup, domains, numbers, access, data work. A request WAITING on someone is the one to chase.',
  }),
  getRequest: dr({
    path: `${DIAG}/ops/requests/:number`,
    response: OpsRequestDetail,
    summary: 'One request by number (RITM000093) with its links, timeline and allowed transitions.',
    meaning: 'The tenant it concerns is on the record when there is one; the requester is free text (who asked), distinct from the operator who logged it.',
  }),
  // Hardware Operations (ADR-63) - the READS. Derivable into MCP tools by
  // construction: an agent may observe the whole hardware estate (what exists,
  // who owns it, where it is, what is low) and may change none of it.
  hardwareDashboard: dr({
    path: `${DIAG}/hardware/dashboard`,
    response: HardwareDashboard,
    summary: 'The hardware operations command center in one read: asset totals by status, catalog counts, location count, low-stock lines and the most recently added assets.',
    meaning: 'The "what does HipNumber own and where is it" answer. `assets.inRepair` and `lowStock` are the two lists that mean something needs doing.',
  }),
  listHardwareCatalog: dr({
    path: `${DIAG}/hardware/catalog`,
    response: z.array(HardwareCatalogRow),
    summary: 'The hardware catalog (definitions), with per-item counts. ?category=CABLE ?tracking=QUANTITY ?active=true ?q=<text> to filter, ?limit=N (max 500).',
    meaning: 'A catalog item is a TYPE of hardware, not a physical thing. `assetCount` is how many serialized assets exist for it; `totalOnHand` and `lowStock` are the quantity view.',
  }),
  getHardwareCatalogItem: dr({
    path: `${DIAG}/hardware/catalog/:id`,
    response: HardwareCatalogItemDetail,
    summary: 'One catalog item with its stock lines across locations.',
    meaning: 'For a quantity item, `inventory` is the per-location on hand / reserved / available with each location reorder threshold and low-stock flag.',
  }),
  listHardwareAssets: dr({
    path: `${DIAG}/hardware/assets`,
    response: z.array(HardwareAsset),
    summary: 'Serialized assets, newest first, catalog and location denormalized. ?status= ?category= ?catalogItemId= ?ownerType= ?tenant=<slug> (owner tenant) ?locationId= ?q=<tag/serial/name> to filter, ?limit=N (max 500).',
    meaning: 'Each asset carries its owner and its current location, which are independent: an asset can be HipNumber-owned and sitting at a tenant facility.',
  }),
  getHardwareAsset: dr({
    path: `${DIAG}/hardware/assets/:id`,
    response: HardwareAssetDetail,
    summary: 'One asset by id or HN-AST tag, with its append-only event stream, its location history and the status transitions its lifecycle currently allows.',
    meaning: 'The history is append-only and newest first. `allowedStatusTransitions` is what the operator status control offers; it excludes assignment-driven statuses.',
  }),
  listHardwareLocations: dr({
    path: `${DIAG}/hardware/locations`,
    response: z.array(HardwareLocationRow),
    summary: 'Custody locations with what is sitting at each (asset count, quantity-inventory line count). ?active=true ?type=REPAIR_CENTER to filter.',
    meaning: 'Where hardware can be. `assetCount` counts serialized assets whose current location is here; `inventoryLines` counts quantity SKUs stocked here.',
  }),
  listHardwareInventory: dr({
    path: `${DIAG}/hardware/inventory`,
    response: z.array(HardwareInventoryLevel),
    summary: 'Quantity-managed stock, one row per SKU and location. ?locationId= ?catalogItemId= ?lowStock=true to filter.',
    meaning: '`available` is on hand minus reserved, derived on read. `lowStock` true means available is at or below this location reorder threshold and the threshold is a real one.',
  }),
  // Hardware Operations Cut 2 (ADR-63) - kit READS.
  listKitTemplates: dr({
    path: `${DIAG}/hardware/kit-templates`,
    response: z.array(KitTemplateRow),
    summary: 'Kit templates (BOMs) with component and instance counts. ?code=RING ?active=true ?current=true to filter.',
    meaning: 'A template is a versioned recipe. `instanceCount` above zero means the version is frozen; `isCurrentVersion` is the one a new kit is built from by default.',
  }),
  getKitTemplate: dr({
    path: `${DIAG}/hardware/kit-templates/:id`,
    response: KitTemplateDetail,
    summary: 'One template version with its full BOM (component lines) and whether it is frozen.',
    meaning: 'Each line is a catalog item with a required quantity and a tracking requirement. `frozen` true means the BOM cannot be edited; make a new version instead.',
  }),
  listKitInstances: dr({
    path: `${DIAG}/hardware/kits`,
    response: z.array(KitInstance),
    summary: 'Kit instances, newest first, with derived readiness. ?status= ?readiness= ?templateCode=RING ?tenant=<slug> (owner) ?q=<code> to filter, ?limit=N (max 500).',
    meaning: 'Readiness is computed per read from membership against the template BOM: READY when every required line is met, INCOMPLETE when one is short, OUT_OF_SERVICE when retired.',
  }),
  getKitInstance: dr({
    path: `${DIAG}/hardware/kits/:id`,
    response: KitInstanceDetail,
    summary: 'One kit by id or HN-CODE with its member assets, its BOM fulfillment, what is missing, the transitions its lifecycle allows, and its event stream.',
    meaning: '`bom` is every template line with its actual count and whether it is met; `missing` is the required lines that are short. Member assets are the serialized things currently installed.',
  }),
  // Hardware Operations Cut 3 (ADR-63) - assignment READS.
  listHardwareAssignments: dr({
    path: `${DIAG}/hardware/assignments`,
    response: z.array(HardwareAssignment),
    summary: 'Assignments, active by default, newest first. ?scope=all for history, ?tenant=<slug>, ?status=ACTIVE|CLOSED, ?type=RENTAL, ?returnRequired=true, ?overdue=true (past expected return, still open), ?limit=N (max 500).',
    meaning: 'An assignment is who has the right to use a thing HipNumber may still own. `overdue` (expected return passed, still ACTIVE and return required) is the list to chase.',
  }),
  getHardwareAssignment: dr({
    path: `${DIAG}/hardware/assignments/:id`,
    response: HardwareAssignment,
    summary: 'One assignment by id, with its target and recipient resolved.',
    meaning: 'The relationship, the billing metadata, and the return dates. Ownership is on the item, not here.',
  }),
  tenantHardware: dr({
    path: `${DIAG}/hardware/tenants/:slug`,
    response: TenantHardware,
    summary: "A tenant's hardware: its active assignments (rented, leased, on loan, deployed) and the items it owns outright.",
    meaning: 'The tenant-facing answer to "what does Barn A have from us, and what is theirs". `returnRequired` assignments are the ones we expect back.',
  }),
  // Hardware Operations Cut 4 (ADR-63 Phase 2) - maintenance READS.
  listMaintenance: dr({
    path: `${DIAG}/hardware/maintenance`,
    response: z.array(MaintenanceEvent),
    summary: 'Maintenance and repair events, open ones by default, newest first. ?scope=all for history, ?status=OPEN, ?type=REPAIR, ?assetId=, ?kitId=, ?limit=N (max 500).',
    meaning: 'What is in the shop. An OPEN or IN_PROGRESS event is unresolved and is what makes an item need attention.',
  }),
  getMaintenance: dr({
    path: `${DIAG}/hardware/maintenance/:id`,
    response: MaintenanceEvent,
    summary: 'One maintenance event by id, with its target resolved.',
    meaning: 'The problem, the diagnosis, the resolution, the cost and the parts.',
  }),
  // Hardware Operations Cut 5 (ADR-63 Phase 2) - inspection READS.
  listInspectionTemplates: dr({
    path: `${DIAG}/hardware/inspection-templates`,
    response: z.array(InspectionTemplateRow),
    summary: 'Inspection templates (checklists) with item and run counts. ?active=true to filter.',
    meaning: 'The reusable checklists a run is built from. `runCount` is how many inspections have used it.',
  }),
  getInspectionTemplate: dr({
    path: `${DIAG}/hardware/inspection-templates/:id`,
    response: InspectionTemplateDetail,
    summary: 'One template with its checklist items in order.',
    meaning: 'Each item is required or optional; a required item that fails a run fails the run.',
  }),
  listInspectionRuns: dr({
    path: `${DIAG}/hardware/inspection-runs`,
    response: z.array(InspectionRun),
    summary: 'Inspection runs, newest first. ?result=FAILED, ?assetId=, ?kitId=, ?templateId=, ?limit=N (max 500).',
    meaning: 'The record of what was inspected and how it came out. A kit\'s latest FAILED run is what makes it read NEEDS_INSPECTION.',
  }),
  getInspectionRun: dr({
    path: `${DIAG}/hardware/inspection-runs/:id`,
    response: InspectionRunDetail,
    summary: 'One run by id with every item outcome, as recorded at the time.',
    meaning: 'The snapshot: the template name and item labels are frozen on the run, so it still reads after the template changes.',
  }),
} as const;

export type DiagnosticRoutes = typeof DIAGNOSTIC_ROUTES;
export type DiagnosticRouteName = keyof DiagnosticRoutes;

/**
 * Both operator tables, for the one typed client that reaches them. Merging
 * them for TRANSPORT changes nothing about derivation: the control MCP builds
 * its tools from `DIAGNOSTIC_ROUTES` alone, and a test asserts that no
 * `PLATFORM_ROUTES` path is reachable as a tool.
 */
export const OPERATOR_ROUTES = { ...PLATFORM_ROUTES, ...DIAGNOSTIC_ROUTES } as const;
export type OperatorRoutes = typeof OPERATOR_ROUTES;
export type OperatorRouteName = keyof OperatorRoutes;

// ---------------------------------------------------------------------------
// The FOURTH route table: the tenant public storefront (ADR-54).
//
// Anonymous, internet-facing, and tenant-scoped by HOST rather than by a
// path slug: every route takes the request hostname as its first segment and
// the server resolves it through tenant_domains. Nothing here is derived
// into an MCP tool (a model has no business browsing storefronts through the
// control plane), and nothing here can mutate: `sr()` fixes `method` to
// 'GET' and `request` to `NoBody`, the same construction as the diagnostic
// table. Every response is an allowlisted projection - the shapes below ARE
// the list of what the public may see, and the storefront route tests in
// control-api assert the private fields are absent.
// ---------------------------------------------------------------------------

export const StorefrontPhase = z.enum(['UPCOMING', 'LIVE', 'COMPLETED']);
export type StorefrontPhase = z.infer<typeof StorefrontPhase>;

/** What the card's primary button DOES. The site maps kind -> href; the API
 *  decides which kind is honest for the sale's authoritative state. */
export const StorefrontCtaKind = z.enum(['VIEW_AUCTION', 'BROWSE_CATALOG', 'REGISTER_TO_BID', 'JOIN_LIVE', 'VIEW_RESULTS']);
export type StorefrontCtaKind = z.infer<typeof StorefrontCtaKind>;

export const StorefrontImage = z.object({ url: z.string(), width: z.number().int(), height: z.number().int() });

export const StorefrontTenant = z.object({
  slug: z.string(),
  name: z.string(),
  /** The host the request arrived on (normalized) and the one the page must
   *  advertise as canonical. Equal on the canonical host; on an alias
   *  (barn-a.hipnum.com, barn-a.localhost) canonicalHost still names the
   *  real one so <link rel=canonical> and og:url are right everywhere. */
  host: z.string(),
  canonicalHost: z.string(),
  isCanonicalHost: z.boolean(),
  /** CSS token overrides (web-sites theme.ts allowlists the keys and values). */
  theme: z.record(z.unknown()),
  /** Public company contact, from tenants.settings.organization. Null when
   *  the office never filled it in - the page renders nothing, not a blank. */
  contact: z.object({
    phone: z.string().nullable(),
    email: z.string().nullable(),
    website: z.string().nullable(),
    address: z.string().nullable(),
    timezone: z.string().nullable(),
  }).nullable(),
  /** The consignor web form (ADR-50) when online consignment is on. */
  consignUrl: z.string().nullable(),
  /** An external bidder app's sign-in entry, when one is configured
   *  (BIDDER_APP_URL). Null = none. */
  registerUrl: z.string().nullable(),
  /** The storefront's own email-code sign-in and registration flow is
   *  available on this host (the control-api has a bidder sign-in seam).
   *  False = the pages explain how to register through the office instead;
   *  never a dead button. */
  signInAvailable: z.boolean(),
  /** Company-default branding when it is enabled (ADR-55): the header logo
   *  and accent on every page. Null = the standard layout of the shell. */
  branding: Branding.nullable(),
  /** The brand shell this HOST wears (ADR-69 Phase 3): tenant_domains.product
   *  through the products registry. The layout, footer and mark follow it; a
   *  sale's lot term follows the sale's own product, never the host. Defaults
   *  to hipnumber so an answer from an older API still renders. */
  shell: ShellKey.default('hipnumber'),
});
export type StorefrontTenant = z.infer<typeof StorefrontTenant>;

export const StorefrontAuction = z.object({
  slug: z.string(),
  title: z.string(),
  phase: StorefrontPhase,
  /** The authoritative sale status the phase was derived from. */
  saleStatus: SaleStatus,
  auctionMode: AuctionMode,
  /** Short, honest status line: "Live now", "Catalog open", "Completed", ... */
  badge: z.string(),
  startsAt: z.string().nullable(),
  timezone: z.string().nullable(),
  location: z.string().nullable(),
  descriptionMd: z.string().nullable(),
  /** Public, non-draft, selling lots (notices excluded). */
  lotCount: z.number().int(),
  /** First catalog photo, card size, when any lot has one. */
  cover: StorefrontImage.nullable(),
  /** The office accepts registrations for this sale (published or live). */
  registrationOpen: z.boolean(),
  /** The tenant's web consignment form is on AND this sale is still taking
   *  entries (published, not yet live). */
  consignmentsOpen: z.boolean(),
  /** Where a bidder joins the live ring: the ring's own page, only while the
   *  sale is live AND a ring endpoint is provisioned. Null otherwise, so a
   *  "Join live" button can never point at nothing. */
  liveJoinUrl: z.string().nullable(),
  primaryCta: z.object({ kind: StorefrontCtaKind, label: z.string() }),
  /** The branding this sale's page renders with (sale override, else the
   *  company default), only when enabled. Null = standard layout. */
  branding: Branding.nullable(),
  /** The product the sale runs under (ADR-69): the pages read the lot term
   *  ("Hip 12" / "Lot 12") and the lot route prefix from it. Defaults to
   *  HIPNUMBER so an answer from an older API still renders. */
  product: z.string().default('HIPNUMBER'),
});
export type StorefrontAuction = z.infer<typeof StorefrontAuction>;

export const StorefrontCompletedPage = z.object({
  items: z.array(StorefrontAuction),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type StorefrontCompletedPage = z.infer<typeof StorefrontCompletedPage>;

export const PublicStorefront = z.object({
  tenant: StorefrontTenant,
  /** Live first, else the nearest upcoming; null when nothing is active. */
  featured: StorefrontAuction.nullable(),
  /** Every active auction, featured included, in display order. */
  active: z.array(StorefrontAuction),
  /** The first page of the archive, newest first. */
  completed: StorefrontCompletedPage,
  generatedAt: z.string(),
});
export type PublicStorefront = z.infer<typeof PublicStorefront>;

/** ADR-68: what a bidder needs to register for a sale, resolved (the sale's
 *  override, else the company default). Short facts for the "at a glance"
 *  strip; the register modal spells them out as sentences. */
export const StorefrontBidderRequirements = z.object({
  cardOnFile: z.boolean(),
  deposit: z.boolean(),
  /** The deposit amount when the office set one; null = "the office will tell you". */
  depositCents: z.number().int().nullable(),
  bankLetter: z.boolean(),
});
export type StorefrontBidderRequirements = z.infer<typeof StorefrontBidderRequirements>;

export const PublicStorefrontSale = z.object({
  tenant: StorefrontTenant,
  auction: StorefrontAuction,
  sale: PublicSale,
  lots: z.array(PublicLot),
  /** ADR-68: the current terms of sale (highest version), public on the sale
   *  page. Null when the office has published none. The version a bidder
   *  accepts is still recorded at registration (migration 0046). */
  terms: z.object({ version: z.number().int(), bodyMd: z.string(), publishedAt: z.string().nullable() }).nullable().default(null),
  /** ADR-68: the buyer premium per channel in basis points, resolved (sale
   *  override, else company default). A channel the office never set is
   *  null and the page shows nothing for it; 0 is a real "none". Null when
   *  neither channel is set. */
  buyerPremium: z.object({ liveBps: z.number().nullable(), onlineBps: z.number().nullable() }).nullable().default(null),
  bidderRequirements: StorefrontBidderRequirements.default({ cardOnFile: false, deposit: false, depositCents: null, bankLetter: false }),
});
export type PublicStorefrontSale = z.infer<typeof PublicStorefrontSale>;

export interface StorefrontRouteDef<Res extends z.ZodTypeAny> {
  readonly method: 'GET';
  /** Absolute path. The first segment after /storefront is the request host. */
  readonly path: string;
  readonly request: typeof NoBody;
  readonly response: Res;
  readonly access: 'public';
  readonly summary: string;
}
const sr = <Res extends z.ZodTypeAny>(d: Omit<StorefrontRouteDef<Res>, 'method' | 'request' | 'access'>): StorefrontRouteDef<Res> =>
  ({ ...d, method: 'GET', request: NoBody, access: 'public' });

export const STOREFRONT_ROUTES = {
  storefront: sr({
    path: '/storefront/:host',
    response: PublicStorefront,
    summary: 'The tenant storefront for a public hostname: branding, the featured auction, active/upcoming auctions and the first page of the completed archive.',
  }),
  storefrontCompleted: sr({
    path: '/storefront/:host/completed',
    response: StorefrontCompletedPage,
    summary: 'One page of the completed-auction archive for a public hostname (?page=N, newest first).',
  }),
  storefrontSale: sr({
    path: '/storefront/:host/sales/:saleSlug',
    response: PublicStorefrontSale,
    summary: 'One public auction on a public hostname: the storefront card, the public sale, and its public lots with media.',
  }),
} as const;

export type StorefrontRoutes = typeof STOREFRONT_ROUTES;
export type StorefrontRouteName = keyof StorefrontRoutes;
